import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, CreditCard, Lock, Download, List, ShoppingCart, TrendingUp, Plus, Eye, Edit,
  CheckCircle, XCircle, Check, Trash2, Upload, Image as ImageIcon, FileText, X,
  Briefcase, ArrowRightCircle, ChevronDown, ChevronRight, FolderPlus, RotateCcw, MoreHorizontal,
  Receipt, Camera, Paperclip
} from 'lucide-react';
import type { Project, Item, User, TeamName, ItemStatus, FundEntry, PurchaserFund, ProjectStatus, ItemApproval, FundDestinationType } from '../types/core';
import type { Attachment } from '../types/attachments';
import { AttachmentManager } from '../types/attachments';
import { FileUploadComponent } from './FileUploadComponent';
import { FileStorageService } from '../services/fileStorageService';
import { ItemsService } from '../services/itemsService';
import { AuditService } from '../services/AuditService';
import type { AuditEntry } from '../utils';
import AttachmentPreviewPanel from './AttachmentPreviewPanel';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { createAuditEntry, isOverdue, isDeadlineApproaching, removeUndefinedValues, generateItemId, itemsSync } from '../utils';
import SummaryTab from './SummaryTab';
import ExportProgressModal from './ExportProgressModal';
import { getFirestore, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebaseConfig'; // Import db instance
import packageJson from '../../package.json';

export interface ProjectViewProps {
  project: Project;
  projects: Project[];
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  setCurrentProject: React.Dispatch<React.SetStateAction<Project | null>>;
  targetTabForProjectView: 'list' | 'purchase' | 'summary' | null;
  setTargetTabForProjectView: React.Dispatch<React.SetStateAction<'list' | 'purchase' | 'summary' | null>>;
}

interface BalanceClearModalStateType {
  clearingUserId: string;
  clearAmount: string;
  clearDescription: string;
  clearAttachments: Attachment[];
}

interface ItemDetailModalProps {
  item: Item;
  project: Project;
  onClose: () => void;
  usersToUse: User[];
  t: (key: string, options?: any) => string;
  onEdit?: () => void;
  onDelete?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  canEdit?: boolean;
  canApprove?: boolean;
  canDelete?: boolean;
  isProjectClosed?: boolean;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({
  item, project, onClose, usersToUse, t,
  onEdit, onDelete, onApprove, onReject,
  canEdit = false, canApprove = false, canDelete = false, isProjectClosed = false
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState<{ url: string; name: string } | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [imageLoadError, setImageLoadError] = useState<Set<number>>(new Set());

  const images = useMemo(() =>
    (item.attachments || []).filter(att => AttachmentManager.canPreviewImage(att)),
    [item.attachments]
  );
  const hasImages = images.length > 0;

  // Preload adjacent images for smoother navigation
  useEffect(() => {
    if (!hasImages) return;

    const preloadImage = (index: number) => {
      if (index < 0 || index >= images.length) return;
      const img = new Image();
      const imageUrl = images[index].url || images[index].data || '';
      if (imageUrl) {
        img.src = imageUrl;
        img.onload = () => {
          setLoadedImages(prev => new Set(prev).add(index));
        };
        img.onerror = () => {
          setImageLoadError(prev => new Set(prev).add(index));
        };
      }
    };

    // Preload current, next and previous images
    preloadImage(currentImageIndex);
    preloadImage(currentImageIndex + 1);
    preloadImage(currentImageIndex - 1);
    // Also preload first and last for wrap-around navigation
    if (currentImageIndex === 0) preloadImage(images.length - 1);
    if (currentImageIndex === images.length - 1) preloadImage(0);
  }, [currentImageIndex, images, hasImages]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenImage) {
          setFullscreenImage(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, fullscreenImage]);

  // W3.CSS style slide functions
  const plusDivs = (n: number) => {
    let newIndex = currentImageIndex + n;
    if (newIndex >= images.length) newIndex = 0;
    if (newIndex < 0) newIndex = images.length - 1;
    setCurrentImageIndex(newIndex);
  };

  const currentDiv = (n: number) => {
    setCurrentImageIndex(n);
  };

  const isCurrentImageLoaded = loadedImages.has(currentImageIndex);
  const hasCurrentImageError = imageLoadError.has(currentImageIndex);

  const handleDownloadImage = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const purchaserUser = item.purchaser ? usersToUse.find(u => u.id === item.purchaser) : null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="item-detail-modal-content flex flex-col relative" onClick={(e) => e.stopPropagation()}>

        {/* Status Badge - Top Left */}
        <div className="modal-badge-container">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full shadow-lg ${item.status === 'approved' ? 'bg-green-100 text-green-700' :
            item.status === 'rejected' ? 'bg-red-100 text-red-700' :
              item.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
            }`}>
            {t(`item.status.${item.status}`, { defaultValue: item.status })}
          </span>
          {item.isEmergency && !item.isGroup && (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700 shadow-lg">
              {t('item.emergency')}
            </span>
          )}
        </div>

        {/* Close Button - Top Right */}
        <button
          onClick={onClose}
          className="modal-close-button bg-white bg-opacity-95 hover:bg-opacity-100 text-gray-800 hover:text-gray-900 transition-all duration-200 shadow-xl border border-gray-200"
        >
          <X size={16} />
        </button>

        {/* Simple Slideshow - W3.CSS Style - Optimized for performance */}
        {hasImages ? (
          <div className="modal-hero-image relative">

            {/* Loading skeleton */}
            {!isCurrentImageLoaded && !hasCurrentImageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                <div className="text-center">
                  <div className="animate-spin rounded-full w-10 h-10 border-3 border-blue-500 border-t-transparent mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('image.loading', { defaultValue: 'Loading image...' })}</p>
                </div>
              </div>
            )}

            {/* Error state */}
            {hasCurrentImageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
                <div className="text-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('image.load.error', { defaultValue: 'Failed to load image' })}</p>
                </div>
              </div>
            )}

            {/* Only render current image - not all hidden ones */}
            {(() => {
              const currentImage = images[currentImageIndex];
              const imageUrl = (currentImage?.url || currentImage?.data) || '';
              return currentImage ? (
                <img
                  key={currentImageIndex}
                  className={`mySlides ${isCurrentImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  src={imageUrl}
                  alt={currentImage.name}
                  loading="eager"
                  decoding="async"
                  onLoad={() => setLoadedImages(prev => new Set(prev).add(currentImageIndex))}
                  onError={() => setImageLoadError(prev => new Set(prev).add(currentImageIndex))}
                  onClick={() => setFullscreenImage({ url: imageUrl, name: currentImage.name })}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease-in-out'
                  }}
                  title={t('image.click.to.view.fullscreen', { defaultValue: 'Click to view fullscreen' })}
                />
              ) : null;
            })()}

            {/* Navigation Arrows - Fixed positioning */}
            {images.length > 1 && (
              <>
                {/* Left arrow */}
                <button
                  onClick={() => plusDivs(-1)}
                  className="w3-left"
                >
                  &#10094;
                </button>

                {/* Right arrow */}
                <button
                  onClick={() => plusDivs(1)}
                  className="w3-right"
                >
                  &#10095;
                </button>
              </>
            )}

            {/* Bottom overlay with dots - W3.CSS style */}
            {images.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <div className="inline-flex space-x-1 bg-black bg-opacity-30 px-3 py-2 rounded-full">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => currentDiv(index)}
                      className={`w3-badge ${index === currentImageIndex ? 'w3-white' : ''}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Title overlay */}
            <div className="modal-hero-title-overlay absolute top-16 left-4 right-4 text-white">
              <h2 className="text-lg font-bold drop-shadow-lg">
                {item.isGroup ? (item.groupName || item.name) : item.name}
              </h2>
              <p className="text-sm opacity-90 drop-shadow">
                {images[currentImageIndex].name}
              </p>
            </div>
          </div>
        ) : (
          <div className="modal-no-image">
            <div className="text-center text-white">
              <div className="w-16 h-16 mx-auto mb-3 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FileText size={28} className="opacity-80" />
              </div>
              <h2 className="text-xl font-bold mb-1">
                {item.isGroup ? (item.groupName || item.name) : item.name}
              </h2>
              <p className="text-sm opacity-75">{t('attachment.no.images', { defaultValue: 'No Images' })}</p>
            </div>
          </div>
        )}

        <div className="modal-content-section flex-1 overflow-y-auto">
          <div className="p-4 pb-20">

            {/* Description */}
            {item.details && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('item.details', { defaultValue: 'Details' })}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-slate-700 p-3 rounded-md leading-relaxed">
                  {item.details}
                </p>
              </div>
            )}

            {/* 2-Column Info Grid */}
            <div className="modal-info-grid">

              {/* Left Column */}
              <div className="space-y-3">
                {!item.isGroup ? (
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('item.quantity', { defaultValue: 'Quantity' })}</span>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.quantity} {item.unit}</p>
                  </div>
                ) : (
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('group.items', { defaultValue: 'Items' })}</span>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {item.childItemIds?.length || 0} {t('general.items', { defaultValue: 'items' })}
                    </p>
                  </div>
                )}

                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('item.price.estimate', { defaultValue: 'Estimated Price' })}</span>
                  <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">฿{((item.estimatePrice || 0) * item.quantity).toLocaleString()}</p>
                </div>

                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('general.created', { defaultValue: 'Created' })}</span>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : t('general.not.available', { defaultValue: 'N/A' })}
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('item.category', { defaultValue: 'Category' })}</span>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {item.category || t('general.not.set')}
                  </p>
                </div>

                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{!item.isGroup ? t('item.purchaser', { defaultValue: 'Purchaser' }) : t('item.price.actual', { defaultValue: 'Actual Price' })}</span>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {!item.isGroup ?
                      (purchaserUser ? `${purchaserUser.fullName} (${purchaserUser.team})` : t('general.not.assigned')) :
                      (item.totalPrice && item.totalPrice > 0 ? `฿${item.totalPrice.toLocaleString()}` : t('general.not.set'))
                    }
                  </p>
                </div>

                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{item.approvedAt ? t('general.approved', { defaultValue: 'Approved' }) : t('general.status', { defaultValue: 'Status' })}</span>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {item.approvedAt ? new Date(item.approvedAt).toLocaleDateString() : t(`item.status.${item.status}`, { defaultValue: item.status })}
                  </p>
                </div>
              </div>
            </div>

            {/* Link - Clickable */}
            {item.link && (
              <div className="mt-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('item.link', { defaultValue: 'Link' })}</span>
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline truncate mt-1"
                  title={item.link}
                >
                  {item.link}
                </a>
              </div>
            )}

            {/* Rejection Reason - Compact */}
            {item.rejectionReason && item.status === 'rejected' && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-md p-2 border border-red-200 dark:border-red-800 mt-3">
                <p className="text-xs text-red-700 dark:text-red-300">
                  <span className="font-semibold">{t('item.rejection.reason')}: </span>
                  {item.rejectionReason}
                </p>
              </div>
            )}

          </div>
        </div>

        {/* Action Buttons - Fixed at Bottom */}
        {!isProjectClosed && (canEdit || canApprove || canDelete) && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-50 dark:bg-slate-700 border-t border-gray-200 dark:border-gray-600 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            <div className="modal-action-buttons">
              {/* Delete button - Left side, red background */}
              {canDelete && (
                <button
                  onClick={() => { onDelete?.(); onClose(); }}
                  className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  style={{ backgroundColor: '#ef4444', color: '#ffffff' }}
                >
                  <Trash2 size={16} />
                  {t('action.delete', { defaultValue: 'Delete' })}
                </button>
              )}

              {/* Spacer to push buttons to right */}
              <div className="flex-1"></div>

              {/* Approve/Reject buttons for pending items */}
              {canApprove && item.status === 'pending' && !item.isGroup && (
                <>
                  <button
                    onClick={() => { onReject?.(); onClose(); }}
                    className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                    style={{ backgroundColor: '#f97316', color: '#ffffff' }}
                  >
                    <XCircle size={16} />
                    {t('action.reject', { defaultValue: 'Reject' })}
                  </button>
                  <button
                    onClick={() => { onApprove?.(); onClose(); }}
                    className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                    style={{ backgroundColor: '#22c55e', color: '#ffffff' }}
                  >
                    <CheckCircle size={16} />
                    {t('action.approve', { defaultValue: 'Approve' })}
                  </button>
                </>
              )}

              {/* Edit button - Blue/Primary - Rightmost */}
              {canEdit && (
                <button
                  onClick={() => { onEdit?.(); onClose(); }}
                  className="px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  style={{ backgroundColor: '#3b82f6', color: '#ffffff' }}
                >
                  <Edit size={16} />
                  {t('action.edit', { defaultValue: 'Edit' })}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Image Viewer */}
      {fullscreenImage && createPortal(
        <div
          className="fullimage-modal-overlay"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="fullimage-modal-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={fullscreenImage.url}
              alt={fullscreenImage.name}
              className="fullimage-modal-image"
            />
            <div className="fullimage-modal-buttons">
              <button
                onClick={() => handleDownloadImage(fullscreenImage.url, fullscreenImage.name)}
                className="fullimage-modal-button"
                title={t('action.download', { defaultValue: 'Download' })}
              >
                <Download size={20} />
              </button>
              <button
                onClick={() => setFullscreenImage(null)}
                className="fullimage-modal-button"
                title={t('action.close', { defaultValue: 'Close' })}
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
};

const ProjectView: React.FC<ProjectViewProps> = ({
  project,
  projects,
  setProjects,
  setCurrentProject,
  targetTabForProjectView,
  setTargetTabForProjectView
}) => {
  // Restore the last-used tab for this specific project from localStorage on mount.
  // Falls back to 'list' if nothing stored or value is invalid.
  const [activeTab, setActiveTab] = useState<'list' | 'purchase' | 'summary'>(() => {
    try {
      const stored = localStorage.getItem(`projectView.activeTab.${project.id}`);
      if (stored === 'list' || stored === 'purchase' || stored === 'summary') {
        return stored;
      }
    } catch {
      // localStorage may be unavailable (private mode etc.) — silently fall back
    }
    return 'list';
  });

  // Persist activeTab whenever it changes so refresh / reopen restores the same tab
  useEffect(() => {
    try {
      localStorage.setItem(`projectView.activeTab.${project.id}`, activeTab);
    } catch {
      // ignore storage errors
    }
  }, [activeTab, project.id]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [triggerInitialAttachmentPreview, setTriggerInitialAttachmentPreview] = useState<Attachment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPurchaser, setFilterPurchaser] = useState('');
  const [showFundManagement, setShowFundManagement] = useState(false);
  const [itemAccessedFrom, setItemAccessedFrom] = useState<'list' | 'purchase'>('list');
  const [selectedItemForAttachmentView, setSelectedItemForAttachmentView] = useState<Item | null>(null);
  const [showBalanceClearModal, setShowBalanceClearModal] = useState(false);
  const [balanceClearModalState, setBalanceClearModalState] = useState<BalanceClearModalStateType>({ clearingUserId: '', clearAmount: '', clearDescription: '', clearAttachments: [] });
  const balanceClearFileInputRef = useRef<HTMLInputElement>(null);

  const [editingInlineItemId, setEditingInlineItemId] = useState<number | null>(null);
  const [editingInlineField, setEditingInlineField] = useState<'category' | 'purchaser' | null>(null);
  // Debug comment to shift lines
  const [itemToMoveToGroup, setItemToMoveToGroup] = useState<Item | null>(null);
  const [showMoveToGroupModal, setShowMoveToGroupModal] = useState(false);
  const [selectedTargetGroupId, setSelectedTargetGroupId] = useState<string>('');
  const [expandedGroupIds, setExpandedGroupIds] = useState<number[]>([]);
  const [preselectedParentIdForNewItem, setPreselectedParentIdForNewItem] = useState<number | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: string, left: string }>({ top: '50px', left: '50px' });
  const [showTeamManagement, setShowTeamManagement] = useState(false);

  // Item Detail Modal state
  const [selectedItemForDetailView, setSelectedItemForDetailView] = useState<Item | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [itemToReject, setItemToReject] = useState<Item | null>(null);

  // Audit Logs State

  // Subscribe to Audit Trail

  const { currentUser, getSupervisorsByTeams, getUsersByTeam, users: allUsers } = useAuth();
  const { addNotification } = useNotifications();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Fallback: If allUsers is empty, load users directly with real-time listener
  const [localUsers, setLocalUsers] = useState<User[]>([]);
  useEffect(() => {
    if (allUsers.length > 0) {
      setLocalUsers([]);
      return;
    }

    const setupLocalUsersListener = async () => {
      try {
        const { collection, onSnapshot } = await import('firebase/firestore');
        const { db } = await import('../firebaseConfig');
        const usersCollection = collection(db, 'users');

        const unsubscribe = onSnapshot(usersCollection, (snapshot) => {
          const users: User[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
          setLocalUsers(users);
        }, () => {
          setLocalUsers([]);
        });

        return unsubscribe;
      } catch (error) {
        return null;
      }
    };

    let unsubscribePromise = setupLocalUsersListener();

    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
  }, [allUsers.length]);

  // Close dropdown when clicking outside and handle scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId !== null) {
        const target = event.target as Element;
        if (!target.closest('.dropdown-menu') && !target.closest('.table-col-menu button')) {
          setOpenDropdownId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openDropdownId !== null) {
        // Update dropdown position on scroll
        const button = document.querySelector(`[data-item-id="${openDropdownId}"] .table-col-menu button`);
        if (button) {
          const rect = button.getBoundingClientRect();
          const menuHeight = 200;
          const menuWidth = 192;

          let top = rect.bottom + 4;
          let left = rect.left;

          if (top + menuHeight > window.innerHeight) {
            top = rect.top - menuHeight - 4;
            if (top < 4) top = 4;
          }

          if (left + menuWidth > window.innerWidth) {
            left = window.innerWidth - menuWidth - 4;
            if (left < 4) left = 4;
          }

          setDropdownPosition({ top: `${top}px`, left: `${left}px` });
        }
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture phase
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [openDropdownId]);

  // Compute supervisors from the available user list in this component to avoid empty lists
  // when the global cache (allUsers) is not populated for non-admins.
  const projectSupervisors = (allUsers.length > 0 ? allUsers : localUsers)
    .filter(u => u.role === 'supervisor' && project.teamsInvolved.includes(u.team as TeamName));
  const numRequiredApprovals = projectSupervisors.length;
  const isListMode = activeTab === 'list';

  // True if this item has been cloned into a purchase item (i.e. it is
  // "superseded" and must be hidden from purchase-side views and totals).
  // String() comparison because legacy data stores some ids as strings.
  const itemHasPurchaseClone = (itemId: number | string, itemsList: Item[]): boolean =>
    itemsList.some(i => i.isPurchaseItem === true && String(i.originalItemId) === String(itemId));

  // Guards against double-processing when Approve/Reject is clicked twice
  // before the realtime listener refreshes local state.
  const statusUpdatesInFlight = useRef<Set<number>>(new Set());

  // Return total spent calculation matching bottom bar
  const calculateSpentAmountForUser = (userId: string, itemsList: Item[]): number => {
    // Check if an item is superseded (legacy approved item that has been converted to purchase item)
    const isSuperseded = (item: Item) => {
      return item.status === 'approved' && !item.isPurchaseItem && itemsList.some(i => i.isPurchaseItem === true && String(i.originalItemId) === String(item.id));
    };

    const calcActualPriceForItem = (item: Item): number => {
      if (item.isGroup) {
        const itemIdStr = String(item.id);
        const children = itemsList.filter(child => String(child.parentId) === itemIdStr);

        // Check if groupTotalPrice is set
        if (item.groupTotalPrice && item.groupTotalPrice > 0) {
          const qualifyingChildren = children.filter(child =>
            child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child))
          );
          if (qualifyingChildren.length > 0) {
            const price = item.groupTotalPrice + (item.shippingCost || 0);
            return price;
          }
          return 0;
        }

        // No groupTotalPrice - sum children's prices
        const childrenTotal = children.reduce((sum, child) => {
          const isValid = child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child));
          if (isValid) {
            const itemPrice = ((child.price || 0) * child.quantity) + (child.shippingCost || 0);
            const result = sum + itemPrice;
            if (itemPrice > 0) {
              // breakdownLog removed
            }
            return result;
          }
          return sum;
        }, 0);

        if (childrenTotal > 0) {
          const totalWithShipping = childrenTotal + (item.shippingCost || 0);
          return totalWithShipping;
        }
        return 0;
      }

      // Non-group item
      if (item.status === 'approved' || item.status === 'bought' || item.isUnsupervisedPurchase === true) {
        // Double check superseded logic just in case
        if (item.status === 'approved' && isSuperseded(item)) {
          return 0;
        }
        const itemTotal = ((item.price || 0) * item.quantity) + (item.shippingCost || 0);
        return itemTotal;
      }
      return 0;
    };

    // Get visible items for this user (top-level items they own or groups with their children)
    let totalSpent = 0;

    itemsList.forEach(item => {
      if (item.parentId) return; // Skip children

      if (item.isGroup) {
        // Include group if it has any approved/bought child belonging to this user
        // Matches visibleItems filter: exclude superseded children
        const hasRelevantChild = itemsList.some(child =>
          String(child.parentId) === String(item.id) &&
          child.purchaser === userId &&
          (child.status === 'bought' || (child.status === 'approved' && !isSuperseded(child)))
        );
        if (hasRelevantChild) {
          totalSpent += calcActualPriceForItem(item);
        }
      } else {
        // Include individual item if owned by user and approved/bought/unsupervised
        // Exclude superseded items
        if (item.purchaser === userId) {
          const isValidStatus = item.status === 'bought' || item.isUnsupervisedPurchase === true || (item.status === 'approved' && !isSuperseded(item));
          if (isValidStatus) {
            totalSpent += calcActualPriceForItem(item);
          }
        }
      }
    });

    return totalSpent;
  };

  // Calculate spent amount for a specific user, matching bottom bar (totalActualPrice) logic
  // Wrapper for compatibility if needed, but we're updating call sites

  // Check if project can be closed: all TOP-LEVEL bought items (not children) have proof_of_purchase & product_picture, and balance is zero
  // Check if project can be closed: all TOP-LEVEL bought items (not children) have proof_of_purchase & product_picture, and balance is zero
  const projectClosureValidation = useMemo(() => {
    const errors: string[] = [];

    // Get only TOP-LEVEL bought items (no parentId) - checking parent status only, not children
    const topLevelBoughtItems = project.items.filter(item =>
      item.status === 'bought' && !item.parentId
    );

    // Check if all top-level bought items have proof_of_purchase and product_picture attachments
    // Check if all top-level bought items have proof_of_purchase and product_picture attachments
    topLevelBoughtItems.forEach(item => {
      const checkAttachment = (i: Item, type: string) => (i.attachments || []).some(att => att.attachmentType === type);

      let hasProofOfPurchase = checkAttachment(item, 'proof_of_purchase');
      let hasProductPicture = checkAttachment(item, 'product_picture');

      if (item.isGroup) {
        // If group is missing attachment, check children
        const children = project.items.filter(child => child.parentId === item.id && (child.status === 'approved' || child.status === 'bought'));

        if (children.length > 0) {
          if (!hasProofOfPurchase) {
            hasProofOfPurchase = children.every(child => checkAttachment(child, 'proof_of_purchase'));
          }
          if (!hasProductPicture) {
            hasProductPicture = children.every(child => checkAttachment(child, 'product_picture'));
          }
        }
      }

      if (!hasProofOfPurchase || !hasProductPicture) {
        errors.push(t('project.close.error.missingAttachments', { itemName: item.name, defaultValue: `Item "${item.name}" is missing proof of purchase or product picture.` }));
      }
    });

    // Check if balance is balanced (project fund is 0 AND all user available amounts are 0)
    // Relaxed check: Allow small floating point differences (< 0.01)
    const projectFundIsZero = Math.abs(project.initialFund) < 0.01;
    if (!projectFundIsZero) {
      errors.push(t('project.close.error.projectFundNotZero', { amount: project.initialFund.toLocaleString(), defaultValue: `Project main fund has remaining balance: ฿${project.initialFund.toLocaleString()}` }));
    }

    // Dynamically calculate each user's actual balance (matching SummaryTab logic)
    // instead of relying on potentially stale pf.availableAmount
    project.purchaserFunds.forEach(pf => {
      const totalAllocated = (pf.allocatedAmount || 0) + (pf.additionalFunds || 0);
      const totalSpent = calculateSpentAmountForUser(pf.purchaserId, project.items);
      const actualBalance = totalAllocated - totalSpent;
      if (Math.abs(actualBalance) > 0.01) {
        const user = allUsers.find(u => u.id === pf.purchaserId);
        const userName = user?.fullName || pf.purchaserId;
        errors.push(t('project.close.error.userHasBalance', { userName, amount: actualBalance.toLocaleString(), defaultValue: `User ${userName} has remaining balance: ฿${actualBalance.toLocaleString()}` }));
      }
    });

    return {
      canClose: errors.length === 0,
      errors
    };
  }, [project.items, project.initialFund, project.purchaserFunds, t, allUsers]);

  const canApprove = (currentUser?.role === 'supervisor' && project.teamsInvolved.includes(currentUser.team)) || currentUser?.role === 'admin';
  const canEdit = currentUser?.role === 'admin' || (currentUser?.team && project.teamsInvolved.includes(currentUser.team));
  const canEditPurchaser = !!currentUser;
  const isAdminOrSupervisor = currentUser?.role === 'admin' || currentUser?.role === 'supervisor';
  const isProjectClosed = project.status === 'closed';
  // Get purchasers only from teams involved in this project
  const projectTeams = project.teamsInvolved;

  // Use allUsers if available, otherwise use localUsers
  const usersToUse = allUsers.length > 0 ? allUsers : localUsers;
  const availablePurchasers = usersToUse.filter(user =>
    user.team && projectTeams.includes(user.team as TeamName)
  );

  useEffect(() => {
    if (targetTabForProjectView && targetTabForProjectView !== activeTab) {
      setActiveTab(targetTabForProjectView);
      setTargetTabForProjectView(null);
    }
  }, [targetTabForProjectView, activeTab, setTargetTabForProjectView]);

  const closeProjectHandler = async () => {
    if (!projectClosureValidation.canClose) {
      alert(t('project.close.blocked.message', { reasons: projectClosureValidation.errors.join('\n'), defaultValue: `Cannot close project:\n${projectClosureValidation.errors.join('\n')}` }));
      return;
    }

    if (!currentUser) return;

    if (window.confirm(`${t('project.close.confirm')}\n\n"${project.name}"\n\n${t('project.close.warning')}`)) {
      const closedAtIso = new Date().toISOString();
      const baseAuditTrail = Array.isArray(project.auditTrail) ? project.auditTrail : [];
      const safeUserName = currentUser.fullName || (currentUser as any).username || currentUser.email || 'Unknown';
      const newAuditTrail = [...baseAuditTrail, createAuditEntry(currentUser.id, safeUserName, 'PROJECT_CLOSED', `Project "${project.name}" closed`)];
      const cleanedAuditTrail = newAuditTrail.map((entry) => removeUndefinedValues(entry));
      try {
        // Delete non-proof attachments from storage (keep only proof_of_purchase and product_picture)
        const { ref, deleteObject, getStorage } = await import('firebase/storage');
        const storage = getStorage();

        // Collect all non-proof attachments to delete
        const attachmentsToDelete: Attachment[] = [];
        const updatedItems = project.items.map(item => {
          if (item.attachments && item.attachments.length > 0) {
            // Separate proof attachments from general attachments
            const proofAttachments = item.attachments.filter(
              att => att.attachmentType === 'proof_of_purchase' || att.attachmentType === 'product_picture'
            );
            const nonProofAttachments = item.attachments.filter(
              att => att.attachmentType !== 'proof_of_purchase' && att.attachmentType !== 'product_picture'
            );

            // Add non-proof attachments to deletion list
            attachmentsToDelete.push(...nonProofAttachments);

            // Return item with only proof attachments
            return { ...item, attachments: proofAttachments };
          }
          return item;
        });

        // Delete non-proof attachments from Firebase Storage
        for (const att of attachmentsToDelete) {
          if (att.storagePath) {
            try {
              const storageRef = ref(storage, att.storagePath);
              await deleteObject(storageRef);
            } catch (deleteErr) {
              // Continue with other deletions even if one fails
            }
          }
        }

        const projectRef = doc(db, 'projects', project.id.toString());
        await updateDoc(projectRef, {
          status: 'closed',
          closedAt: closedAtIso,
          closedBy: currentUser.id,
          auditTrail: cleanedAuditTrail,
          items: [], // Items are in subcollection - keep empty
        });

        // Batch update to remove deleted attachments from items in subcollection
        const batch = writeBatch(db);
        let batchCount = 0;
        const MAX_BATCH_SIZE = 500;

        for (const item of updatedItems) {
          const originalItem = project.items.find(i => i.id === item.id);
          if (originalItem && originalItem.attachments?.length !== item.attachments?.length) {
            const itemRef = doc(db, 'projects', project.id.toString(), 'items', item.id.toString());
            batch.update(itemRef, { attachments: item.attachments });
            batchCount++;

            if (batchCount >= MAX_BATCH_SIZE) {
              await batch.commit();
              batchCount = 0;
              // Re-initialize batch would be needed if loop continued, but here we just commit and finish for simplicity or create new batch
              // For robust large project handling, multiple batches logic is ideal, but assuming <500 modified items for now.
              // To be safe, let's just commit what we have if we hit limit, though unlikely.
            }
          }
        }

        if (batchCount > 0) {
          await batch.commit();
        }
        const updatedProjectData = {
          ...project,
          status: 'closed' as ProjectStatus,
          closedAt: closedAtIso,
          closedBy: currentUser.id,
          auditTrail: cleanedAuditTrail,
          items: updatedItems,
        };
        // Local state will be updated automatically by the realtime listener
        alert(`Project closed successfully. ${attachmentsToDelete.length} non-proof attachment(s) deleted.`);
      } catch (error) {
        alert('Failed to close project on server. Please try again.');
      }
    }
  };

  // Helper function to handle attachment deletion with proper error handling
  const handleAttachmentDelete = async (
    att: Attachment,
    attachments: Attachment[],
    setAttachments: (attachments: Attachment[]) => void,
    t: (key: string, options?: any) => string
  ) => {
    if (!window.confirm(t('file.remove.confirm', { fileName: att.name, defaultValue: `Are you sure you want to remove ${att.name}?` }))) {
      return;
    }

    const originalAttachments = [...attachments];
    const remainingAttachments = attachments.filter(a => a.id !== att.id);

    // Optimistically update UI
    setAttachments(remainingAttachments);

    // Try to delete from storage if it's not a legacy attachment
    if (att.storagePath && !(att as any).isLegacy) {
      try {
        const deleteResult = await FileStorageService.deleteFile(att.storagePath);

        if (deleteResult.success) {
        } else if (deleteResult.fileNotFound) {
          // File not found in storage - this is okay, attachment is orphaned
          alert(t('file.remove.orphaned.success', {
            fileName: att.name,
            defaultValue: `${att.name} was removed from the list. The file was not found in storage (possibly already deleted).`
          }));
        } else {
          // Other storage error - revert UI changes
          setAttachments(originalAttachments);
          alert(t('file.remove.error', {
            defaultValue: "Failed to delete file from storage.",
            error: deleteResult.error
          }));
        }
      } catch (error) {
        // Unexpected error - revert UI changes
        setAttachments(originalAttachments);
        alert(t('file.remove.error', {
          defaultValue: "Failed to delete file from storage.",
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
      }
    }
    // If it's a legacy attachment or has no storagePath, deletion is already complete with UI update
  };

  interface AddItemModalProps {
    initialPreviewTrigger: Attachment | null;
    onPreviewConsumed: () => void;
    defaultParentId?: number | null;
  }
  const AddItemModal: React.FC<AddItemModalProps> = ({ initialPreviewTrigger, onPreviewConsumed, defaultParentId }) => {
    const initialFormData = {
      name: '', details: '', quantity: '1',
      unit: project.mode === 'กำลังจัดซื้อ' ? 'ชิ้น' : 'รายการ',
      estimatePrice: '', price: '', purchaser: '',
      purchaseDate: '', deliveryDate: '', deadline: '',
      category: project.categories.includes('อุปกรณ์') ? 'อุปกรณ์' : (project.categories[0] || 'อื่นๆ'),
      isEmergency: false,
      isGroup: defaultParentId ? false : false,
      groupName: '',
      groupTotalPrice: '', // Combined price for all items in the group (purchase mode)
      shippingCost: '',
      parentId: defaultParentId || undefined,
      selectedGroupId: defaultParentId ? defaultParentId.toString() : '',
      link: '',
      isTotalPrice: false, // When true, entered price is total (will be divided by quantity)
    };
    const [formData, setFormDataInternal] = useState(initialFormData);
    const [attachments, setAttachmentsInternal] = useState<Attachment[]>([]);
    const [isCurrentlyUploading, setIsCurrentlyUploading] = useState(false);
    const [isPreviewingAttachment, setIsPreviewingAttachment] = useState(false);
    const [attachmentToPreview, setAttachmentToPreview] = useState<Attachment | null>(null);

    // Ref to track if user has made any modifications - MUST be before wrappers
    const hasUserModifiedRef = useRef(false);

    // STABLE editingItem - capture the item on first render and don't let it change
    // This prevents the form from resetting when external updates change the project.items
    const stableEditingItemRef = useRef<typeof editingItem>(editingItem);
    // Only update the stable ref if we're switching to a different item (or no item)
    if (!hasUserModifiedRef.current) {
      const currentId = editingItem?.id;
      const stableId = stableEditingItemRef.current?.id;
      if (currentId !== stableId) {
        stableEditingItemRef.current = editingItem;
      }
    }
    // Use the stable version everywhere in the modal
    const stableEditingItem = stableEditingItemRef.current;

    // Wrapper for setFormData that marks form as modified by user
    const setFormData: typeof setFormDataInternal = (update) => {
      hasUserModifiedRef.current = true; // Mark that user has made changes
      setFormDataInternal(update);
    };

    // Wrapper for setAttachments that marks form as modified by user  
    const setAttachments: typeof setAttachmentsInternal = (update) => {
      hasUserModifiedRef.current = true;
      setAttachmentsInternal(update);
    };
    const isEffectivelyListModeForForm = activeTab === 'list';
    const isActuallyViewOnly = stableEditingItem && isEffectivelyListModeForForm && (stableEditingItem.status === 'approved' || stableEditingItem.status === 'converted') && currentUser?.role !== 'admin' && currentUser?.role !== 'supervisor';

    // Check if this item's parent group has groupTotalPrice set
    const parentGroupWithTotalPrice = (() => {
      const parentId = stableEditingItem?.parentId || formData.parentId || formData.selectedGroupId;
      if (!parentId) return null;
      const parentGroup = project.items.find(item => item.id === parseInt(parentId.toString()) && item.isGroup);
      if (parentGroup && parentGroup.groupTotalPrice && parentGroup.groupTotalPrice > 0) {
        return parentGroup;
      }
      return null;
    })();
    const shouldHidePriceFields = !isEffectivelyListModeForForm && parentGroupWithTotalPrice !== null;

    // Ref to track if the modal has been initialized - prevents form reset on realtime updates
    const isInitializedRef = useRef(false);
    const lastEditingItemIdRef = useRef<number | null>(null);
    // Note: hasUserModifiedRef is declared earlier, near setFormData wrapper

    useEffect(() => {
      // NEVER re-initialize if user has made any modifications
      if (hasUserModifiedRef.current) {
        return;
      }

      // Only initialize form data once per modal open or when switching to a different item
      const currentEditingItemId = stableEditingItem?.id || null;
      const shouldInitialize = !isInitializedRef.current || lastEditingItemIdRef.current !== currentEditingItemId;

      if (!shouldInitialize) {
        // Skip re-initialization to preserve user's typed data during realtime updates
        return;
      }

      if (stableEditingItem) {

        setFormData({
          name: stableEditingItem.name || '',
          details: stableEditingItem.details || '',
          quantity: stableEditingItem.quantity?.toString() || '1',
          unit: stableEditingItem.unit || (project.mode === 'กำลังจัดซื้อ' ? 'ชิ้น' : 'รายการ'),
          estimatePrice: stableEditingItem.estimatePrice?.toString() || '',
          price: stableEditingItem.price?.toString() || '',
          purchaser: stableEditingItem.purchaser || '',
          purchaseDate: stableEditingItem.purchaseDate || '',
          deliveryDate: stableEditingItem.deliveryDate || '',
          deadline: stableEditingItem.deadline || '',
          category: stableEditingItem.category || (project.categories.includes('อุปกรณ์') ? 'อุปกรณ์' : (project.categories[0] || 'อื่นๆ')),
          isEmergency: stableEditingItem.isEmergency || false,
          shippingCost: stableEditingItem.shippingCost?.toString() || '0',
          isGroup: stableEditingItem.isGroup || false,
          groupName: stableEditingItem.groupName || '',
          groupTotalPrice: stableEditingItem.groupTotalPrice?.toString() || '',
          parentId: stableEditingItem.parentId || undefined,
          selectedGroupId: stableEditingItem.parentId?.toString() || '',
          link: stableEditingItem.link || '',
          isTotalPrice: false, // Always start with false for isTotalPrice
        });

        setAttachments(stableEditingItem.attachments || []);

        if (initialPreviewTrigger && initialPreviewTrigger.id) {
          const itemAttachmentIds = (stableEditingItem.attachments || []).map(a => a.id);
          if (itemAttachmentIds.includes(initialPreviewTrigger.id)) {
            setAttachmentToPreview(initialPreviewTrigger);
            setIsPreviewingAttachment(true);
          }
          if (onPreviewConsumed) onPreviewConsumed();
        }
        else {
          setIsPreviewingAttachment(false);
          setAttachmentToPreview(null);
        }
      } else {
        const baseNewFormData = { ...initialFormData };
        if (defaultParentId) {
          baseNewFormData.parentId = defaultParentId;
          baseNewFormData.selectedGroupId = defaultParentId.toString();
          baseNewFormData.isGroup = false;
        }
        setFormData(baseNewFormData);
        setAttachments([]);
        setIsPreviewingAttachment(false);
        setAttachmentToPreview(null);
      }

      // Mark as initialized and track the current item ID
      isInitializedRef.current = true;
      lastEditingItemIdRef.current = currentEditingItemId;
    }, [stableEditingItem, initialPreviewTrigger, project.mode, project.categories, currentUser?.id, onPreviewConsumed, defaultParentId]);

    // Reset initialization when modal closes
    useEffect(() => {
      return () => {
        isInitializedRef.current = false;
        lastEditingItemIdRef.current = null;
        hasUserModifiedRef.current = false; // Reset modification tracking
      };
    }, []);

    // Pause realtime updates while modal is open, trigger sync on close
    useEffect(() => {
      itemsSync.modalOpenCount++;
      return () => {
        // Clamped: a stuck counter would silently freeze all realtime updates until reload
        itemsSync.modalOpenCount = Math.max(0, itemsSync.modalOpenCount - 1);
        if (itemsSync.modalOpenCount === 0 && itemsSync.pendingUpdate) {
          itemsSync.pendingUpdate = false;
          itemsSync.onResume?.();
        }
      };
    }, []);

    // Prevent wheel scrolling on number inputs
    useEffect(() => {
      const handleWheel = (e: WheelEvent) => {
        const target = e.target as HTMLInputElement;
        if (target.type === 'number' && target === document.activeElement) {
          e.preventDefault();
        }
      };

      // Add event listener to the document
      document.addEventListener('wheel', handleWheel, { passive: false });

      // Cleanup
      return () => {
        document.removeEventListener('wheel', handleWheel);
      };
    }, []);

    const handleAttemptCloseModal = () => {
      if (isCurrentlyUploading) {
        if (window.confirm(t('file.upload.cancel.confirm'))) {
          setShowAddItem(false); setEditingItem(null); setPreselectedParentIdForNewItem(null); if (onPreviewConsumed) onPreviewConsumed(); setIsPreviewingAttachment(false); setAttachmentToPreview(null);
        }
      } else {
        setShowAddItem(false); setEditingItem(null); setPreselectedParentIdForNewItem(null); if (onPreviewConsumed) onPreviewConsumed(); setIsPreviewingAttachment(false); setAttachmentToPreview(null);
      }
    };
    const handleSave = async () => {
      if (isCurrentlyUploading && !window.confirm(t('file.upload.save.confirm'))) return;
      if (!formData.name.trim() || isActuallyViewOnly || isProjectClosed) return;
      if (formData.isGroup && !formData.groupName.trim()) {
        alert(t('error.groupNameRequired', { defaultValue: 'Group name is required for an item group.' }));
        return;
      }
      const intendedTabAfterSave = itemAccessedFrom; setTargetTabForProjectView(intendedTabAfterSave);
      const isNewUnsupervisedItem = !editingItem && (activeTab === 'purchase' && !isListMode);
      try {

        const processedAttachments: Attachment[] = [];
        for (const attachment of attachments) {
          if (attachment.data && !attachment.url && (attachment as any).isLegacy) {
            try {
              const migrated = await AttachmentManager.migrateLegacyAttachment(
                attachment,
                project.id,
                editingItem?.id || Date.now(),
                currentUser?.id || 'unknown'
              );
              processedAttachments.push(migrated);
            } catch (error) {
              processedAttachments.push(attachment);
            }
          } else if (attachment.url && attachment.storagePath) {
            processedAttachments.push(attachment);
          } else if (attachment.file && !attachment.url) {
            processedAttachments.push(attachment);
          } else if (attachment.url && !attachment.storagePath && !(attachment as any).isLegacy) {
            processedAttachments.push(attachment);
          } else {
          }
        }

        const newItemId = editingItem?.id || generateItemId();

        const finalParentId = formData.selectedGroupId ? parseInt(formData.selectedGroupId) : (formData.isGroup ? undefined : (defaultParentId || editingItem?.parentId));

        const baseItemData: Omit<Item, 'id'> = { // Ensure all Item fields are covered or explicitly undefined
          name: formData.isGroup ? (formData.groupName.trim() || formData.name.trim()) : formData.name.trim(),
          details: formData.details.trim(),
          quantity: formData.isGroup ? 1 : (parseFloat(formData.quantity) || 1),
          unit: formData.isGroup ? t('general.set', { defaultValue: 'Set' }) : (formData.unit.trim() || (project.mode === 'กำลังจัดซื้อ' ? 'ชิ้น' : 'รายการ')),
          // If isTotalPrice is checked, divide the entered price by quantity to get per-unit price
          price: formData.isGroup ? 0 : (formData.isTotalPrice && (parseFloat(formData.quantity) || 1) > 1
            ? (parseFloat(formData.price) || 0) / (parseFloat(formData.quantity) || 1)
            : (parseFloat(formData.price) || 0)),
          estimatePrice: formData.isGroup ? 0 : (parseFloat(formData.estimatePrice) || 0),
          shippingCost: Math.max(0, parseFloat(formData.shippingCost) || 0),
          purchaser: formData.isGroup ? undefined : (isNewUnsupervisedItem ? currentUser?.id : formData.purchaser),
          purchaseDate: formData.isGroup ? undefined : formData.purchaseDate,
          deliveryDate: formData.isGroup ? undefined : formData.deliveryDate,
          deadline: formData.isGroup ? undefined : formData.deadline,
          category: formData.isGroup ? t('category.group', { defaultValue: 'Group' }) : (formData.category || project.categories[0] || 'อื่นๆ'),
          isEmergency: formData.isGroup ? false : formData.isEmergency,
          status: (() => {
            const rawPrice = formData.price?.toString() || '';
            const cleanPrice = parseFloat(rawPrice.replace(/[^0-9.-]/g, '')) || 0;
            const isPurchaseContext = activeTab === 'purchase' || itemAccessedFrom === 'purchase';
            const isBought = isPurchaseContext && !formData.isGroup && cleanPrice > 0;

            if (isNewUnsupervisedItem) return 'approved';
            if (isBought) return 'bought';
            return editingItem?.status || 'pending';
          })() as ItemStatus,
          approvals: editingItem?.approvals || [],
          attachments: processedAttachments,
          createdBy: editingItem?.createdBy || currentUser?.id || '',
          createdAt: editingItem?.createdAt || new Date().toISOString(),
          isFromListMode: editingItem?.isFromListMode || (activeTab === 'list'),
          isUnsupervisedPurchase: isNewUnsupervisedItem,
          lastEditedAt: new Date().toISOString(),
          lastEditedBy: currentUser?.id || '',
          isGroup: formData.isGroup,
          groupName: formData.isGroup ? (formData.groupName.trim() || formData.name.trim()) : undefined,
          groupTotalPrice: formData.isGroup ? (parseFloat(formData.groupTotalPrice) || 0) : undefined,
          childItemIds: formData.isGroup ? (editingItem?.childItemIds || []) : undefined,
          parentId: finalParentId,
          rejectionReason: editingItem?.rejectionReason,
          link: formData.link?.trim() || undefined,
          isPurchaseItem: editingItem?.isPurchaseItem || isNewUnsupervisedItem,
          originalItemId: editingItem?.originalItemId,
        };

        const newItem: Item = { id: newItemId, ...baseItemData };

        let projectToUpdate = projects.find(p => p.id === project.id);
        if (!projectToUpdate) return;
        let itemsForUpdate = [...projectToUpdate.items];

        // BUSINESS RULE: a group has exactly ONE purchaser. An item saved into
        // a group inherits the group's purchaser; a conflicting choice is
        // rejected so mixed-purchaser groups can never be created here.
        if (finalParentId !== undefined && finalParentId !== null && !newItem.isGroup) {
          const groupMembers = itemsForUpdate.filter(i =>
            (String(i.id) === String(finalParentId) || String(i.parentId) === String(finalParentId)) && i.id !== newItem.id);
          const establishedPurchaser = groupMembers.find(m => m.purchaser)?.purchaser;
          if (establishedPurchaser) {
            if (newItem.purchaser && newItem.purchaser !== establishedPurchaser) {
              const groupPurchaserName = usersToUse.find(u => u.id === establishedPurchaser)?.fullName || 'another user';
              alert(t('error.groupSinglePurchaser', {
                name: groupPurchaserName,
                defaultValue: `This group is assigned to "${groupPurchaserName}". A group can only have one purchaser - please keep the same purchaser, or reassign the whole group from the item list first.`
              }));
              return;
            }
            if (!newItem.purchaser) {
              newItem.purchaser = establishedPurchaser;
            }
          }
        }

        // Handle conversion of existing item to group - keep original item as child
        let convertedChildItem: Item | null = null;
        if (editingItem && !editingItem.isGroup && formData.isGroup) {
          // Converting an existing non-group item to a group
          // Create a child item with the original item's data
          const childItemId = generateItemId();
          convertedChildItem = {
            id: childItemId,
            name: editingItem.name,
            details: editingItem.details || '',
            quantity: editingItem.quantity,
            unit: editingItem.unit,
            price: editingItem.price || 0,
            estimatePrice: editingItem.estimatePrice || 0,
            shippingCost: 0, // Shipping cost stays on the group
            purchaser: editingItem.purchaser,
            purchaseDate: editingItem.purchaseDate,
            deliveryDate: editingItem.deliveryDate,
            deadline: editingItem.deadline,
            category: editingItem.category,
            isEmergency: editingItem.isEmergency,
            status: editingItem.status,
            approvals: editingItem.approvals || [],
            attachments: editingItem.attachments || [], // Move attachments to child
            createdBy: editingItem.createdBy || currentUser?.id || '',
            createdAt: editingItem.createdAt || new Date().toISOString(),
            isFromListMode: editingItem.isFromListMode,
            isUnsupervisedPurchase: editingItem.isUnsupervisedPurchase,
            lastEditedAt: new Date().toISOString(),
            lastEditedBy: currentUser?.id || '',
            isGroup: false,
            parentId: newItemId, // Set parent to the new group
            rejectionReason: editingItem.rejectionReason,
            isPurchaseItem: editingItem.isPurchaseItem,
            originalItemId: editingItem.originalItemId,
          };

          // Update the group item to reference the child and clear attachments (they're on child now)
          newItem.childItemIds = [childItemId];
          newItem.attachments = []; // Clear attachments from group, they're on the child
          newItem.shippingCost = editingItem.shippingCost || 0; // Keep shipping on group
        }

        if (newItem.parentId && (!editingItem || editingItem.parentId !== newItem.parentId)) {
          // If new item or item's parent changed
          // Remove from old parent if applicable
          if (editingItem && editingItem.parentId) {
            const oldParentIdx = itemsForUpdate.findIndex(i => i.id === editingItem.parentId && i.isGroup);
            if (oldParentIdx !== -1) {
              const oldParent = { ...itemsForUpdate[oldParentIdx] };
              oldParent.childItemIds = (oldParent.childItemIds || []).filter(id => id !== newItem.id);
              itemsForUpdate[oldParentIdx] = oldParent;
            }
          }
          // Add to new parent
          const parentGroupIndex = itemsForUpdate.findIndex(i => i.id === newItem.parentId && i.isGroup);
          if (parentGroupIndex !== -1) {
            const parentGroup = { ...itemsForUpdate[parentGroupIndex] };
            parentGroup.childItemIds = [...(parentGroup.childItemIds || []), newItem.id].filter((id, idx, self) => self.indexOf(id) === idx); // ensure unique
            itemsForUpdate[parentGroupIndex] = parentGroup;
          }
        }

        let finalPurchaserFunds = projectToUpdate.purchaserFunds.map(pf => ({ ...pf, fundEntries: [...pf.fundEntries] }));

        let updatedItemsList = editingItem
          ? itemsForUpdate.map((i: Item) => i.id === editingItem.id ? newItem : i)
          : [...itemsForUpdate, newItem];

        // Add the converted child item if we're converting to a group
        if (convertedChildItem) {
          updatedItemsList = [...updatedItemsList, convertedChildItem];
        }

        // --- Start of revised spentAmount logic V6 (Using consistent calculation with SummaryTab) ---
        // Collect all affected purchaser IDs (including children's purchasers for groups)
        const affectedPurchaserIds = new Set<string>();
        if (editingItem?.purchaser) {
          affectedPurchaserIds.add(editingItem.purchaser);
        }
        if (newItem.purchaser) {
          affectedPurchaserIds.add(newItem.purchaser);
        }
        // Also include purchasers of children if this is a group or affects a group
        if (newItem.isGroup || newItem.parentId) {
          updatedItemsList.forEach(item => {
            if (item.purchaser) {
              // Include purchasers of items in the same group
              if (newItem.isGroup && item.parentId === newItem.id) {
                affectedPurchaserIds.add(item.purchaser);
              }
              // Include purchasers of siblings if this item is in a group
              if (newItem.parentId && item.parentId === newItem.parentId) {
                affectedPurchaserIds.add(item.purchaser);
              }
            }
          });
        }

        affectedPurchaserIds.forEach(purchaserId => {
          if (!purchaserId) return;

          let fundIndex = finalPurchaserFunds.findIndex(f => f.purchaserId === purchaserId);
          if (fundIndex === -1 && purchaserId) {
            finalPurchaserFunds.push({
              purchaserId: purchaserId, allocatedAmount: 0, additionalFunds: 0,
              spentAmount: 0, availableAmount: 0, fundEntries: [],
            });
            fundIndex = finalPurchaserFunds.length - 1;
          }

          if (fundIndex !== -1) {
            // Use the new consistent calculation that matches SummaryTab
            const newTotalSpentForThisPurchaser = calculateSpentAmountForUser(purchaserId, updatedItemsList);

            finalPurchaserFunds[fundIndex].spentAmount = newTotalSpentForThisPurchaser;
            finalPurchaserFunds[fundIndex].availableAmount =
              (finalPurchaserFunds[fundIndex].allocatedAmount || 0) +
              (finalPurchaserFunds[fundIndex].additionalFunds || 0) -
              newTotalSpentForThisPurchaser;
          }
        });
        // --- End of revised spentAmount logic V6 ---
        const auditAction = convertedChildItem ? 'ITEM_CONVERTED_TO_GROUP' : (editingItem ? 'ITEM_UPDATED' : 'ITEM_CREATED');
        const auditMessage = convertedChildItem
          ? `Item "${editingItem?.name}" converted to group "${newItem.groupName || newItem.name}" with original item as child`
          : `Item "${newItem.name}" ${editingItem ? 'updated' : 'created'}`;
        const auditEntry = createAuditEntry(currentUser?.id || '', currentUser?.fullName || '', auditAction, auditMessage, newItem.id, editingItem, newItem);

        const finalUpdatedProjectData: Project = {
          ...projectToUpdate,
          items: updatedItemsList,
          purchaserFunds: finalPurchaserFunds,
          auditTrail: [...projectToUpdate.auditTrail, auditEntry]
        };

        // Local state will be updated automatically by the realtime listener

        // Close modal immediately to prevent re-render flash during Firestore update
        handleAttemptCloseModal();
        if (intendedTabAfterSave === 'purchase') setActiveTab('purchase'); else setActiveTab('list');
        setItemAccessedFrom('list');
        setPreselectedParentIdForNewItem(null);

        // Immediately save to Firestore to prevent data loss on refresh
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { db } = await import('../firebaseConfig');
          const { removeUndefinedValues } = await import('../utils');

          // Save item to subcollection using ItemsService
          await ItemsService.saveItem(project.id.toString(), newItem);

          // If editing an item that's part of a group, also save the child items
          if (convertedChildItem) {
            await ItemsService.saveItem(project.id.toString(), convertedChildItem);
          }

          // Save project document WITHOUT items array (items are now in subcollection)
          const projectDocRef = doc(db, 'projects', project.id.toString());
          const projectData: any = {
            name: finalUpdatedProjectData.name,
            description: finalUpdatedProjectData.description,
            initialFund: finalUpdatedProjectData.initialFund,
            totalReceived: finalUpdatedProjectData.totalReceived,
            items: [], // Keep empty - items are now in subcollection
            fundEntries: finalUpdatedProjectData.fundEntries,
            purchaserFunds: finalUpdatedProjectData.purchaserFunds,
            initialFundAttachments: finalUpdatedProjectData.initialFundAttachments || [],
            mode: finalUpdatedProjectData.mode,
            status: finalUpdatedProjectData.status,
            teamsInvolved: finalUpdatedProjectData.teamsInvolved,
            createdBy: finalUpdatedProjectData.createdBy,
            createdAt: finalUpdatedProjectData.createdAt,
            auditTrail: finalUpdatedProjectData.auditTrail,
            categories: finalUpdatedProjectData.categories,
            lastUpdated: new Date().toISOString(),
            lastUpdatedBy: currentUser?.id,
            deletionApprovals: finalUpdatedProjectData.deletionApprovals || [],
          };

          if (finalUpdatedProjectData.initialFundDate) projectData.initialFundDate = finalUpdatedProjectData.initialFundDate;
          if (finalUpdatedProjectData.deadline) projectData.deadline = finalUpdatedProjectData.deadline;
          if (finalUpdatedProjectData.closedAt) projectData.closedAt = finalUpdatedProjectData.closedAt;
          if (finalUpdatedProjectData.closedBy) projectData.closedBy = finalUpdatedProjectData.closedBy;
          if (finalUpdatedProjectData.deletionInitiatedBy) projectData.deletionInitiatedBy = finalUpdatedProjectData.deletionInitiatedBy;

          const cleanedData = removeUndefinedValues(projectData);
          await setDoc(projectDocRef, cleanedData);
        } catch (firestoreError) {
          // Still continue with the UI update even if Firestore save fails
        }

        if (newItem.purchaser && newItem.purchaser !== editingItem?.purchaser && !newItem.isGroup) {
          addNotification({ userId: newItem.purchaser, type: 'assignment', title: t('notification.new.assignment'), message: `Item "${newItem.name}" has been assigned to you in project "${project.name}"`, projectId: project.id, itemId: newItem.id, projectName: project.name, itemName: newItem.name });
        }
      } catch (error) { alert('Failed to save item. Please try again.'); }
    };
    const viewAttachmentInModal = (attachment: Attachment) => { setAttachmentToPreview(attachment); setIsPreviewingAttachment(true); };
    const closeAttachmentPreviewInModal = () => { setIsPreviewingAttachment(false); setAttachmentToPreview(null); };

    return (
      <div className="modal-overlay">
        <div className={`add-item-modal-content ${isPreviewingAttachment ? 'is-previewing' : ''}`}>
          <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 truncate pr-2">
              {isPreviewingAttachment && attachmentToPreview ? `${t('action.view')} ${t('item.files')}: ${attachmentToPreview.name}` : isActuallyViewOnly ? `${t('item.view')}: ${formData.name || editingItem?.name || ''}` : editingItem ? `${t('item.edit')}: ${formData.name || editingItem?.name || ''}` : (activeTab === 'list' ? t('item.add') : t('item.add.purchase'))}
            </h2>
            {isPreviewingAttachment ? (
              <button onClick={closeAttachmentPreviewInModal} className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 px-3 py-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title={t('action.back')}>
                <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('action.back')}
              </button>
            ) : (
              <button onClick={handleAttemptCloseModal} className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-400">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="modal-body-columns-container">
            <div className="modal-form-column custom-scrollbar">
              {/* Name and Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label htmlFor="itemNameModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{formData.isGroup ? t('item.groupName') : t('item.name')} *</label><input id="itemNameModalFinal" type="text" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value, groupName: prev.isGroup ? e.target.value : prev.groupName }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>
                {!formData.isGroup && (<div><label htmlFor="itemCategoryModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.category')}</label><select id="itemCategoryModalFinal" value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600">{project.categories.map(cat => { const catKey = { 'อุปกรณ์สำนักงาน': 'category.office.supplies', 'อุปกรณ์': 'category.equipment', 'วัสดุ': 'category.materials', 'บริการ': 'category.services', 'เดินทาง': 'category.travel', 'การตลาด': 'category.marketing', 'การบำรุงรักษา': 'category.maintenance', 'อื่นๆ': 'category.other' }[cat]; return (<option key={cat} value={cat}>{catKey ? t(catKey, { defaultValue: cat }) : cat}</option>); })}</select></div>)}
              </div>
              {/* Details */}
              <div><label htmlFor="itemDetailsModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.details')}</label><textarea id="itemDetailsModalFinal" value={formData.details} onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))} rows={3} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>

              {/* Link field */}
              <div><label htmlFor="itemLinkModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.link', { defaultValue: 'Link' })}</label><input id="itemLinkModalFinal" type="text" value={formData.link} onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))} placeholder="https://..." disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>

              {/* Quantity, Unit, Deadline - Hidden for group headers */}
              {!formData.isGroup && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label htmlFor="itemQuantityModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.quantity')} *</label><input id="itemQuantityModalFinal" type="number" value={formData.quantity} onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))} step="0.01" min="0" disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>
                  <div><label htmlFor="itemUnitModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.unit')} *</label><input id="itemUnitModalFinal" type="text" value={formData.unit} onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>
                  <div><label htmlFor="itemDeadlineModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.deadline')}</label><input id="itemDeadlineModalFinal" type="date" value={formData.deadline} onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>
                </div>
              )}

              {/* Shipping Cost - Hidden for child items when parent has groupTotalPrice */}
              {!shouldHidePriceFields && (
                <div><label htmlFor="itemShippingCostModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.shippingCost')}</label><input id="itemShippingCostModalFinal" type="number" value={formData.shippingCost} onChange={(e) => setFormData(prev => ({ ...prev, shippingCost: e.target.value }))} step="0.01" min="0" placeholder="0.00" disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>
              )}
              {shouldHidePriceFields && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-700">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {t('item.priceIncludedInGroup', { defaultValue: 'Price included in group total' })}: ฿{parentGroupWithTotalPrice?.groupTotalPrice?.toLocaleString()}
                  </p>
                </div>
              )}

              {/* Price fields - Hidden for group headers and when parent has groupTotalPrice */}
              {!formData.isGroup && !shouldHidePriceFields && (
                isEffectivelyListModeForForm ? (<div><label htmlFor="itemEstimatePriceModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.price.estimate')}</label><input id="itemEstimatePriceModalFinal" type="number" value={formData.estimatePrice} onChange={(e) => setFormData(prev => ({ ...prev, estimatePrice: e.target.value }))} step="0.01" min="0" disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div>)
                  : (<>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label htmlFor="itemActualPriceModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('item.price.actual')} *</label>
                        <div className="flex items-center">
                          <input type="checkbox" id="isTotalPriceCheckbox" checked={formData.isTotalPrice} onChange={(e) => setFormData(prev => ({ ...prev, isTotalPrice: e.target.checked }))} disabled={isActuallyViewOnly || isProjectClosed} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                          <label htmlFor="isTotalPriceCheckbox" className="ml-2 block text-xs text-gray-600 dark:text-gray-400">{t('item.isTotalPrice', { defaultValue: 'Total price (÷ qty)' })}</label>
                        </div>
                      </div>
                      <input id="itemActualPriceModalFinal" type="number" value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))} step="0.01" min="0" placeholder="0.00" disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" />
                      {formData.isTotalPrice && parseFloat(formData.quantity) > 1 && parseFloat(formData.price) > 0 && (
                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                          {t('item.unitPriceCalculated', { defaultValue: 'Unit price' })}: ฿{((parseFloat(formData.price) || 0) / (parseFloat(formData.quantity) || 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><div><label htmlFor="itemPurchaseDateModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.purchase.date')}</label><input id="itemPurchaseDateModalFinal" type="date" value={formData.purchaseDate} onChange={(e) => setFormData(prev => ({ ...prev, purchaseDate: e.target.value }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div><div><label htmlFor="itemDeliveryDateModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.delivery.date')}</label><input id="itemDeliveryDateModalFinal" type="date" value={formData.deliveryDate} onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" /></div></div>
                  </>)
              )}

              {/* Group Total Price - Only for groups in purchase mode */}
              {formData.isGroup && !isEffectivelyListModeForForm && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
                  <label htmlFor="itemGroupTotalPriceModalFinal" className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                    {t('item.groupTotalPrice', { defaultValue: 'Group Total Price' })}
                  </label>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                    {t('item.groupTotalPrice.hint', { defaultValue: 'Combined actual price for all items in this group' })}
                  </p>
                  <input
                    id="itemGroupTotalPriceModalFinal"
                    type="number"
                    value={formData.groupTotalPrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, groupTotalPrice: e.target.value }))}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    disabled={isActuallyViewOnly || isProjectClosed}
                    className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600 w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              )}

              {/* Grouping Options */}
              <div className="mt-4 border-t pt-4 space-y-4">
                {/* "Is Group" Checkbox - only if not preselected to be a child and not editing a child */}
                {!defaultParentId && !editingItem?.parentId && (
                  <div className="flex items-center">
                    <input type="checkbox" id="itemIsGroupModalFinal" checked={Boolean(formData.isGroup)} onChange={(e) => setFormData(prev => ({ ...prev, isGroup: e.target.checked, name: e.target.checked && !prev.groupName ? prev.name : prev.name, groupName: e.target.checked && !prev.groupName ? prev.name : prev.groupName, unit: e.target.checked ? t('general.set', { defaultValue: 'Set' }) : (project.mode === 'กำลังจัดซื้อ' ? 'ชิ้น' : 'รายการ'), quantity: e.target.checked ? '1' : prev.quantity, selectedGroupId: e.target.checked ? '' : prev.selectedGroupId, parentId: e.target.checked ? undefined : prev.parentId }))} disabled={isActuallyViewOnly || isProjectClosed || !!formData.selectedGroupId} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    <label htmlFor="itemIsGroupModalFinal" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('item.isGroup')}</label>
                  </div>
                )}

                {/* Group Name - only if "Is Group" is checked and not preselected/editing as child */}
                {formData.isGroup && !defaultParentId && !editingItem?.parentId && (
                  <div>
                    <label htmlFor="itemGroupNameModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.groupName')} *</label>
                    <input id="itemGroupNameModalFinal" type="text" value={formData.groupName} onChange={(e) => setFormData(prev => ({ ...prev, groupName: e.target.value, name: e.target.value }))} disabled={isActuallyViewOnly || isProjectClosed} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600" />
                  </div>
                )}

                {/* Add to Existing Group Dropdown - only if not creating a group and not preselected as child */}
                {!formData.isGroup && !defaultParentId && availableGroups.length > 0 && (
                  <div>
                    <label htmlFor="itemSelectedGroupIdModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.addToGroupOptional')}</label>
                    <select
                      id="itemSelectedGroupIdModalFinal"
                      value={formData.selectedGroupId}
                      onChange={(e) => setFormData(prev => ({ ...prev, selectedGroupId: e.target.value, parentId: e.target.value ? parseInt(e.target.value) : undefined, isGroup: e.target.value ? false : prev.isGroup }))}
                      disabled={isActuallyViewOnly || isProjectClosed || formData.isGroup}
                      className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600 w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">{t('general.none')}</option>
                      {availableGroups.filter(g => g.id !== editingItem?.id).map(group => (
                        <option key={group.id} value={group.id.toString()}>{group.groupName || group.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Purchaser and Emergency - Hidden for group headers */}
              {!formData.isGroup && (
                <>
                  <div className="mt-4"><label htmlFor="itemPurchaserModalFinal" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('item.purchaser')}</label><select id="itemPurchaserModalFinal" value={formData.purchaser} onChange={(e) => setFormData(prev => ({ ...prev, purchaser: e.target.value }))} disabled={!!(isActuallyViewOnly || isProjectClosed || (isEffectivelyListModeForForm && editingItem?.purchaser && editingItem.status !== 'pending' && !isAdminOrSupervisor || isProjectClosed))} className="dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600"><option value="">{t('general.select')} {t('item.purchaser')}</option>{availablePurchasers.map((user: User) => (<option key={user.id} value={user.id}>{user.fullName} ({user.team})</option>))}</select></div>
                  {!isEffectivelyListModeForForm && (<div className="flex items-center pt-2"><input type="checkbox" id="itemIsEmergencyModalFinal" checked={Boolean(formData.isEmergency)} onChange={(e) => setFormData(prev => ({ ...prev, isEmergency: e.target.checked }))} disabled={isActuallyViewOnly || isProjectClosed} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" /><label htmlFor="itemIsEmergencyModalFinal" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">{t('item.emergency')}</label></div>)}
                </>
              )}

              {/* Attachments - 3 Separate Sections by Type with Drag-and-Drop */}
              <div className="pt-4 space-y-4">
                {/* Handler to change attachment type on drop */}
                {(() => {
                  const handleDragStart = (e: React.DragEvent, attId: string) => {
                    e.dataTransfer.setData('attachmentId', attId);
                    e.dataTransfer.effectAllowed = 'move';
                  };
                  const handleDragOver = (e: React.DragEvent) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  };
                  const handleDrop = (e: React.DragEvent, targetType: 'proof_of_purchase' | 'product_picture' | 'general') => {
                    e.preventDefault();
                    const attId = e.dataTransfer.getData('attachmentId');
                    if (attId) {
                      setAttachments(attachments.map(a =>
                        a.id === attId ? { ...a, attachmentType: targetType } : a
                      ));
                    }
                  };

                  return (
                    <>
                      {/* Section 1: Proof of Purchase (หลักฐานการซื้อ) */}
                      <div
                        className="border-2 border-amber-200 dark:border-amber-700 rounded-lg p-3 bg-amber-50 dark:bg-amber-900/20 transition-colors"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, 'proof_of_purchase')}
                        onDragEnter={(e) => { e.currentTarget.classList.add('border-amber-500', 'bg-amber-100'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-amber-500', 'bg-amber-100'); }}
                      >
                        <label className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">
                          <Receipt className="w-5 h-5" /> {t('attachment.type.proof_of_purchase', { defaultValue: 'หลักฐานการซื้อ' })}
                        </label>
                        {attachments.filter(a => a.attachmentType === 'proof_of_purchase').length > 0 && (
                          <div className="space-y-1 mb-2 max-h-24 overflow-y-auto">
                            {attachments.filter(a => a.attachmentType === 'proof_of_purchase').map((att: Attachment) => (
                              <div
                                key={att.id}
                                draggable={!isActuallyViewOnly && !isProjectClosed}
                                onDragStart={(e) => handleDragStart(e, att.id)}
                                className={`flex items-center justify-between text-xs p-1.5 bg-white dark:bg-slate-800 rounded ${!isActuallyViewOnly && !isProjectClosed ? 'cursor-grab active:cursor-grabbing hover:bg-amber-100' : ''}`}
                              >
                                <span className="truncate flex-1 mr-2">{att.name}</span>
                                <div className="flex items-center space-x-1">
                                  {(att.url || att.data) && <button type="button" onClick={() => viewAttachmentInModal(att)} className="text-blue-600 hover:text-blue-700 p-1"><Eye className="w-3 h-3" /></button>}
                                  {!isActuallyViewOnly && !isProjectClosed && <button type="button" onClick={() => handleAttachmentDelete(att, attachments, setAttachments, t)} className="text-red-500 hover:text-red-700 p-1"><X className="w-3 h-3" /></button>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {!isActuallyViewOnly && !isProjectClosed && (
                          <FileUploadComponent projectId={project.id} itemId={editingItem?.id || Date.now()} userId={currentUser?.id || 'unknown'} attachments={attachments} onAttachmentsChange={(updated) => setAttachments(updated)} disabled={false} maxFiles={999} onUploadingStateChange={setIsCurrentlyUploading} t={t} compact={true} attachmentType="proof_of_purchase" />
                        )}
                      </div>

                      {/* Section 2: Product Picture (รูปภาพสินค้า) */}
                      <div
                        className="border-2 border-blue-200 dark:border-blue-700 rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20 transition-colors"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, 'product_picture')}
                        onDragEnter={(e) => { e.currentTarget.classList.add('border-blue-500', 'bg-blue-100'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100'); }}
                      >
                        <label className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                          <Camera className="w-5 h-5" /> {t('attachment.type.product_picture', { defaultValue: 'รูปภาพสินค้า' })}
                        </label>
                        {attachments.filter(a => a.attachmentType === 'product_picture').length > 0 && (
                          <div className="space-y-1 mb-2 max-h-24 overflow-y-auto">
                            {attachments.filter(a => a.attachmentType === 'product_picture').map((att: Attachment) => (
                              <div
                                key={att.id}
                                draggable={!isActuallyViewOnly && !isProjectClosed}
                                onDragStart={(e) => handleDragStart(e, att.id)}
                                className={`flex items-center justify-between text-xs p-1.5 bg-white dark:bg-slate-800 rounded ${!isActuallyViewOnly && !isProjectClosed ? 'cursor-grab active:cursor-grabbing hover:bg-blue-100' : ''}`}
                              >
                                <span className="truncate flex-1 mr-2">{att.name}</span>
                                <div className="flex items-center space-x-1">
                                  {(att.url || att.data) && <button type="button" onClick={() => viewAttachmentInModal(att)} className="text-blue-600 hover:text-blue-700 p-1"><Eye className="w-3 h-3" /></button>}
                                  {!isActuallyViewOnly && !isProjectClosed && <button type="button" onClick={() => handleAttachmentDelete(att, attachments, setAttachments, t)} className="text-red-500 hover:text-red-700 p-1"><X className="w-3 h-3" /></button>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {!isActuallyViewOnly && !isProjectClosed && (
                          <FileUploadComponent projectId={project.id} itemId={editingItem?.id || Date.now()} userId={currentUser?.id || 'unknown'} attachments={attachments} onAttachmentsChange={(updated) => setAttachments(updated)} disabled={false} maxFiles={999} onUploadingStateChange={setIsCurrentlyUploading} t={t} compact={true} attachmentType="product_picture" />
                        )}
                      </div>

                      {/* Section 3: General Attachments (ไฟล์แนบทั่วไป) */}
                      <div
                        className="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-slate-800/50 transition-colors"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, 'general')}
                        onDragEnter={(e) => { e.currentTarget.classList.add('border-gray-500', 'bg-gray-200'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-gray-500', 'bg-gray-200'); }}
                      >
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          <Paperclip className="w-5 h-5" /> {t('attachment.type.general', { defaultValue: 'ไฟล์แนบทั่วไป' })}
                        </label>
                        {attachments.filter(a => !a.attachmentType || a.attachmentType === 'general').length > 0 && (
                          <div className="space-y-1 mb-2 max-h-24 overflow-y-auto">
                            {attachments.filter(a => !a.attachmentType || a.attachmentType === 'general').map((att: Attachment) => (
                              <div
                                key={att.id}
                                draggable={!isActuallyViewOnly && !isProjectClosed}
                                onDragStart={(e) => handleDragStart(e, att.id)}
                                className={`flex items-center justify-between text-xs p-1.5 bg-white dark:bg-slate-700 rounded ${!isActuallyViewOnly && !isProjectClosed ? 'cursor-grab active:cursor-grabbing hover:bg-gray-200' : ''}`}
                              >
                                <span className="truncate flex-1 mr-2">{att.name}</span>
                                <div className="flex items-center space-x-1">
                                  {(att.url || att.data) && <button type="button" onClick={() => viewAttachmentInModal(att)} className="text-blue-600 hover:text-blue-700 p-1"><Eye className="w-3 h-3" /></button>}
                                  {!isActuallyViewOnly && !isProjectClosed && <button type="button" onClick={() => handleAttachmentDelete(att, attachments, setAttachments, t)} className="text-red-500 hover:text-red-700 p-1"><X className="w-3 h-3" /></button>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {!isActuallyViewOnly && !isProjectClosed && (
                          <FileUploadComponent projectId={project.id} itemId={editingItem?.id || Date.now()} userId={currentUser?.id || 'unknown'} attachments={attachments} onAttachmentsChange={(updated) => setAttachments(updated)} disabled={false} maxFiles={999} onUploadingStateChange={setIsCurrentlyUploading} t={t} compact={true} attachmentType="general" />
                        )}
                      </div>
                    </>
                  );
                })()}

                {attachments.length === 0 && (isActuallyViewOnly || isProjectClosed) && (<p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('general.no.files')}</p>)}
              </div>
            </div>
            {isPreviewingAttachment && attachmentToPreview && (
              <div className="modal-preview-column custom-scrollbar">
                <div className="attachment-display-area">
                  {AttachmentManager.canPreviewImage(attachmentToPreview) ? (
                    <img src={attachmentToPreview.url || attachmentToPreview.data} alt={attachmentToPreview.name} className="attachment-image-preview attachment-image-preview-constrained" />
                  ) : attachmentToPreview.type === 'application/pdf' && attachmentToPreview.url ? (
                    <iframe src={attachmentToPreview.url} className="attachment-iframe-preview attachment-iframe-preview-constrained" title={attachmentToPreview.name}></iframe>
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-400 p-4">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
                      <p className="font-medium text-gray-700 dark:text-gray-300 break-all">{attachmentToPreview.name}</p>
                      <p>{t('attachment.no.preview', { defaultValue: 'Preview not available.' })}</p>
                    </div>
                  )}
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{attachmentToPreview.type} - {AttachmentManager.formatFileSize(attachmentToPreview.size)}</div>
                {(attachmentToPreview.url || AttachmentManager.getDownloadUrl(attachmentToPreview)) && <a href={attachmentToPreview.url || AttachmentManager.getDownloadUrl(attachmentToPreview)} download={attachmentToPreview.name} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 flex-shrink-0 self-start"><Download className="w-3 h-3 mr-1.5" />{t('action.download')}</a>}
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button onClick={isPreviewingAttachment ? closeAttachmentPreviewInModal : handleAttemptCloseModal} className="btn-secondary btn-modal-action dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600 dark:hover:bg-slate-600">
              {isPreviewingAttachment ? t('action.back') : (isActuallyViewOnly ? t('action.close') : t('action.cancel'))}
            </button>
            {!isActuallyViewOnly && !isProjectClosed && !isPreviewingAttachment && (
              <button onClick={handleSave} className="btn-primary btn-modal-action">{t('action.save')}</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const FundManagementModal: React.FC = () => {
    // All users start with 'add' tab - the allocate functionality is merged into add tab
    const getDefaultTab = (): 'add' | 'history' | 'requests' => {
      return 'add';
    };

    const [activeSubTab, setActiveSubTab] = useState<'add' | 'history' | 'requests'>(getDefaultTab());
    const [allocations, setAllocations] = useState<Record<string, { amount: string; limit: string }>>({});
    // Fund request state
    const [fundRequestAmount, setFundRequestAmount] = useState('');
    const [fundRequestReason, setFundRequestReason] = useState('');
    const [fundRequestAttachments, setFundRequestAttachments] = useState<Attachment[]>([]);
    const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [fundAmount, setFundAmount] = useState('');
    const [fundDescription, setFundDescription] = useState('');
    const [fundSource, setFundSource] = useState<'project' | 'other'>('other');
    const [fundDestination, setFundDestination] = useState<FundDestinationType>('project_main_fund');
    const [selectedUserForFund, setSelectedUserForFund] = useState<string>('');
    const [fundAttachments, setFundAttachments] = useState<Attachment[]>([]); // For "Add Fund" tab
    const [confirmingSetAllocationForUserId, setConfirmingSetAllocationForUserId] = useState<string | null>(null);
    const [currentAllocationAttachments, setCurrentAllocationAttachments] = useState<Attachment[]>([]); // For "Allocate" tab
    const [fundEntryForAttachmentPreview, setFundEntryForAttachmentPreview] = useState<FundEntry | null>(null);
    const [attachmentToPreviewInFundHistory, setAttachmentToPreviewInFundHistory] = useState<Attachment | null>(null);
    const [editingFundEntryAttachments, setEditingFundEntryAttachments] = useState<FundEntry | null>(null);
    const [currentEditingFundEntryAttachmentsList, setCurrentEditingFundEntryAttachmentsList] = useState<Attachment[]>([]);

    const [reassigningFundEntryId, setReassigningFundEntryId] = useState<number | null>(null);
    const [reassignTargetUserId, setReassignTargetUserId] = useState<string>('');

    // Add state for editing fund entry details (amount, description)
    const [editingFundEntry, setEditingFundEntry] = useState<FundEntry | null>(null);
    const [editingFundAmount, setEditingFundAmount] = useState<string>('');
    const [editingFundDescription, setEditingFundDescription] = useState<string>('');

    // Helper function to recalculate fund totals after editing
    const recalculateFundTotals = (updatedProject: Project): Project => {
      const projectCopy = { ...updatedProject };

      // Some callers pass a project whose items array was already blanked for
      // saving (items live in the subcollection). Fall back to the live items
      // list - otherwise every user's spentAmount would be recalculated as 0.
      const itemsForSpentCalculation = (projectCopy.items && projectCopy.items.length > 0)
        ? projectCopy.items
        : project.items;

      // Recalculate purchaser fund totals
      projectCopy.purchaserFunds = projectCopy.purchaserFunds.map(fund => {
        const updatedFund = { ...fund };

        // Recalculate allocatedAmount from allocated_to_user entries
        const allocatedEntries = fund.fundEntries.filter(entry => entry.type === 'allocated_to_user');
        updatedFund.allocatedAmount = allocatedEntries.reduce((sum, entry) => sum + entry.amount, 0);

        // Recalculate additionalFunds from additional_to_user entries
        const additionalEntries = fund.fundEntries.filter(entry => entry.type === 'additional_to_user');
        updatedFund.additionalFunds = additionalEntries.reduce((sum, entry) => sum + entry.amount, 0);

        // Recalculate availableAmount (allocated + additional - spent).
        // IMPORTANT: use the same canonical formula as the item save/approve
        // paths, the bottom bar, and project closure validation. The previous
        // local formula ignored groupTotalPrice and group shipping and counted
        // superseded originals, so every funds-tab action stamped different
        // numbers into spentAmount than every item action.
        const spentAmount = calculateSpentAmountForUser(fund.purchaserId, itemsForSpentCalculation);
        updatedFund.spentAmount = spentAmount;
        updatedFund.availableAmount = updatedFund.allocatedAmount + updatedFund.additionalFunds - spentAmount;

        return updatedFund;
      });

      // Recalculate project totals correctly based on FUND ENTRIES, not balances
      // The totalReceived should represent all money that came INTO the project, not internal transfers

      // Money from initial project fund (from project.fundEntries with type 'initial_project')
      const totalFromInitialEntries = projectCopy.fundEntries.filter(entry => entry.type === 'initial_project').reduce((sum, entry) => sum + entry.amount, 0);

      // Money from additional project fund additions (from project.fundEntries with type 'additional_project')
      const totalFromAdditionalProject = projectCopy.fundEntries.filter(entry => entry.type === 'additional_project').reduce((sum, entry) => sum + entry.amount, 0);

      // Money added directly to users from external sources (from purchaserFunds.fundEntries with type 'additional_to_user')
      // Money added directly to users from external sources (from purchaserFunds.additionalFunds)
      // This includes all manual additions and adjustments (like balance clears) that affect the user's total fund
      const totalFromAdditionalUsers = (projectCopy.purchaserFunds || []).reduce((sum, fund) => {
        return sum + (fund.additionalFunds || 0);
      }, 0);

      // Standard Modern Calculation
      if (totalFromInitialEntries > 0) {
        projectCopy.totalReceived = totalFromInitialEntries + totalFromAdditionalProject + totalFromAdditionalUsers;
      } else {
        // Fallback for Legacy Projects
        const currentRemainingProjectFund = projectCopy.initialFund || 0;
        const totalAllocatedToUsers = projectCopy.purchaserFunds.reduce((sum, pf) => sum + (pf.allocatedAmount || 0), 0);
        const effectiveProjectLevelFund = currentRemainingProjectFund + totalAllocatedToUsers;

        projectCopy.totalReceived = effectiveProjectLevelFund + totalFromAdditionalUsers;
      }

      // Items live in the items subcollection. Several callers setDoc the
      // returned object directly, so never hand back a full items array or it
      // gets written into the project document (1MB limit risk + stale copies).
      projectCopy.items = [];

      return projectCopy;
    };

    // Manual recalculation function for debugging
    const handleManualRecalculation = async () => {
      let recalculatedProject = recalculateFundTotals(project);

      try {
        const { doc, setDoc } = await import('firebase/firestore');
        const { db } = await import('../firebaseConfig');
        const projectDocRef = doc(db, 'projects', project.id.toString());

        recalculatedProject.items = []; // Items are in subcollection - keep empty
        await setDoc(projectDocRef, recalculatedProject);
        // Local state will be updated automatically by the realtime listener

        alert('Fund totals have been recalculated and saved!');
      } catch (error) {
        alert('Failed to save recalculation. Please try again.');
      }
    };

    useEffect(() => {
      const initialAllocations: Record<string, { amount: string; limit: string }> = {};
      availablePurchasers.forEach(user => {
        const existingFund = project.purchaserFunds.find(pf => pf.purchaserId === user.id);
        initialAllocations[user.id] = { amount: existingFund?.allocatedAmount.toString() || '0', limit: existingFund?.budgetLimit?.toString() || '' };
      });
      setAllocations(initialAllocations);

      // Auto-recalculation check on project load to prevent stale data
      const checkAndFixTotals = async () => {
        const recalculatedProject = recalculateFundTotals(project);

        // Check if totals are different (indicating stale data)
        if (Math.abs(recalculatedProject.totalReceived - project.totalReceived) > 0.001) {

          try {
            const { doc, setDoc } = await import('firebase/firestore');
            const { db } = await import('../firebaseConfig');
            const projectDocRef = doc(db, 'projects', project.id.toString());

            recalculatedProject.items = []; // Items are in subcollection - keep empty
            await setDoc(projectDocRef, recalculatedProject);
            // Local state will be updated automatically by the realtime listener
          } catch (error) {
          }
        } else {
        }
      };

      // Run auto-fix check after a short delay to ensure UI is ready
      setTimeout(checkAndFixTotals, 1000);

    }, [project.id, project.purchaserFunds, availablePurchasers]);

    const handleAllocationChange = (userId: string, value: string) => setAllocations(prev => ({ ...prev, [userId]: { ...prev[userId], amount: value } }));

    const handleSetUserAllocation = async (userIdToAllocate: string) => {
      if (!currentUser || !(currentUser.role === 'supervisor' || currentUser.role === 'admin')) return;
      const allocData = allocations[userIdToAllocate]; if (!allocData) { alert("Error: Allocation data not found for user."); return; }
      const newTotalAllocatedFromProject = parseFloat(allocData.amount || '0');
      if (isNaN(newTotalAllocatedFromProject) || newTotalAllocatedFromProject < 0) { alert(`Invalid allocation amount. Amount must be zero or positive.`); return; }
      const updatedPurchaserFunds = project.purchaserFunds.map(pf => ({ ...pf, fundEntries: [...pf.fundEntries] }));
      let purchaserFund = updatedPurchaserFunds.find(f => f.purchaserId === userIdToAllocate);
      const currentAllocatedFromProject = purchaserFund ? purchaserFund.allocatedAmount : 0;
      const allocationDelta = newTotalAllocatedFromProject - currentAllocatedFromProject;
      if (project.initialFund - allocationDelta < 0) { alert(t('fund.insufficientProjectFund')); return; }
      let newProjectInitialFund = project.initialFund - allocationDelta;
      const userFullName = availablePurchasers.find(u => u.id === userIdToAllocate)?.fullName || userIdToAllocate;
      let auditDetails = `Project fund changed by ฿${(-allocationDelta).toLocaleString()}. `;
      if (!purchaserFund) { purchaserFund = { purchaserId: userIdToAllocate, allocatedAmount: 0, additionalFunds: 0, spentAmount: 0, availableAmount: 0, fundEntries: [], isProjectAllocationSet: false, }; updatedPurchaserFunds.push(purchaserFund); }
      purchaserFund.allocatedAmount = newTotalAllocatedFromProject; purchaserFund.isProjectAllocationSet = true;
      // Recompute spent fresh instead of trusting the stored value, which can
      // be stale (e.g. after an item deletion that predates the funds fix)
      purchaserFund.spentAmount = calculateSpentAmountForUser(userIdToAllocate, project.items);
      purchaserFund.availableAmount = purchaserFund.allocatedAmount + purchaserFund.additionalFunds - purchaserFund.spentAmount;
      if (allocationDelta !== 0) {
        const fundEntryDescription = allocationDelta > 0 ? `${t('fund.allocateFromProjectFund')} ฿${allocationDelta.toLocaleString()}` : `Returned ฿${Math.abs(allocationDelta).toLocaleString()} to project fund`;
        const fundEntry: FundEntry = {
          id: generateItemId(),
          amount: allocationDelta,
          description: fundEntryDescription,
          date: new Date().toISOString(),
          attachments: currentAllocationAttachments, // Use attachments for this allocation
          createdAt: new Date().toISOString(),
          purchaserId: userIdToAllocate,
          type: 'allocated_to_user',
          transferFromId: allocationDelta > 0 ? 'project_main_fund' : userIdToAllocate,
          transferToId: allocationDelta > 0 ? userIdToAllocate : 'project_main_fund',
        };
        purchaserFund.fundEntries.push(fundEntry); auditDetails += `${userFullName} project allocation set to ฿${newTotalAllocatedFromProject.toLocaleString()} (change: ฿${allocationDelta.toLocaleString()}). `;
        addNotification({ userId: userIdToAllocate, type: 'fund', title: t('fund.management'), message: `Your project allocation for "${project.name}" was set to ฿${newTotalAllocatedFromProject.toLocaleString()}. Change: ฿${allocationDelta.toLocaleString()}`, projectId: project.id, projectName: project.name });
      }
      const auditEntry = createAuditEntry(currentUser.id, currentUser.fullName, 'USER_PROJECT_ALLOCATION_SET', auditDetails.trim(), project.id, { oldProjectFund: project.initialFund, userId: userIdToAllocate, oldAllocation: currentAllocatedFromProject }, { newProjectFund: newProjectInitialFund, userId: userIdToAllocate, newAllocation: newTotalAllocatedFromProject });
      const updatedProjectData: Project = { ...project, initialFund: newProjectInitialFund, purchaserFunds: updatedPurchaserFunds, auditTrail: [...project.auditTrail, auditEntry] };

      // CRITICAL FIX: Save allocation to database

      try {
        const { doc, setDoc } = await import('firebase/firestore');
        const { db } = await import('../firebaseConfig');
        const { removeUndefinedValues } = await import('../utils');
        const projectDocRef = doc(db, 'projects', project.id.toString());

        // Save project data without timestamp updates
        updatedProjectData.items = []; // Items are in subcollection - keep empty to avoid 1MB limit
        const cleanedData = removeUndefinedValues(updatedProjectData);
        await setDoc(projectDocRef, cleanedData);
      } catch (dbError) {
        alert('Failed to save fund allocation to database. Please try again.');
        return; // Don't continue with local updates if database save failed
      }

      // Local state will be updated automatically by the realtime listener
      setAllocations(prev => ({ ...prev, [userIdToAllocate]: { ...prev[userIdToAllocate], amount: newTotalAllocatedFromProject.toString() } }));
      setCurrentAllocationAttachments([]); // Reset attachments after use
      alert(`${t('fund.allocate')} for ${userFullName} ${t('general.success')}`);
    };

    const handleAddFunds = async () => {
      if (!currentUser || !fundAmount) return; const amount = parseFloat(fundAmount); if (isNaN(amount) || amount <= 0) { alert(t('error.invalidAmount', { defaultValue: 'Invalid amount.' })); return; }
      const { items: _items, auditTrail: _auditTrail, ...projectWithoutLargeArrays } = project;
      let updatedProjectData = { ...projectWithoutLargeArrays, items: [], auditTrail: [], fundEntries: [...project.fundEntries], purchaserFunds: project.purchaserFunds.map(pf => ({ ...pf, fundEntries: [...pf.fundEntries] })) };
      let notificationMessage = ''; let notificationUserId = ''; let auditDetails = ''; let fundEntryType: FundEntry['type'] = 'additional_project';
      const fundEntryBase: Omit<FundEntry, 'type' | 'id' | 'purchaserId' | 'targetUserId' | 'transferFromId' | 'transferToId'> = { amount, description: fundDescription.trim() || 'Fund addition', date: new Date().toISOString(), attachments: fundAttachments, createdAt: new Date().toISOString(), };
      if (fundSource === 'project') {
        if (fundDestination !== 'user_project_fund' || !selectedUserForFund) { alert(t('error.fundFromProjectToUserOnly', { defaultValue: 'Funds from Project Fund must be allocated to a specific user.' })); return; }
        if (updatedProjectData.initialFund < amount) { alert(t('fund.insufficientProjectFund')); return; }
        const targetUser = allUsers.find(u => u.id === selectedUserForFund); if (!targetUser) { alert(t('error.userNotFound', { defaultValue: 'Target user not found.' })); return; }
        updatedProjectData.initialFund -= amount; fundEntryType = 'allocated_to_user';
        const existingIndex = updatedProjectData.purchaserFunds.findIndex(f => f.purchaserId === selectedUserForFund);
        if (existingIndex >= 0) {
          updatedProjectData.purchaserFunds[existingIndex].allocatedAmount += amount; updatedProjectData.purchaserFunds[existingIndex].availableAmount += amount; updatedProjectData.purchaserFunds[existingIndex].fundEntries.push({ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id, targetUserId: selectedUserForFund, transferFromId: 'project_main_fund', transferToId: selectedUserForFund });
        } else { updatedProjectData.purchaserFunds.push({ purchaserId: selectedUserForFund, allocatedAmount: amount, additionalFunds: 0, spentAmount: 0, availableAmount: amount, fundEntries: [{ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id, targetUserId: selectedUserForFund, transferFromId: 'project_main_fund', transferToId: selectedUserForFund }], }); }
        notificationMessage = `฿${amount.toLocaleString()} allocated from project fund to ${targetUser.fullName}.`; notificationUserId = selectedUserForFund; auditDetails = `Allocated ฿${amount.toLocaleString()} from project fund to ${targetUser.fullName}. Project fund new balance: ฿${updatedProjectData.initialFund.toLocaleString()}`;
      } else {
        if (fundDestination === 'project_main_fund') {
          // Check user role: normal users should add to their personal fund, not main project fund
          if (currentUser.role === 'user') {
            // Redirect to user's personal fund
            fundEntryType = 'additional_to_user';
            const existingIndex = updatedProjectData.purchaserFunds.findIndex(f => f.purchaserId === currentUser.id);
            if (existingIndex >= 0) {
              updatedProjectData.purchaserFunds[existingIndex].additionalFunds += amount;
              updatedProjectData.purchaserFunds[existingIndex].availableAmount += amount;
              updatedProjectData.purchaserFunds[existingIndex].fundEntries.push({ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id, targetUserId: currentUser.id });
            } else {
              updatedProjectData.purchaserFunds.push({
                purchaserId: currentUser.id,
                allocatedAmount: 0,
                additionalFunds: amount,
                spentAmount: 0,
                availableAmount: amount,
                fundEntries: [{ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id, targetUserId: currentUser.id }],
              });
            }
            updatedProjectData.totalReceived += amount;
            notificationMessage = t('fund.addedToUserInProject', { userName: currentUser.fullName });
            auditDetails = `Added ฿${amount.toLocaleString()} to ${currentUser.fullName}'s personal fund (normal user adding to project fund).`;
          } else {
            // Admin/Supervisor: add to main project fund
            fundEntryType = 'additional_project';
            // Increase both total received (for reporting) AND initialFund (remaining project fund used for allocations)
            updatedProjectData.totalReceived += amount;
            updatedProjectData.initialFund = (updatedProjectData.initialFund || 0) + amount;
            updatedProjectData.fundEntries.push({ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id });
            notificationMessage = t('fund.addedToProjectMain');
            auditDetails = `Added ฿${amount.toLocaleString()} to project main fund (source: Other). New project total received: ฿${updatedProjectData.totalReceived.toLocaleString()}. New remaining project fund: ฿${updatedProjectData.initialFund.toLocaleString()}`;
          }
        } else if (fundDestination === 'user_project_fund' && selectedUserForFund) {
          fundEntryType = 'additional_to_user'; const targetUser = allUsers.find(u => u.id === selectedUserForFund); if (!targetUser) { alert(t('error.userNotFound', { defaultValue: 'Target user not found.' })); return; }
          const existingIndex = updatedProjectData.purchaserFunds.findIndex(f => f.purchaserId === selectedUserForFund);
          if (existingIndex >= 0) {
            updatedProjectData.purchaserFunds[existingIndex].additionalFunds += amount; updatedProjectData.purchaserFunds[existingIndex].availableAmount += amount; updatedProjectData.purchaserFunds[existingIndex].fundEntries.push({ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id, targetUserId: selectedUserForFund });
          } else { updatedProjectData.purchaserFunds.push({ purchaserId: selectedUserForFund, allocatedAmount: 0, additionalFunds: amount, spentAmount: 0, availableAmount: amount, fundEntries: [{ ...fundEntryBase, id: generateItemId(), type: fundEntryType, purchaserId: currentUser.id, targetUserId: selectedUserForFund }], }); }
          updatedProjectData.totalReceived += amount; notificationMessage = t('fund.addedToUserInProject', { userName: targetUser.fullName }); notificationUserId = selectedUserForFund; auditDetails = `Added ฿${amount.toLocaleString()} directly to ${targetUser.fullName}'s project fund (source: Other).`;
        } else { alert(t('error.missingFields', { defaultValue: 'Please select a destination and user if applicable.' })); return; }
      }
      // Use AuditService instead of appending to array
      await AuditService.addAuditEntry(project.id, createAuditEntry(currentUser.id, currentUser.fullName, 'FUNDS_ADDED_VIA_TAB', auditDetails, project.id));

      // CRITICAL: Recalculate all fund totals after adding funds
      updatedProjectData = recalculateFundTotals(updatedProjectData);

      // CRITICAL FIX: Save fund addition to database

      // Save to database first, then update local state
      (async () => {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { db } = await import('../firebaseConfig');
          const { removeUndefinedValues } = await import('../utils');
          const projectDocRef = doc(db, 'projects', project.id.toString());

          const cleanedData = removeUndefinedValues(updatedProjectData);
          await setDoc(projectDocRef, cleanedData);

          // Only update local state after successful database save
          // Local state will be updated automatically by the realtime listener

          if (notificationUserId) { addNotification({ userId: notificationUserId, type: 'fund', title: t('notification.fund.added'), message: `You received ฿${amount.toLocaleString()} for project "${project.name}". Reason: ${fundDescription || 'Additional funds'}. Source: ${fundSource === 'project' ? 'Project Fund' : 'Other'}.`, projectId: project.id, projectName: project.name }); }
          alert(notificationMessage || (t('fund.add') + ' ' + t('general.success')));
          setFundAmount(''); setFundDescription(''); setFundAttachments([]); setSelectedUserForFund(''); setFundSource('other'); setFundDestination('project_main_fund');
        } catch (dbError) {
          alert('Failed to save fund addition to database. Please try again.');
          return; // Don't update local state if database save failed
        }
      })();
    };

    return (
      <div className="modal-overlay" onClick={() => setShowFundManagement(false)}>
        <div className="modal-content fund-management-modal-content w-full max-h-[90vh] relative fund-management-modal" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowFundManagement(false)}
            className="btn-close-modal"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
          {/* Restructured Header */}
          <div className="modal-header">
            <div className="modal-title-block">
              <h2 className="modal-title">{t('fund.management')}</h2>
              <span className="modal-version">v{packageJson.version}</span>
            </div>
            {/* Tab Navigation - Pill Style */}
            <div className="funding-modal-tabs-list">
              <nav className="funding-modal-tabs-nav" aria-label="Fund Management Tabs">
                <button
                  onClick={() => setActiveSubTab('add')}
                  className={`funding-modal-tab ${activeSubTab === 'add' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  aria-selected={activeSubTab === 'add'}
                >
                  <Plus className="w-4 h-4 mr-1 inline" /> {t('fund.add')}
                </button>
                <button
                  onClick={() => setActiveSubTab('history')}
                  className={`funding-modal-tab ${activeSubTab === 'history' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  aria-selected={activeSubTab === 'history'}
                >
                  <FileText className="w-4 h-4 mr-1 inline" /> {t('fund.history')}
                </button>
                <button
                  onClick={() => setActiveSubTab('requests')}
                  className={`funding-modal-tab tab-requests ${activeSubTab === 'requests' ? 'active' : ''}`}
                  type="button"
                  role="tab"
                  aria-selected={activeSubTab === 'requests'}
                >
                  <Edit className="w-4 h-4 mr-1 inline" /> {t('fund.requests')}
                  {(project.fundRequests?.filter(r => r.status === 'pending').length || 0) > 0 && (
                    <span className="pending-badge">
                      {project.fundRequests?.filter(r => r.status === 'pending').length}
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </div>
          <div className="modal-body custom-scrollbar">
            {activeSubTab === 'add' && (
              <div className="funding-modal-tab-content">
                <div className="space-y-4">
                  {/* Fund Summary Panel - Shows project fund and user balances for quick reference */}
                  {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                    <div className="fund-summary-card">
                      <div className="flex justify-between items-center">
                        <span className="summary-label">{t('fund.availableProjectFund', { defaultValue: 'Available Project Fund' })}</span>
                        <span className="summary-value">฿{project.initialFund.toLocaleString()}</span>
                      </div>
                      {/* Collapsible user balances */}
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 font-medium">{t('fund.viewUserBalances', { defaultValue: 'View User Balances' })} ({availablePurchasers.length})</summary>
                        <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                          {availablePurchasers.map((user: User) => {
                            const userFund = project.purchaserFunds.find(f => f.purchaserId === user.id);
                            const displaySpentAmount = calculateSpentAmountForUser(user.id, project.items);
                            const totalAllocatedToUser = (userFund?.allocatedAmount || 0) + (userFund?.additionalFunds || 0);
                            const displayBalanceAmount = totalAllocatedToUser - displaySpentAmount;
                            return (
                              <div key={user.id} className="flex justify-between text-xs bg-white/80 p-2 rounded border border-blue-100">
                                <span className="font-medium text-gray-700">{user.fullName}</span>
                                <span className={displayBalanceAmount >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                  ฿{displayBalanceAmount.toLocaleString()}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold">{t('fund.add')}</h3>
                  <div className="space-y-4">
                    {/* Fund Source: Users can only use 'Other Source' */}
                    {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                      <div><label className="block text-sm font-medium mb-1">{t('fund.source', { defaultValue: 'Fund Source' })} *</label><select value={fundSource} onChange={(e) => { setFundSource(e.target.value as 'project' | 'other'); if (e.target.value === 'project') { setFundDestination('user_project_fund'); if (!selectedUserForFund && availablePurchasers.length > 0) { setSelectedUserForFund(availablePurchasers[0].id); } } }} className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"><option value="other">{t('fund.source.other', { defaultValue: 'Other Source (New Money)' })}</option><option value="project">{t('fund.source.project', { defaultValue: 'Transfer from Project Fund' })}</option></select></div>
                    )}
                    {/* Fund Destination: Users can only select their own fund */}
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('fund.destination')} *</label>
                      {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') ? (
                        <select value={fundDestination} onChange={(e) => setFundDestination(e.target.value as FundDestinationType)} disabled={fundSource === 'project'} className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500">
                          <option value="project_main_fund" disabled={fundSource === 'project'}>{t('fund.destination.project')}</option>
                          <option value="user_project_fund">{t('fund.destination.user')}</option>
                        </select>
                      ) : (
                        <div>
                          <input type="text" value={t('fund.destination.myFund', { defaultValue: 'My Personal Fund (in this project)' })} disabled className="w-full p-2 border border-gray-300 rounded bg-gray-100" />
                        </div>
                      )}
                    </div>
                    {/* Select User for Fund: Admins/Supervisors can select, users default to themselves */}
                    {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && fundDestination === 'user_project_fund' && (
                      <div><label className="block text-sm font-medium mb-1">{t('fund.selectUserForFund')} *</label><select value={selectedUserForFund} onChange={(e) => setSelectedUserForFund(e.target.value)} className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"><option value="">{t('general.select')} {t('user.user')}</option>{availablePurchasers.map(user => (<option key={user.id} value={user.id}>{user.fullName} ({user.team})</option>))}</select></div>
                    )}
                    {/* Amount, Description, Attachments are common */}
                    <div><label className="block text-sm font-medium mb-1">{t('general.amount')} *</label><input type="number" value={fundAmount} onChange={(e) => setFundAmount(e.target.value)} placeholder="0.00" step="0.01" className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" /></div>
                    <div><label className="block text-sm font-medium mb-1">{t('general.description')}</label><textarea value={fundDescription} onChange={(e) => setFundDescription(e.target.value)} placeholder={t('fund.balance.reason')} style={{ height: '4rem' }} className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" /></div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('item.files')}</label>
                      <FileUploadComponent projectId={project.id} itemId={0} userId={currentUser?.id || 'unknown'} attachments={fundAttachments} onAttachmentsChange={setFundAttachments} t={t} maxFiles={10} />
                      {fundAttachments.length > 0 && (
                        <div className="space-y-1 mt-3 max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded p-2 custom-scrollbar">
                          {fundAttachments.map((att: Attachment) => (
                            <div key={att.id} className="flex items-center justify-between text-sm p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded">
                              <span className="truncate flex-1 mr-2 inline-flex items-center text-gray-700 dark:text-gray-200" title={att.name}>
                                {AttachmentManager.canPreviewImage(att) ? <ImageIcon className="w-4 h-4 inline mr-1.5 text-gray-500 dark:text-gray-400 flex-shrink-0" /> : <FileText className="w-4 h-4 inline mr-1.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
                                {att.name}
                              </span>
                              <div className="flex items-center flex-shrink-0 space-x-2">
                                {(att.url || AttachmentManager.getDownloadUrl(att)) && (
                                  <a
                                    href={att.url || AttachmentManager.getDownloadUrl(att)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800 text-xs"
                                    title={t('action.download')}
                                  >
                                    {t('action.download')}
                                  </a>
                                )}
                                <button onClick={() => handleAttachmentDelete(att, fundAttachments, setFundAttachments, t)} className="text-red-600 hover:text-red-800 text-xs" title={t('action.remove')}>
                                  {t('action.remove')}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={handleAddFunds}
                      disabled={!fundAmount || (fundDestination === 'user_project_fund' && !(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && !currentUser?.id) || ((currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && fundDestination === 'user_project_fund' && !selectedUserForFund)}
                      className="btn-fund-add-premium"
                    >
                      <Plus className="w-4 h-4" /> {t('fund.add')}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {activeSubTab === 'history' && (
              <div className="funding-modal-tab-content">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5" /> {t('fund.history')}
                  </h3>

                  <div className="space-y-3">
                    {/* Project Main Fund History - Visible to all users */}
                    <div className="fund-history-section">
                      <div className="section-header">
                        <Briefcase className="w-4 h-4 inline mr-1" /> {t('fund.projectMainFund')}
                      </div>
                      {!(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                        <p className="text-xs text-gray-500 mb-3 italic">{t('fund.projectMainFund.userNote', { defaultValue: 'This shows the overall project budget and funding history.' })}</p>
                      )}

                      {(() => {
                        const allEntries = [];

                        // Add initial fund entry if it exists (from initialFund field)
                        if (project.initialFund && project.initialFund > 0) {
                          const hasInitialEntry = (project.fundEntries || []).some(entry => entry.type === 'initial_project');
                          if (!hasInitialEntry) {
                            allEntries.push({
                              id: 'initial-fund-fallback',
                              amount: project.initialFund,
                              description: 'Initial Budget',
                              createdAt: project.createdAt,
                              type: 'initial_project' as const,
                              isFallback: true
                            });
                          }
                        }

                        // Add all fund entries from fundEntries array
                        const mainFundEntries = (project.fundEntries || [])
                          .filter(entry => entry.type === 'initial_project' || entry.type === 'additional_project');
                        allEntries.push(...mainFundEntries);

                        // Sort by creation date (newest first) - handle empty dates safely
                        allEntries.sort((a, b) => {
                          const getDateValue = (dateStr: string) => {
                            if (!dateStr || dateStr === '') return 0;
                            const parsed = Date.parse(dateStr);
                            return isNaN(parsed) ? 0 : parsed;
                          };
                          const dateA = getDateValue(a.createdAt);
                          const dateB = getDateValue(b.createdAt);
                          return dateB - dateA;
                        });

                        return allEntries.length > 0 ? allEntries.map((entry: any) => (
                          <div key={entry.id} className="fund-history-item flex items-center justify-between">
                            <div className="flex-grow">
                              {editingFundEntry?.id === entry.id ? (
                                // Editing mode for project main fund
                                <div className="space-y-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                                    <input
                                      type="number"
                                      value={editingFundAmount}
                                      onChange={(e) => setEditingFundAmount(e.target.value)}
                                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                      step="0.01"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                      value={editingFundDescription}
                                      onChange={(e) => setEditingFundDescription(e.target.value)}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                      rows={2}
                                    />
                                  </div>
                                  <div className="flex space-x-2 mt-2">
                                    <button
                                      onClick={() => {
                                        // Save changes to project main fund
                                        const amount = parseFloat(editingFundAmount);
                                        if (isNaN(amount) || amount < 0) {
                                          alert('Please enter a valid positive amount');
                                          return;
                                        }

                                        // Update the project fund entry
                                        let updatedProject = { ...project };
                                        if (entry.isFallback) {
                                          // Update initialFund for fallback entries
                                          updatedProject.initialFund = amount;
                                        } else {
                                          // Update in fundEntries array
                                          const entryIndex = updatedProject.fundEntries.findIndex(fe => fe.id === entry.id);
                                          if (entryIndex !== -1) {
                                            updatedProject.fundEntries[entryIndex] = {
                                              ...entry,
                                              amount: amount,
                                              description: editingFundDescription
                                            };
                                          }
                                        }

                                        // CRITICAL: Recalculate all fund totals after editing
                                        updatedProject = recalculateFundTotals(updatedProject);

                                        // CRITICAL FIX: Save project main fund edit to database

                                        // Save to database first, then update local state
                                        (async () => {
                                          try {
                                            const { doc, setDoc } = await import('firebase/firestore');
                                            const { db } = await import('../firebaseConfig');
                                            const { removeUndefinedValues } = await import('../utils');
                                            const projectDocRef = doc(db, 'projects', project.id.toString());

                                            const cleanedData = removeUndefinedValues(updatedProject);
                                            await setDoc(projectDocRef, cleanedData);

                                            // Local state will be updated automatically by the realtime listener

                                            // Clear editing state
                                            setEditingFundEntry(null);
                                            setEditingFundAmount('');
                                            setEditingFundDescription('');

                                            addNotification({
                                              userId: currentUser?.id || '',
                                              type: 'fund',
                                              title: t('fund.entry.updated.title', { defaultValue: 'Project Fund Updated' }),
                                              message: t('fund.entry.updated.message', { amount: amount.toLocaleString(), defaultValue: `Project fund updated to ฿${amount.toLocaleString()}` }),
                                              projectId: project.id
                                            });
                                          } catch (dbError) {
                                            alert('Failed to save fund edit to database. Please try again.');
                                            return; // Don't update local state if database save failed
                                          }
                                        })();
                                      }}
                                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                    >
                                      {t('action.save')}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingFundEntry(null);
                                        setEditingFundAmount('');
                                        setEditingFundDescription('');
                                      }}
                                      className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                                    >
                                      {t('action.cancel')}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                // Display mode for project main fund
                                <>
                                  <div className="font-medium text-gray-800">฿{entry.amount.toLocaleString()}</div>
                                  <div className="text-sm text-gray-600">
                                    {entry.description || (entry.type === 'initial_project' ? t('project.initial.budget') : t('fund.additional'))}
                                    {entry.isFallback && <span className="ml-1 text-xs text-gray-400">{t('fund.fromInitialSetup')}</span>}
                                  </div>
                                  <div className="text-xs text-gray-400">{(() => {
                                    if (!entry.createdAt || entry.createdAt === '') return t('general.noDate');
                                    const parsed = Date.parse(entry.createdAt);
                                    return isNaN(parsed) ? 'Invalid date' : new Date(entry.createdAt).toLocaleDateString();
                                  })()}</div>
                                </>
                              )}
                            </div>
                            <div className="text-sm flex items-center space-x-2">
                              <span className={`status-badge ${entry.type === 'initial_project' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                {entry.type === 'initial_project' ? t('project.initial.budget') : t('fund.additional')}
                              </span>
                              {/* Edit button for project main fund entries (admins/supervisors only) */}
                              {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && editingFundEntry?.id !== entry.id && (
                                <button
                                  onClick={() => {
                                    setEditingFundEntry(entry);
                                    setEditingFundAmount(entry.amount.toString());
                                    setEditingFundDescription(entry.description || (entry.type === 'initial_project' ? t('project.initial.budget') : t('fund.additional')));
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800 px-1 py-0.5 hover:bg-blue-50 rounded"
                                >
                                  <Edit className="w-3 h-3 inline mr-1" /> {t('action.edit')}
                                </button>
                              )}
                              {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && (
                                <button
                                  onClick={() => {
                                    if (entry.isFallback) { alert(t('fund.cannotRemoveInitialEntry')); return; }
                                    if (!window.confirm(t('fund.entry.remove.confirm', { defaultValue: 'Remove this fund entry? This will decrease the remaining project fund accordingly.' }))) return;
                                    let updatedProject = { ...project };
                                    // Remove from fundEntries
                                    updatedProject.fundEntries = (updatedProject.fundEntries || []).filter(fe => fe.id !== entry.id);
                                    // Adjust remaining project fund when removing additional or initial project entries
                                    if (entry.type === 'additional_project' || entry.type === 'initial_project') {
                                      const newInitial = Math.max(0, (updatedProject.initialFund || 0) - (entry.amount || 0));
                                      updatedProject.initialFund = newInitial;
                                    }
                                    // Recalculate
                                    updatedProject = recalculateFundTotals(updatedProject);
                                    // Persist
                                    (async () => {
                                      try {
                                        const { doc, setDoc } = await import('firebase/firestore');
                                        const { db } = await import('../firebaseConfig');
                                        const { removeUndefinedValues } = await import('../utils');
                                        const projectDocRef = doc(db, 'projects', project.id.toString());
                                        await setDoc(projectDocRef, removeUndefinedValues(updatedProject));
                                        // Local state will be updated automatically by the realtime listener
                                      } catch (err) {
                                        alert('Failed to remove fund entry.');
                                      }
                                    })();
                                  }}
                                  className="text-xs text-red-600 hover:text-red-800 px-1 py-0.5 hover:bg-red-50 rounded"
                                >
                                  🗑 {t('action.remove')}
                                </button>
                              )}
                            </div>
                          </div>
                        )) : <p className="text-sm text-gray-500">{t('general.none')}</p>;
                      })()}
                    </div>
                    {/* Purchaser Fund History - Now visible to all users */}

                    {project.purchaserFunds
                      .map((fund: PurchaserFund) => {
                        const user = allUsers.find(u => u.id === fund.purchaserId);

                        // Allow showing funds even if user isn't found in allUsers - display with user ID as fallback 

                        // FIXED: Show all fund entries to all users (remove restrictive filtering)
                        const userSpecificEntries = fund.fundEntries; // Show all entries for this fund

                        // Show all funds to all users - no restriction based on role

                        const displaySpentAmount = calculateSpentAmountForUser(fund.purchaserId, project.items);
                        const totalAllocatedToUser = fund.allocatedAmount + fund.additionalFunds;
                        const displayAvailableAmount = totalAllocatedToUser - displaySpentAmount;
                        return (
                          <div key={fund.purchaserId} className="fund-history-section">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="section-header mb-0">
                                  👤 {user?.fullName || fund.purchaserId}
                                </span>
                                {fund.isProjectAllocationSet && (
                                  <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap">
                                    {t('fund.allocatedToUser', { defaultValue: 'ALLOCATED TO USER' })}
                                  </span>
                                )}
                                {user?.team && <span className="text-xs text-gray-500">({user.team})</span>}
                              </div>
                              <div className="text-right text-sm">
                                <div className="text-green-600 font-medium">{t('fund.available')}: ฿{displayAvailableAmount.toLocaleString()}</div>
                                <div className="text-red-600">{t('fund.spent')}: ฿{displaySpentAmount.toLocaleString()}</div>
                              </div>
                            </div>
                            <div className="space-y-2">

                              {userSpecificEntries.length > 0 ? userSpecificEntries.map((entry: FundEntry) => (
                                <div key={entry.id} className="fund-history-item flex items-start justify-between">
                                  <div className="flex-grow">
                                    {editingFundEntry?.id === entry.id ? (
                                      // Editing mode
                                      <div className="space-y-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                                          <input
                                            type="number"
                                            value={editingFundAmount}
                                            onChange={(e) => setEditingFundAmount(e.target.value)}
                                            className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            step="0.01"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                          <textarea
                                            value={editingFundDescription}
                                            onChange={(e) => setEditingFundDescription(e.target.value)}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                                            rows={2}
                                          />
                                        </div>
                                        <div className="flex space-x-2 mt-2">
                                          <button
                                            onClick={() => {
                                              // Save changes
                                              const amount = parseFloat(editingFundAmount);
                                              if (isNaN(amount) || amount < 0) {
                                                alert('Please enter a valid positive amount');
                                                return;
                                              }

                                              // Update the fund entry
                                              let updatedProject = { ...project };
                                              const fundIndex = updatedProject.purchaserFunds.findIndex(pf => pf.purchaserId === fund.purchaserId);
                                              if (fundIndex !== -1) {
                                                const entryIndex = updatedProject.purchaserFunds[fundIndex].fundEntries.findIndex(fe => fe.id === entry.id);
                                                if (entryIndex !== -1) {
                                                  updatedProject.purchaserFunds[fundIndex].fundEntries[entryIndex] = {
                                                    ...entry,
                                                    amount: amount,
                                                    description: editingFundDescription
                                                  };

                                                  // CRITICAL: Recalculate all fund totals after editing
                                                  updatedProject = recalculateFundTotals(updatedProject);

                                                  // CRITICAL FIX: Save user fund entry edit to database

                                                  // Save to database first, then update local state
                                                  (async () => {
                                                    try {
                                                      const { doc, setDoc } = await import('firebase/firestore');
                                                      const { db } = await import('../firebaseConfig');
                                                      const { removeUndefinedValues } = await import('../utils');
                                                      const projectDocRef = doc(db, 'projects', project.id.toString());

                                                      const cleanedData = removeUndefinedValues(updatedProject);
                                                      await setDoc(projectDocRef, cleanedData);

                                                      // Local state will be updated automatically by the realtime listener

                                                      // Clear editing state
                                                      setEditingFundEntry(null);
                                                      setEditingFundAmount('');
                                                      setEditingFundDescription('');

                                                      addNotification({
                                                        userId: currentUser?.id || '',
                                                        type: 'fund',
                                                        title: t('fund.entry.updated.title', { defaultValue: 'Fund Entry Updated' }),
                                                        message: t('fund.entry.updated.message', { amount: amount.toLocaleString(), defaultValue: `Fund entry updated to ฿${amount.toLocaleString()}` }),
                                                        projectId: project.id
                                                      });
                                                    } catch (dbError) {
                                                      alert('Failed to save fund edit to database. Please try again.');
                                                      return; // Don't update local state if database save failed
                                                    }
                                                  })();
                                                }
                                              }
                                            }}
                                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                          >
                                            Save
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingFundEntry(null);
                                              setEditingFundAmount('');
                                              setEditingFundDescription('');
                                            }}
                                            className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      // Display mode
                                      <>
                                        <div className="font-medium text-gray-800">฿{entry.amount.toLocaleString()}</div>
                                        <div className="text-sm text-gray-600">{entry.description}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">{entry.createdAt && entry.createdAt !== '' ? new Date(entry.createdAt).toLocaleDateString() : 'No date'}</div>
                                      </>
                                    )}
                                    {/* Attachments section for each entry */}
                                    <div className="mt-1.5 space-y-1">
                                      {entry.attachments && entry.attachments.length > 0 && (
                                        <div className="space-y-1">
                                          {entry.attachments.map((att: Attachment) => (
                                            <div key={att.id} className="flex items-center justify-between">
                                              <span className="text-xs text-gray-700 dark:text-gray-200 truncate flex-1 mr-2" title={att.name}>
                                                {att.name}
                                              </span>
                                              <div className="flex items-center gap-2 flex-shrink-0">
                                                <button
                                                  onClick={() => {
                                                    setFundEntryForAttachmentPreview(entry);
                                                    setAttachmentToPreviewInFundHistory(att);
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                                                  title={t('attachment.view.action', { defaultValue: 'View' })}
                                                >
                                                  <Eye size={14} />
                                                </button>
                                                {(att.url || AttachmentManager.getDownloadUrl(att)) && (
                                                  <a
                                                    href={att.url || AttachmentManager.getDownloadUrl(att)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded inline-flex items-center"
                                                    title={t('action.download')}
                                                  >
                                                    <Download size={14} />
                                                  </a>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* Attachment upload/replace/remove logic */}
                                      {(!entry.attachments || entry.attachments.length === 0) ? (
                                        // No attachment: allow upload for all users
                                        <FileUploadComponent
                                          projectId={project.id}
                                          itemId={entry.id}
                                          userId={currentUser?.id || 'unknown'}
                                          attachments={entry.attachments || []}
                                          onAttachmentsChange={async (newAtts) => {
                                            // Allow up to 10 files
                                            if (newAtts.length > 10) {
                                              alert(t('file.upload.maxFiles.alert', { maxFiles: 10, defaultValue: 'Maximum 10 attachments allowed.' }));
                                              return;
                                            }
                                            // Only allow image/pdf
                                            const file = newAtts[0];
                                            if (file && !(file.type.startsWith('image/') || file.type === 'application/pdf')) {
                                              alert(t('file.upload.validation.errors', { errors: t('file.typeNotAllowed', { defaultValue: 'Only image or PDF allowed.' }), defaultValue: 'Only image or PDF allowed.' }));
                                              return;
                                            }
                                            // Process attachments like item logic
                                            const processedAttachments: Attachment[] = [];
                                            for (const att of newAtts) {
                                              if (att.data && !att.url && att.isLegacy) {
                                                try {
                                                  const migrated = await AttachmentManager.migrateLegacyAttachment(att, project.id, entry.id, currentUser?.id || 'unknown');
                                                  processedAttachments.push(migrated);
                                                } catch (error) {
                                                  processedAttachments.push(att);
                                                }
                                              } else if (att.url && att.storagePath) {
                                                processedAttachments.push(att);
                                              } else if (att.file && !att.url) {
                                                processedAttachments.push(att);
                                              } else if (att.url && !att.storagePath && !(att as any).isLegacy) {
                                                processedAttachments.push(att);
                                              }
                                            }
                                            // Local state will be updated automatically by the realtime listener
                                            addNotification({
                                              userId: currentUser?.id || '',
                                              type: 'fund',
                                              title: t('attachments.updated.title', { defaultValue: 'Attachment Uploaded' }),
                                              message: t('attachments.updated.message', { entryDescription: entry.description, defaultValue: `Attachment for fund entry "${entry.description}" uploaded.` }),
                                              projectId: project.id
                                            });
                                            // Persist to Firestore
                                            try {
                                              const { doc, setDoc } = await import('firebase/firestore');
                                              const { db } = await import('../firebaseConfig');
                                              const { removeUndefinedValues } = await import('../utils');
                                              const projectDocRef = doc(db, 'projects', project.id.toString());
                                              const projectToSave = { ...project } as Project;
                                              const pfIndex = projectToSave.purchaserFunds.findIndex(pf => pf.purchaserId === fund.purchaserId);
                                              if (pfIndex !== -1) {
                                                const feIndex = projectToSave.purchaserFunds[pfIndex].fundEntries.findIndex(fe => fe.id === entry.id);
                                                if (feIndex !== -1) {
                                                  projectToSave.purchaserFunds[pfIndex].fundEntries[feIndex] = {
                                                    ...projectToSave.purchaserFunds[pfIndex].fundEntries[feIndex],
                                                    attachments: processedAttachments
                                                  };
                                                }
                                              }
                                              await setDoc(projectDocRef, removeUndefinedValues(projectToSave));
                                            } catch (err) {
                                            }
                                          }}
                                          t={t}
                                          maxFiles={10}
                                          compact={true}
                                        />
                                      ) : (
                                        // Attachment exists: only sup/admin can replace/remove
                                        (currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && !isProjectClosed && (
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={() => {
                                                if (window.confirm(t('file.upload.replace.confirm', { defaultValue: 'Replacing the attachment will remove the current file. Continue?' }))) {
                                                  setEditingFundEntryAttachments(entry);
                                                  setCurrentEditingFundEntryAttachmentsList([]);
                                                }
                                              }}
                                              className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded"
                                              title={t('attachment.replace.action', { defaultValue: 'Replace Attachment' })}
                                            >
                                              <Upload size={14} />
                                            </button>
                                            <button
                                              onClick={async () => {
                                                if (window.confirm(t('file.remove.confirm', { fileName: entry.attachments[0].name, defaultValue: `Are you sure you want to remove ${entry.attachments[0].name}?` }))) {
                                                  const updatedProject = { ...project };
                                                  const fundCollection = entry.targetUserId === fund.purchaserId || entry.purchaserId === fund.purchaserId ?
                                                    updatedProject.purchaserFunds.find(pf => pf.purchaserId === fund.purchaserId)?.fundEntries :
                                                    updatedProject.fundEntries;
                                                  if (fundCollection) {
                                                    const entryIndex = fundCollection.findIndex(fe => fe.id === entry.id);
                                                    if (entryIndex !== -1) {
                                                      fundCollection[entryIndex].attachments = [];
                                                      addNotification({
                                                        userId: currentUser?.id || '',
                                                        type: 'fund',
                                                        title: t('attachments.updated.title', { defaultValue: 'Attachment Removed' }),
                                                        message: t('attachments.updated.message', { entryDescription: entry.description, defaultValue: `Attachment for fund entry "${entry.description}" removed.` }),
                                                        projectId: project.id
                                                      });
                                                      try {
                                                        const { doc, setDoc } = await import('firebase/firestore');
                                                        const { db } = await import('../firebaseConfig');
                                                        const { removeUndefinedValues } = await import('../utils');
                                                        const projectDocRef = doc(db, 'projects', project.id.toString());
                                                        await setDoc(projectDocRef, removeUndefinedValues(updatedProject));
                                                      } catch (err) {
                                                      }
                                                    }
                                                  }
                                                }
                                              }}
                                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                              title={t('attachment.remove.action', { defaultValue: 'Remove Attachment' })}
                                            >
                                              <X size={14} />
                                            </button>
                                          </div>
                                        )
                                      )}
                                    </div>
                                    {/* Inline Attachment Editor for Replace */}
                                    {editingFundEntryAttachments?.id === entry.id && (
                                      <div className="mt-2 pt-2 border-t border-gray-200">
                                        <FileUploadComponent
                                          projectId={project.id}
                                          itemId={entry.id}
                                          userId={currentUser?.id || 'unknown'}
                                          attachments={[]}
                                          onAttachmentsChange={async (newAtts) => {
                                            // Allow up to 10 files
                                            if (newAtts.length > 10) {
                                              alert(t('file.upload.maxFiles.alert', { maxFiles: 10, defaultValue: 'Maximum 10 attachments allowed.' }));
                                              return;
                                            }
                                            const file = newAtts[0];
                                            if (file && !(file.type.startsWith('image/') || file.type === 'application/pdf')) {
                                              alert(t('file.upload.validation.errors', { errors: t('file.typeNotAllowed', { defaultValue: 'Only image or PDF allowed.' }), defaultValue: 'Only image or PDF allowed.' }));
                                              return;
                                            }
                                            // Process attachments like item logic
                                            const processedAttachments: Attachment[] = [];
                                            for (const att of newAtts) {
                                              if (att.data && !att.url && att.isLegacy) {
                                                try {
                                                  const migrated = await AttachmentManager.migrateLegacyAttachment(att, project.id, entry.id, currentUser?.id || 'unknown');
                                                  processedAttachments.push(migrated);
                                                } catch (error) {
                                                  processedAttachments.push(att);
                                                }
                                              } else if (att.url && att.storagePath) {
                                                processedAttachments.push(att);
                                              } else if (att.file && !att.url) {
                                                processedAttachments.push(att);
                                              } else if (att.url && !att.storagePath && !att.isLegacy) {
                                                processedAttachments.push(att);
                                              }
                                            }
                                            // Save to entry (replace)
                                            const updatedProject = { ...project };
                                            const fundCollection = entry.targetUserId === fund.purchaserId || entry.purchaserId === fund.purchaserId ?
                                              updatedProject.purchaserFunds.find(pf => pf.purchaserId === fund.purchaserId)?.fundEntries :
                                              updatedProject.fundEntries;
                                            if (fundCollection) {
                                              const entryIndex = fundCollection.findIndex(fe => fe.id === entry.id);
                                              if (entryIndex !== -1) {
                                                fundCollection[entryIndex].attachments = processedAttachments;
                                                // Local state will be updated automatically by the realtime listener
                                                addNotification({
                                                  userId: currentUser?.id || '',
                                                  type: 'fund',
                                                  title: t('attachments.updated.title', { defaultValue: 'Attachment Replaced' }),
                                                  message: t('attachments.updated.message', { entryDescription: entry.description, defaultValue: `Attachment for fund entry "${entry.description}" replaced.` }),
                                                  projectId: project.id
                                                });
                                                // Persist replace to Firestore
                                                try {
                                                  const { doc, setDoc } = await import('firebase/firestore');
                                                  const { db } = await import('../firebaseConfig');
                                                  const { removeUndefinedValues } = await import('../utils');
                                                  const projectDocRef = doc(db, 'projects', project.id.toString());
                                                  await setDoc(projectDocRef, removeUndefinedValues(updatedProject));
                                                } catch (err) {
                                                }
                                              }
                                            }
                                            setEditingFundEntryAttachments(null);
                                            setCurrentEditingFundEntryAttachmentsList([]);
                                          }}
                                          t={t}
                                          maxFiles={10}
                                        />
                                        <div className="mt-2 flex justify-center space-x-2">
                                          <button
                                            onClick={() => {
                                              setEditingFundEntryAttachments(null);
                                              setCurrentEditingFundEntryAttachmentsList([]);
                                            }}
                                            className="btn-secondary btn-xs"
                                          >
                                            {t('action.cancel', { defaultValue: 'Cancel' })}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {/* End attachment logic */}
                                    {/* Existing edit logic for attachments (if any) can be left as is or removed if redundant */}
                                  </div>
                                  <div className="text-sm flex-shrink-0 ml-2 flex items-center space-x-2">
                                    {/* Only show badge for types other than 'allocated_to_user' */}
                                    {entry.type !== 'allocated_to_user' && (
                                      <span className={`status-badge ${entry.type === 'additional_to_user' ? 'bg-teal-100 text-teal-800' : entry.type === 'balance_clear' ? 'bg-purple-100 text-purple-800' : entry.type === 'user_spent' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{t(`fund.${entry.type}`, { defaultValue: entry.type.replace(/_/g, ' ') })}</span>
                                    )}
                                    {/* Edit button for admins and supervisors */}
                                    {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && editingFundEntry?.id !== entry.id && (
                                      <button
                                        onClick={() => {
                                          setEditingFundEntry(entry);
                                          setEditingFundAmount(entry.amount.toString());
                                          setEditingFundDescription(entry.description);
                                        }}
                                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                        title={t('fund.entry.edit', { defaultValue: 'Edit fund entry' })}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                    )}
                                    {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && (
                                      <button
                                        onClick={() => {
                                          if (!window.confirm(t('fund.entry.remove.confirm', { defaultValue: 'Remove this fund entry?' }))) return;
                                          let updatedProject = { ...project };
                                          const pfIndex = updatedProject.purchaserFunds.findIndex(pf => pf.purchaserId === fund.purchaserId);
                                          if (pfIndex === -1) return;
                                          updatedProject.purchaserFunds[pfIndex].fundEntries = updatedProject.purchaserFunds[pfIndex].fundEntries.filter(fe => fe.id !== entry.id);
                                          // Recalculate totals after removal
                                          updatedProject = recalculateFundTotals(updatedProject);
                                          (async () => {
                                            try {
                                              const { doc, setDoc } = await import('firebase/firestore');
                                              const { db } = await import('../firebaseConfig');
                                              const { removeUndefinedValues } = await import('../utils');
                                              const projectDocRef = doc(db, 'projects', project.id.toString());
                                              await setDoc(projectDocRef, removeUndefinedValues(updatedProject));
                                              // Local state will be updated automatically by the realtime listener
                                            } catch (err) {
                                              alert('Failed to remove fund entry.');
                                            }
                                          })();
                                        }}
                                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                                        title={t('fund.entry.remove', { defaultValue: 'Remove fund entry' })}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && !isProjectClosed && (
                                      <>
                                        {reassigningFundEntryId === entry.id ? (
                                          <div className="flex items-center space-x-1">
                                            <select value={reassignTargetUserId} onChange={(e) => setReassignTargetUserId(e.target.value)} className="p-1 text-xs border rounded">
                                              <option value="">{t('general.select')} {t('user.user')}</option>
                                              {availablePurchasers.map(u => (
                                                <option key={u.id} value={u.id}>{u.fullName}</option>
                                              ))}
                                            </select>
                                            <button
                                              onClick={async () => {
                                                if (!reassignTargetUserId) { alert(t('general.select')); return; }
                                                let updatedProject = { ...project };
                                                const sourceIndex = updatedProject.purchaserFunds.findIndex(pf => pf.purchaserId === fund.purchaserId);
                                                if (sourceIndex === -1) return;
                                                const entryIndex = updatedProject.purchaserFunds[sourceIndex].fundEntries.findIndex(fe => fe.id === entry.id);
                                                if (entryIndex === -1) return;
                                                const sourceEntry = updatedProject.purchaserFunds[sourceIndex].fundEntries[entryIndex];
                                                const movedEntry = {
                                                  ...sourceEntry,
                                                  targetUserId: reassignTargetUserId,
                                                  purchaserId: (sourceEntry.type === 'allocated_to_user' || sourceEntry.type === 'additional_to_user')
                                                    ? reassignTargetUserId
                                                    : (sourceEntry.purchaserId || reassignTargetUserId)
                                                };
                                                // Remove from source to avoid duplicates
                                                updatedProject.purchaserFunds[sourceIndex].fundEntries.splice(entryIndex, 1);
                                                let targetIndex = updatedProject.purchaserFunds.findIndex(pf => pf.purchaserId === reassignTargetUserId);
                                                if (targetIndex === -1) {
                                                  updatedProject.purchaserFunds.push({ purchaserId: reassignTargetUserId, allocatedAmount: 0, additionalFunds: 0, spentAmount: 0, availableAmount: 0, fundEntries: [], isProjectAllocationSet: false });
                                                  targetIndex = updatedProject.purchaserFunds.length - 1;
                                                }
                                                // Ensure no duplicate id exists in target list before inserting
                                                updatedProject.purchaserFunds[targetIndex].fundEntries = updatedProject.purchaserFunds[targetIndex].fundEntries.filter(fe => fe.id !== movedEntry.id);
                                                updatedProject.purchaserFunds[targetIndex].fundEntries.push(movedEntry);
                                                updatedProject = recalculateFundTotals(updatedProject);
                                                try {
                                                  const { doc, setDoc } = await import('firebase/firestore');
                                                  const { db } = await import('../firebaseConfig');
                                                  const { removeUndefinedValues } = await import('../utils');
                                                  const projectDocRef = doc(db, 'projects', project.id.toString());
                                                  await setDoc(projectDocRef, removeUndefinedValues(updatedProject));
                                                  // Local state will be updated automatically by the realtime listener
                                                  setReassigningFundEntryId(null);
                                                  setReassignTargetUserId('');
                                                } catch (err) {
                                                  alert('Failed to reassign.');
                                                }
                                              }}
                                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                            >
                                              {t('action.save')}
                                            </button>
                                            <button onClick={() => { setReassigningFundEntryId(null); setReassignTargetUserId(''); }} className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600">{t('action.cancel')}</button>
                                          </div>
                                        ) : (
                                          <button onClick={() => { setReassigningFundEntryId(entry.id); setReassignTargetUserId(fund.purchaserId); }} className="p-1 text-purple-700 hover:text-purple-900 hover:bg-purple-50 rounded" title={t('fund.reassign', { defaultValue: 'Reassign' })}>
                                            <ArrowRightCircle size={14} />
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              )) : <p className="text-sm text-gray-500">{t('fund.history.noEntries', { defaultValue: 'No fund transactions yet. Funds will appear here when allocated to you or when you add additional funds.' })}</p>}
                            </div>
                          </div>
                        );
                      })}
                    {project.purchaserFunds.length === 0 && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700">{t('fund.history.noProjectFunds', { defaultValue: 'No user funds have been allocated in this project yet. Use the "Add Fund" or "Allocate" tabs to get started.' })}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Requests Tab Content */}
            {activeSubTab === 'requests' && (
              <div className="funding-modal-tab-content">
                <div className="space-y-6">
                  {/* Request Form for Normal Users */}
                  {currentUser?.role === 'user' && (
                    <div className="request-form-card">
                      <div className="form-header">
                        📝 {t('fund.request.new')}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fund.request.amount')} *</label>
                          <input
                            type="number"
                            value={fundRequestAmount}
                            onChange={(e) => setFundRequestAmount(e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            className="w-full p-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fund.request.reason')} *</label>
                          <textarea
                            value={fundRequestReason}
                            onChange={(e) => setFundRequestReason(e.target.value)}
                            placeholder={t('fund.request.reason')}
                            rows={3}
                            className="w-full p-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                          />
                        </div>
                        <button
                          onClick={async () => {
                            const amount = parseFloat(fundRequestAmount);
                            if (isNaN(amount) || amount <= 0) {
                              alert('Please enter a valid amount');
                              return;
                            }
                            if (!fundRequestReason.trim()) {
                              alert('Please enter a reason for the request');
                              return;
                            }
                            if (!currentUser) return;

                            const newRequest = {
                              id: Date.now().toString(),
                              requesterId: currentUser.id,
                              requesterName: currentUser.fullName,
                              amount,
                              reason: fundRequestReason,
                              status: 'pending' as const,
                              createdAt: new Date().toISOString(),
                              attachments: fundRequestAttachments
                            };

                            const updatedProject = {
                              ...project,
                              fundRequests: [...(project.fundRequests || []), newRequest]
                            };

                            try {
                              const { doc, setDoc } = await import('firebase/firestore');
                              const { db } = await import('../firebaseConfig');
                              const { removeUndefinedValues } = await import('../utils');
                              const projectDocRef = doc(db, 'projects', project.id.toString());
                              await setDoc(projectDocRef, removeUndefinedValues(updatedProject));

                              // Notify supervisors
                              const supervisors = getSupervisorsByTeams(project.teamsInvolved);
                              supervisors.forEach(supervisor => {
                                addNotification({
                                  userId: supervisor.id,
                                  type: 'fund',
                                  title: t('notification.fund.request.new', { userName: currentUser.fullName }),
                                  message: `฿${amount.toLocaleString()} - ${fundRequestReason}`,
                                  projectId: project.id,
                                  projectName: project.name
                                });
                              });

                              setFundRequestAmount('');
                              setFundRequestReason('');
                              setFundRequestAttachments([]);
                              alert(t('fund.request.submit') + ' ' + t('general.success'));
                            } catch (error) {
                              alert('Failed to submit request. Please try again.');
                            }
                          }}
                          className="btn-submit-request"
                        >
                          <Upload className="w-4 h-4 mr-1 inline" /> {t('fund.request.submit')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Pending Requests for Supervisors/Admins */}
                  {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-lg">
                        <Briefcase className="w-4 h-4 inline mr-1" /> {t('fund.request.pendingRequests')}
                        <span className="bg-amber-100 text-amber-700 text-sm px-2 py-0.5 rounded-full">
                          {project.fundRequests?.filter(r => r.status === 'pending').length || 0}
                        </span>
                      </h3>
                      {(project.fundRequests?.filter(r => r.status === 'pending').length || 0) === 0 ? (
                        <div className="empty-requests-state">
                          <span className="empty-icon"><FileText className="w-8 h-8 text-gray-300" /></span>
                          <p>{t('fund.request.noRequests')}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {project.fundRequests?.filter(r => r.status === 'pending').map(request => (
                            <div key={request.id} className="fund-request-card pending">
                              <div className="request-header">
                                <div>
                                  <p className="request-requester">{request.requesterName}</p>
                                  <p className="request-amount">฿{request.amount.toLocaleString()}</p>
                                </div>
                                <div className="request-actions">
                                  <button
                                    onClick={async () => {
                                      if (!currentUser) return;
                                      if (request.amount > project.initialFund) {
                                        alert('Insufficient project funds to approve this request.');
                                        return;
                                      }

                                      let updatedProject = { ...project };
                                      const requestIndex = updatedProject.fundRequests?.findIndex(r => r.id === request.id) ?? -1;
                                      if (requestIndex === -1 || !updatedProject.fundRequests) return;

                                      // Update request status
                                      updatedProject.fundRequests[requestIndex] = {
                                        ...request,
                                        status: 'approved',
                                        reviewedAt: new Date().toISOString(),
                                        reviewedBy: currentUser.id,
                                        reviewerName: currentUser.fullName
                                      };

                                      // Transfer funds
                                      updatedProject.initialFund -= request.amount;
                                      const existingFundIndex = updatedProject.purchaserFunds.findIndex(f => f.purchaserId === request.requesterId);
                                      const newFundEntry = {
                                        id: generateItemId(),
                                        amount: request.amount,
                                        description: `Fund request approved: ${request.reason}`,
                                        date: new Date(),
                                        attachments: request.attachments || [],
                                        createdAt: new Date().toISOString(),
                                        type: 'allocated_to_user' as const,
                                        purchaserId: currentUser.id,
                                        targetUserId: request.requesterId
                                      };

                                      if (existingFundIndex >= 0) {
                                        updatedProject.purchaserFunds[existingFundIndex].allocatedAmount += request.amount;
                                        updatedProject.purchaserFunds[existingFundIndex].availableAmount += request.amount;
                                        updatedProject.purchaserFunds[existingFundIndex].fundEntries.push(newFundEntry);
                                      } else {
                                        updatedProject.purchaserFunds.push({
                                          purchaserId: request.requesterId,
                                          allocatedAmount: request.amount,
                                          additionalFunds: 0,
                                          spentAmount: 0,
                                          availableAmount: request.amount,
                                          fundEntries: [newFundEntry]
                                        });
                                      }

                                      updatedProject = recalculateFundTotals(updatedProject);

                                      try {
                                        const { doc, setDoc } = await import('firebase/firestore');
                                        const { db } = await import('../firebaseConfig');
                                        const { removeUndefinedValues } = await import('../utils');
                                        const projectDocRef = doc(db, 'projects', project.id.toString());
                                        await setDoc(projectDocRef, removeUndefinedValues(updatedProject));

                                        addNotification({
                                          userId: request.requesterId,
                                          type: 'fund',
                                          title: t('notification.fund.request.approved'),
                                          message: `฿${request.amount.toLocaleString()} approved for: ${request.reason}`,
                                          projectId: project.id,
                                          projectName: project.name
                                        });
                                      } catch (error) {
                                        alert('Failed to approve request. Please try again.');
                                      }
                                    }}
                                    className="btn-approve"
                                  >
                                    <Check className="w-4 h-4 mr-1 inline" /> {t('fund.request.approve')}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setRejectingRequestId(request.id);
                                      setRejectionReason('');
                                    }}
                                    className="btn-reject"
                                  >
                                    <XCircle className="w-4 h-4 mr-1 inline" /> {t('fund.request.reject')}
                                  </button>
                                </div>
                              </div>
                              <p className="request-reason">{request.reason}</p>
                              <p className="request-date">{new Date(request.createdAt).toLocaleString()}</p>
                              {rejectingRequestId === request.id && (
                                <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fund.request.rejectionReason')}</label>
                                  <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    rows={2}
                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                  />
                                  <div className="flex gap-2 mt-2">
                                    <button
                                      onClick={async () => {
                                        if (!currentUser) return;

                                        const updatedProject = { ...project };
                                        const requestIndex = updatedProject.fundRequests?.findIndex(r => r.id === request.id) ?? -1;
                                        if (requestIndex === -1 || !updatedProject.fundRequests) return;

                                        updatedProject.fundRequests[requestIndex] = {
                                          ...request,
                                          status: 'rejected',
                                          reviewedAt: new Date().toISOString(),
                                          reviewedBy: currentUser.id,
                                          reviewerName: currentUser.fullName,
                                          rejectionReason: rejectionReason
                                        };

                                        try {
                                          const { doc, setDoc } = await import('firebase/firestore');
                                          const { db } = await import('../firebaseConfig');
                                          const { removeUndefinedValues } = await import('../utils');
                                          const projectDocRef = doc(db, 'projects', project.id.toString());
                                          await setDoc(projectDocRef, removeUndefinedValues(updatedProject));

                                          addNotification({
                                            userId: request.requesterId,
                                            type: 'fund',
                                            title: t('notification.fund.request.rejected'),
                                            message: `฿${request.amount.toLocaleString()} rejected. Reason: ${rejectionReason || 'No reason provided'}`,
                                            projectId: project.id,
                                            projectName: project.name
                                          });

                                          setRejectingRequestId(null);
                                          setRejectionReason('');
                                        } catch (error) {
                                          alert('Failed to reject request. Please try again.');
                                        }
                                      }}
                                      className="bg-red-600 hover:bg-red-700 text-white text-sm py-1 px-3 rounded"
                                    >
                                      {t('action.confirm')}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setRejectingRequestId(null);
                                        setRejectionReason('');
                                      }}
                                      className="bg-gray-400 hover:bg-gray-500 text-white text-sm py-1 px-3 rounded"
                                    >
                                      {t('action.cancel')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* User's Request History (for normal users) */}
                  {currentUser?.role === 'user' && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-800"><FileText className="w-4 h-4 inline mr-1" /> {t('fund.request.yourRequests')}</h3>
                      {(project.fundRequests?.filter(r => r.requesterId === currentUser?.id).length || 0) === 0 ? (
                        <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                          {t('fund.request.noRequests')}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {project.fundRequests?.filter(r => r.requesterId === currentUser?.id).map(request => (
                            <div key={request.id} className={`p-3 rounded-lg border ${request.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                              request.status === 'approved' ? 'bg-green-50 border-green-200' :
                                'bg-red-50 border-red-200'
                              }`}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold">฿{request.amount.toLocaleString()}</p>
                                  <p className="text-sm text-gray-600">{request.reason}</p>
                                  <p className="text-xs text-gray-400">{new Date(request.createdAt).toLocaleString()}</p>
                                </div>
                                <span className={`text-xs font-medium px-2 py-1 rounded ${request.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                  request.status === 'approved' ? 'bg-green-200 text-green-800' :
                                    'bg-red-200 text-red-800'
                                  }`}>
                                  {t(`fund.request.${request.status}`)}
                                </span>
                              </div>
                              {request.status === 'rejected' && request.rejectionReason && (
                                <p className="text-sm text-red-600 mt-2"><XCircle className="w-4 h-4 inline mr-1" />{request.rejectionReason}</p>
                              )}
                              {request.status === 'approved' && request.reviewerName && (
                                <p className="text-sm text-green-600 mt-2"><Check className="w-4 h-4 inline mr-1" />Approved by {request.reviewerName}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* All Requests History (for supervisors/admins) */}
                  {(currentUser?.role === 'supervisor' || currentUser?.role === 'admin') && (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-800"><FileText className="w-4 h-4 inline mr-1" /> {t('fund.request.allRequests')}</h3>
                      {(project.fundRequests?.filter(r => r.status !== 'pending').length || 0) === 0 ? (
                        <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
                          {t('fund.request.noRequests')}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {project.fundRequests?.filter(r => r.status !== 'pending').map(request => (
                            <div key={request.id} className={`p-3 rounded-lg border ${request.status === 'approved' ? 'bg-green-50 border-green-200' :
                              'bg-red-50 border-red-200'
                              }`}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-800">{request.requesterName}</p>
                                  <p className="font-semibold">฿{request.amount.toLocaleString()}</p>
                                  <p className="text-sm text-gray-600">{request.reason}</p>
                                  <p className="text-xs text-gray-400">{new Date(request.createdAt).toLocaleString()}</p>
                                </div>
                                <div className="text-right">
                                  <span className={`text-xs font-medium px-2 py-1 rounded ${request.status === 'approved' ? 'bg-green-200 text-green-800' :
                                    'bg-red-200 text-red-800'
                                    }`}>
                                    {t(`fund.request.${request.status}`)}
                                  </span>
                                  {request.reviewerName && (
                                    <p className="text-xs text-gray-500 mt-1">by {request.reviewerName}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {fundEntryForAttachmentPreview && (
            <AttachmentPreviewPanel
              item={{
                id: fundEntryForAttachmentPreview.id,
                name: fundEntryForAttachmentPreview.description || `Fund Entry ${fundEntryForAttachmentPreview.id}`,
                attachments: fundEntryForAttachmentPreview.attachments || [],
                details: fundEntryForAttachmentPreview.description || '',
                quantity: 1,
                unit: 'transaction',
                price: fundEntryForAttachmentPreview.amount,
                status: 'approved',
                approvals: [],
                createdBy: currentUser?.id || 'system',
                createdAt: fundEntryForAttachmentPreview.createdAt,
                estimatePrice: 0, // Required by Item, default
                category: 'Fund Transaction', // Required by Item, default
                // Optional fields from Item, defaults or undefined
                purchaser: fundEntryForAttachmentPreview.purchaserId || undefined,
                isGroup: false,
                isFromListMode: false,
                lastEditedAt: fundEntryForAttachmentPreview.createdAt,
                lastEditedBy: currentUser?.id || 'system',
                shippingCost: 0,
                parentId: undefined,
                childItemIds: undefined,
                groupName: undefined,
                isEmergency: false,
                isUnsupervisedPurchase: false,
                rejectionReason: undefined,
                deadline: undefined,
                purchaseDate: undefined,
                deliveryDate: undefined,
              }}
              onClose={() => {
                setFundEntryForAttachmentPreview(null);
                setAttachmentToPreviewInFundHistory(null);
              }}
              t={t}
            />
          )}
        </div>
      </div>
    );
  };

  const getVisibleItems = (): Item[] => {
    let topLevelItems: Item[] = project.items.filter(item => !item.parentId);
    let itemsToFilter: Item[];

    if (isListMode) {
      itemsToFilter = topLevelItems.filter(item => !item.isPurchaseItem); // Start with all top-level items for list mode, excluding purchase items

      if (filterPurchaser) {
        itemsToFilter = itemsToFilter.filter(item => {
          if (item.isGroup) {
            const children = project.items.filter(child => child.parentId === item.id);
            return children.some(child => child.purchaser === filterPurchaser || (!child.purchaser && filterPurchaser === 'unassigned'));
          }
          return item.purchaser === filterPurchaser || (!item.purchaser && filterPurchaser === 'unassigned');
        });
      }
      if (filterStatus) {
        itemsToFilter = itemsToFilter.filter(item => {
          if (item.isGroup) {
            const children = project.items.filter(child => child.parentId === item.id);
            return children.some(child => child.status === filterStatus);
          }
          return item.status === filterStatus;
        });
      }

    } else { // Purchase mode
      itemsToFilter = topLevelItems.filter(item => {
        const isPurchaseItem = item.isPurchaseItem === true;
        const isLegacyApproved = item.status === 'approved' && !item.isPurchaseItem && !itemHasPurchaseClone(item.id, project.items);
        const isBought = item.status === 'bought';

        if (item.isGroup) {
          return project.items.some(child => {
            const childIsPurchase = child.isPurchaseItem === true;
            const childIsLegacy = child.status === 'approved' && !child.isPurchaseItem && !itemHasPurchaseClone(child.id, project.items);
            const childIsBought = child.status === 'bought';
            return child.parentId === item.id && (childIsPurchase || childIsLegacy || childIsBought);
          });
        } else {
          return (isPurchaseItem || isLegacyApproved || isBought) || item.isUnsupervisedPurchase === true;
        }
      });

      if (isAdminOrSupervisor) {
        if (filterPurchaser) {
          itemsToFilter = itemsToFilter.filter(item => {
            if (item.isGroup) {
              return project.items.some(child => child.parentId === item.id && (child.status === 'approved' || child.status === 'bought') && (child.purchaser === filterPurchaser || (!child.purchaser && filterPurchaser === 'unassigned')));
            }
            return item.purchaser === filterPurchaser || (!item.purchaser && filterPurchaser === 'unassigned');
          });
        }
      } else {
        itemsToFilter = itemsToFilter.filter(item => {
          if (item.isGroup) {
            return project.items.some(child => child.parentId === item.id && (child.status === 'approved' || child.status === 'bought') && child.purchaser === currentUser?.id);
          }
          // For non-group items, they must be approved/unsupervised AND assigned to current user
          return item.purchaser === currentUser?.id && (item.status === 'approved' || item.status === 'bought' || item.isUnsupervisedPurchase === true);
        });
      }
      if (filterStatus && filterStatus !== 'approved') {
        itemsToFilter = itemsToFilter.filter(item => {
          if (item.isGroup) {
            return project.items.some(child => child.parentId === item.id && child.status === filterStatus);
          }
          return item.status === filterStatus;
        });
      }
    }

    if (searchTerm) {
      itemsToFilter = itemsToFilter.filter(item => {
        const name = (item.groupName || item.name || '').toLowerCase();
        const details = (item.details || '').toLowerCase();
        return name.includes(searchTerm.toLowerCase()) || details.includes(searchTerm.toLowerCase());
      });
    }
    if (filterCategory) {
      itemsToFilter = itemsToFilter.filter(item => {
        if (item.isGroup) {
          const children = project.items.filter(child => child.parentId === item.id);
          return children.some(child => child.category === filterCategory);
        }
        return item.category === filterCategory;
      });
    }
    return itemsToFilter;
  };

  const handleUpdateItemField = (itemId: number, field: 'category' | 'purchaser', value: string) => {
    const allowed = field === 'purchaser' ? canEditPurchaser : canEdit;
    if (!currentUser || isProjectClosed || !allowed) {
      setEditingInlineItemId(null);
      setEditingInlineField(null);
      return;
    }

    const projectToUpdate = projects.find(p => p.id === project.id);
    if (!projectToUpdate) return;

    const itemToUpdate = projectToUpdate.items.find(i => i.id === itemId);
    if (!itemToUpdate) return;

    // Block purchaser edit if item is already approved (admin/supervisor can override)
    if (field === 'purchaser' && itemToUpdate.status === 'approved' && !isAdminOrSupervisor) {
      alert(t('error.cannotEditApprovedItem', { defaultValue: 'Cannot change purchaser for approved items.' }));
      setEditingInlineItemId(null);
      setEditingInlineField(null);
      return;
    }

    const oldItem = { ...itemToUpdate };
    let updatedItem = { ...itemToUpdate, [field]: value, lastEditedAt: new Date().toISOString(), lastEditedBy: currentUser.id };
    let additionalItemsToSave: Item[] = [];

    let auditAction: AuditEntry['action'] = 'ITEM_UPDATED';
    let auditDetails = `Item "${updatedItem.name}" ${field} updated.`;

    if (field === 'category') {
      auditDetails = `Item "${updatedItem.name}" category changed from "${oldItem.category || 'N/A'}" to "${value || 'N/A'}"`;
    } else if (field === 'purchaser') {
      const oldPurchaserUser = allUsers.find(u => u.id === oldItem.purchaser);
      const newPurchaserUser = allUsers.find(u => u.id === value);
      auditDetails = `Item "${updatedItem.name}" purchaser changed from "${oldPurchaserUser ? oldPurchaserUser.fullName : 'Unassigned'}" to "${newPurchaserUser ? newPurchaserUser.fullName : 'Unassigned'}"`;

      // BUSINESS RULE: a group has exactly ONE purchaser. Changing any
      // member's purchaser reassigns the whole group (parent + all children).
      // Purchase clones are also kept in sync with their originals.
      const relatedItems: Item[] = [];
      const groupId = itemToUpdate.isGroup ? itemToUpdate.id : itemToUpdate.parentId;
      projectToUpdate.items.forEach(i => {
        if (i.id === itemId) return;
        const inSameGroup = groupId !== undefined && groupId !== null &&
          (String(i.id) === String(groupId) || String(i.parentId) === String(groupId));
        const isCloneOfEdited = i.isPurchaseItem === true && String(i.originalItemId) === String(itemId);
        const isOriginalOfEdited = itemToUpdate.isPurchaseItem === true && String(itemToUpdate.originalItemId) === String(i.id);
        if (inSameGroup || isCloneOfEdited || isOriginalOfEdited) relatedItems.push(i);
      });

      const affected = relatedItems.filter(i => (i.purchaser || '') !== (value || ''));
      if (affected.length > 0) {
        if (!isAdminOrSupervisor && affected.some(i => i.status === 'approved')) {
          alert(t('error.cannotEditApprovedItem', { defaultValue: 'Cannot change purchaser for approved items.' }));
          setEditingInlineItemId(null);
          setEditingInlineField(null);
          return;
        }
        const conflicting = affected.filter(i => i.purchaser && i.purchaser !== value);
        if (conflicting.length > 0) {
          const currentName = allUsers.find(u => u.id === conflicting[0].purchaser)?.fullName || 'another user';
          const newName = newPurchaserUser?.fullName || 'Unassigned';
          const confirmed = window.confirm(t('item.group.singlePurchaserConfirm', {
            currentName, newName,
            defaultValue: `A group can only have one purchaser. This will change the purchaser of ALL items in this group from "${currentName}" to "${newName}". Continue?`
          }));
          if (!confirmed) {
            setEditingInlineItemId(null);
            setEditingInlineField(null);
            return;
          }
        }
        additionalItemsToSave = affected.map(i => ({
          ...i,
          purchaser: value || undefined,
          lastEditedAt: new Date().toISOString(),
          lastEditedBy: currentUser.id
        }));
      }

      if (value && value !== oldItem.purchaser) {
        addNotification({
          userId: value,
          type: 'assignment',
          title: t('notification.new.assignment'),
          message: `Item "${updatedItem.name}" has been assigned to you in project "${project.name}"`,
          projectId: project.id,
          itemId: updatedItem.id,
          projectName: project.name,
          itemName: updatedItem.name
        });
      }
    }

    const updatedItems = projectToUpdate.items.map(i => {
      if (i.id === itemId) return updatedItem;
      const propagated = additionalItemsToSave.find(a => a.id === i.id);
      return propagated || i;
    });
    const auditEntry = createAuditEntry(currentUser.id, currentUser.fullName, auditAction, auditDetails, itemId, { [field]: oldItem[field] }, { [field]: value });

    // Reassigning a purchaser moves spending between users - recalculate funds
    // for everyone affected (old owner, new owner, propagated group members)
    let recalculatedPurchaserFunds = projectToUpdate.purchaserFunds;
    if (field === 'purchaser') {
      const affectedPurchaserIds = new Set<string>();
      if (oldItem.purchaser) affectedPurchaserIds.add(oldItem.purchaser);
      if (value) affectedPurchaserIds.add(value);
      additionalItemsToSave.forEach(i => {
        const before = projectToUpdate.items.find(p => p.id === i.id);
        if (before?.purchaser) affectedPurchaserIds.add(before.purchaser);
      });
      recalculatedPurchaserFunds = projectToUpdate.purchaserFunds.map(pf => {
        if (!affectedPurchaserIds.has(pf.purchaserId)) return pf;
        const newSpent = calculateSpentAmountForUser(pf.purchaserId, updatedItems);
        return {
          ...pf,
          spentAmount: newSpent,
          availableAmount: (pf.allocatedAmount || 0) + (pf.additionalFunds || 0) - newSpent,
        };
      });
    }

    const updatedProjectData: Project = {
      ...projectToUpdate,
      items: updatedItems,
      purchaserFunds: recalculatedPurchaserFunds,
      auditTrail: [...projectToUpdate.auditTrail, auditEntry]
    };

    // Save to Firebase immediately
    (async () => {
      try {
        const { doc, setDoc } = await import('firebase/firestore');
        const { db } = await import('../firebaseConfig');
        const { removeUndefinedValues } = await import('../utils');

        // Save the updated item (and any group members whose purchaser was
        // propagated) to the items subcollection
        for (const itemToSave of [updatedItem, ...additionalItemsToSave]) {
          await ItemsService.saveItem(project.id.toString(), itemToSave);
        }

        // Save project document WITHOUT items array (items are in subcollection)
        const projectDocRef = doc(db, 'projects', project.id.toString());
        const projectData: any = {
          name: updatedProjectData.name,
          description: updatedProjectData.description,
          initialFund: updatedProjectData.initialFund,
          totalReceived: updatedProjectData.totalReceived,
          items: [], // Items are in subcollection - keep empty
          fundEntries: updatedProjectData.fundEntries,
          purchaserFunds: updatedProjectData.purchaserFunds,
          initialFundAttachments: updatedProjectData.initialFundAttachments || [],
          mode: updatedProjectData.mode,
          status: updatedProjectData.status,
          teamsInvolved: updatedProjectData.teamsInvolved,
          createdBy: updatedProjectData.createdBy,
          createdAt: updatedProjectData.createdAt,
          auditTrail: updatedProjectData.auditTrail,
          categories: updatedProjectData.categories,
          lastUpdated: new Date().toISOString(),
          lastUpdatedBy: currentUser?.id,
          deletionApprovals: updatedProjectData.deletionApprovals || [],
        };
        if (updatedProjectData.initialFundDate) projectData.initialFundDate = updatedProjectData.initialFundDate;
        if (updatedProjectData.deadline) projectData.deadline = updatedProjectData.deadline;
        if (updatedProjectData.closedAt) projectData.closedAt = updatedProjectData.closedAt;
        if (updatedProjectData.closedBy) projectData.closedBy = updatedProjectData.closedBy;
        if (updatedProjectData.deletionInitiatedBy) projectData.deletionInitiatedBy = updatedProjectData.deletionInitiatedBy;

        const cleanedData = removeUndefinedValues(projectData);
        await setDoc(projectDocRef, cleanedData);

        // Clear editing state after successful save
        setEditingInlineItemId(null);
        setEditingInlineField(null);
      } catch (error) {
        alert(t('error.save.failed', { defaultValue: 'Failed to save changes. Please try again.' }));
        // Keep editing state on error so user can retry
      }
    })();
  };

  const handleMoveItemToGroup = () => {
    if (!itemToMoveToGroup || !selectedTargetGroupId || !currentUser || isProjectClosed || !canEdit) {
      setShowMoveToGroupModal(false);
      setItemToMoveToGroup(null);
      setSelectedTargetGroupId('');
      return;
    }

    const targetGroupIdNum = parseInt(selectedTargetGroupId, 10);
    if (isNaN(targetGroupIdNum)) return;

    const projectToUpdate = projects.find(p => p.id === project.id);
    if (!projectToUpdate) return;

    const existingItem = projectToUpdate.items.find(i => i.id === itemToMoveToGroup.id);
    const existingTargetGroup = projectToUpdate.items.find(i => i.id === targetGroupIdNum && i.isGroup);

    if (!existingItem || !existingTargetGroup) {
      alert(t('error.generic', { defaultValue: 'Item or target group not found.' }));
      return;
    }

    // BUSINESS RULE: a group has exactly ONE purchaser. Moving an item into a
    // group makes it adopt the group's purchaser (confirm if it differs).
    const targetGroupMembers = projectToUpdate.items.filter(i =>
      String(i.id) === String(targetGroupIdNum) || String(i.parentId) === String(targetGroupIdNum));
    const groupPurchaser = targetGroupMembers.find(m => m.purchaser)?.purchaser;
    let finalPurchaser = existingItem.purchaser;
    if (groupPurchaser && existingItem.purchaser && existingItem.purchaser !== groupPurchaser) {
      const groupPurchaserName = usersToUse.find(u => u.id === groupPurchaser)?.fullName || 'another user';
      const itemPurchaserName = usersToUse.find(u => u.id === existingItem.purchaser)?.fullName || 'another user';
      const confirmed = window.confirm(t('item.moveToGroup.purchaserConfirm', {
        groupPurchaserName, itemPurchaserName,
        defaultValue: `This group is assigned to "${groupPurchaserName}" but the item is assigned to "${itemPurchaserName}". A group can only have one purchaser - the item will be reassigned to "${groupPurchaserName}". Continue?`
      }));
      if (!confirmed) return;
      finalPurchaser = groupPurchaser;
    } else if (groupPurchaser && !existingItem.purchaser) {
      finalPurchaser = groupPurchaser;
    }

    const itemsToSave: Item[] = [];

    const movedItem: Item = { ...existingItem, parentId: targetGroupIdNum as any, purchaser: finalPurchaser, lastEditedAt: new Date().toISOString(), lastEditedBy: currentUser.id };
    itemsToSave.push(movedItem);

    // Move the item's purchase clone along with it (clones mirror the
    // original's group placement and purchaser)
    const purchaseClone = projectToUpdate.items.find(i => i.isPurchaseItem === true && String(i.originalItemId) === String(existingItem.id));
    if (purchaseClone) {
      itemsToSave.push({ ...purchaseClone, parentId: targetGroupIdNum as any, purchaser: finalPurchaser, lastEditedAt: new Date().toISOString(), lastEditedBy: currentUser.id });
    }

    // Remove from old group's child list if it had one
    if (existingItem.parentId) {
      const oldParentGroup = projectToUpdate.items.find(i => String(i.id) === String(existingItem.parentId) && i.isGroup);
      if (oldParentGroup && oldParentGroup.childItemIds) {
        itemsToSave.push({ ...oldParentGroup, childItemIds: oldParentGroup.childItemIds.filter(id => String(id) !== String(existingItem.id)) });
      }
    }

    // Add to target group's child list
    const updatedTargetGroup: Item = {
      ...existingTargetGroup,
      childItemIds: [...(existingTargetGroup.childItemIds || []), existingItem.id].filter((id, index, self) => self.indexOf(id) === index) as any,
    };
    itemsToSave.push(updatedTargetGroup);

    const updatedItems = projectToUpdate.items.map(i => itemsToSave.find(s => s.id === i.id) || i);

    // Moving items between groups changes group totals (shipping attribution),
    // so recalculate funds for every purchaser involved
    const affectedPurchaserIds = new Set<string>();
    if (existingItem.purchaser) affectedPurchaserIds.add(existingItem.purchaser);
    if (finalPurchaser) affectedPurchaserIds.add(finalPurchaser);
    if (groupPurchaser) affectedPurchaserIds.add(groupPurchaser);
    const recalculatedPurchaserFunds = projectToUpdate.purchaserFunds.map(pf => {
      if (!affectedPurchaserIds.has(pf.purchaserId)) return pf;
      const newSpent = calculateSpentAmountForUser(pf.purchaserId, updatedItems);
      return {
        ...pf,
        spentAmount: newSpent,
        availableAmount: (pf.allocatedAmount || 0) + (pf.additionalFunds || 0) - newSpent,
      };
    });

    const auditDetails = `Item "${existingItem.name}" moved to group "${existingTargetGroup.groupName || existingTargetGroup.name}".`;
    const auditEntry = createAuditEntry(currentUser.id, currentUser.fullName, 'ITEM_MOVED_TO_GROUP', auditDetails, existingItem.id, { oldParentId: itemToMoveToGroup.parentId }, { newParentId: targetGroupIdNum });

    // Persist to Firestore (this handler previously saved nothing, so the move
    // silently disappeared when the realtime listener refreshed)
    (async () => {
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../firebaseConfig');

        for (const itemToSave of itemsToSave) {
          await ItemsService.saveItem(project.id.toString(), itemToSave);
        }

        const projectDocRef = doc(db, 'projects', project.id.toString());
        await updateDoc(projectDocRef, {
          purchaserFunds: recalculatedPurchaserFunds,
          lastUpdated: new Date().toISOString(),
          lastUpdatedBy: currentUser.id,
        });

        try {
          await AuditService.addAuditEntry(project.id.toString(), auditEntry);
        } catch (auditError) {
        }

        // Local state will be updated automatically by the realtime listener
        addNotification({
          userId: existingTargetGroup.createdBy,
          type: 'assignment',
          title: t('item.movedToGroup.notificationTitle', { defaultValue: 'Item Moved to Group' }),
          message: `Item "${existingItem.name}" was moved to your group "${existingTargetGroup.groupName || existingTargetGroup.name}" by ${currentUser.fullName}.`,
          projectId: project.id,
          itemId: existingItem.id
        });
      } catch (error) {
        alert(t('error.save.failed', { defaultValue: 'Failed to save changes. Please try again.' }));
      }
    })();

    setShowMoveToGroupModal(false);
    setItemToMoveToGroup(null);
    setSelectedTargetGroupId('');
  };

  const availableGroups = useMemo(() => project.items.filter(item => item.isGroup && !item.parentId), [project.items]);

  const handleItemStatusUpdate = async (itemId: number, newStatus: 'approved' | 'rejected', rejectionReasonInput?: string) => {
    if (statusUpdatesInFlight.current.has(itemId)) return;
    statusUpdatesInFlight.current.add(itemId);
    try {
      await performItemStatusUpdate(itemId, newStatus, rejectionReasonInput);
    } finally {
      statusUpdatesInFlight.current.delete(itemId);
    }
  };

  const performItemStatusUpdate = async (itemId: number, newStatus: 'approved' | 'rejected', rejectionReasonInput?: string) => {
    if (!currentUser || !canApprove || isProjectClosed) return;

    const projectToUpdate = projects.find(p => p.id === project.id);
    if (!projectToUpdate) return;

    const itemIndex = projectToUpdate.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;

    const itemToUpdate = { ...projectToUpdate.items[itemIndex] };
    const oldItemStatus = itemToUpdate.status;
    const oldApprovals = [...itemToUpdate.approvals];

    // Approving an already-approved item would only create duplicate clones
    if (newStatus === 'approved' && oldItemStatus === 'approved') return;

    let updatedApprovals = [...itemToUpdate.approvals];
    let finalStatus = itemToUpdate.status;
    let auditAction: AuditEntry['action'] = 'ITEM_UPDATED';
    let auditDetails = '';
    let notificationTitle = '';
    let notificationMessage = '';
    let notifyUserIds: string[] = [];

    if (newStatus === 'approved') {
      const existingApprovalIndex = updatedApprovals.findIndex(app => app.teamSupervisorId === currentUser.id);
      if (existingApprovalIndex !== -1) {
        updatedApprovals[existingApprovalIndex] = { ...updatedApprovals[existingApprovalIndex], approved: true, approvedAt: new Date().toISOString() };
      } else {
        updatedApprovals.push({ teamSupervisorId: currentUser.id, teamName: currentUser.team, approved: true, approvedAt: new Date().toISOString(), supervisorFullName: currentUser.fullName });
      }

      const currentApprovalCount = updatedApprovals.filter(app => app.approved).length;

      // Admin can immediately approve items regardless of supervisor requirements
      if (currentUser.role === 'admin' || currentApprovalCount >= numRequiredApprovals) {
        finalStatus = 'approved';
        auditAction = 'ITEM_APPROVED';
        auditDetails = currentUser.role === 'admin'
          ? `Item "${itemToUpdate.name}" approved by admin ${currentUser.fullName}.`
          : `Item "${itemToUpdate.name}" fully approved.`;
        notificationTitle = t('notification.item.approved.title');
        notificationMessage = t('notification.item.approved.message', { itemName: itemToUpdate.name, projectName: project.name });
        if (itemToUpdate.createdBy && itemToUpdate.createdBy !== currentUser.id) notifyUserIds.push(itemToUpdate.createdBy);
        if (itemToUpdate.purchaser && itemToUpdate.purchaser !== currentUser.id && itemToUpdate.purchaser !== itemToUpdate.createdBy) notifyUserIds.push(itemToUpdate.purchaser);

      } else {
        finalStatus = 'pending'; // Remains pending, but approval recorded
        auditAction = 'ITEM_APPROVAL_PROGRESSED';
        auditDetails = `Item "${itemToUpdate.name}" approval progressed by ${currentUser.fullName} (${currentApprovalCount}/${numRequiredApprovals}).`;
        // Potentially notify creator or other supervisors about progress
      }
    } else if (newStatus === 'rejected') {
      const reason = rejectionReasonInput || t('item.rejection.reason.default', { defaultValue: 'No reason provided' });
      finalStatus = 'rejected';
      itemToUpdate.rejectionReason = reason;
      updatedApprovals = []; // Clear previous approvals on rejection
      auditAction = 'ITEM_REJECTED';
      auditDetails = `Item "${itemToUpdate.name}" rejected by ${currentUser.fullName}. Reason: ${reason}`;
      notificationTitle = t('notification.item.rejected.title');
      notificationMessage = t('notification.item.rejected.message', { itemName: itemToUpdate.name, projectName: project.name, reason });
      if (itemToUpdate.createdBy && itemToUpdate.createdBy !== currentUser.id) notifyUserIds.push(itemToUpdate.createdBy);
      if (itemToUpdate.purchaser && itemToUpdate.purchaser !== currentUser.id && itemToUpdate.purchaser !== itemToUpdate.createdBy) notifyUserIds.push(itemToUpdate.purchaser);
    }

    const updatedItem: Item = {
      ...itemToUpdate,
      status: finalStatus,
      approvals: updatedApprovals,
      lastEditedAt: new Date().toISOString(),
      lastEditedBy: currentUser.id,
      // Reset shipping cost to 0 when item is approved (similar to estimate→actual price transition)
      // ONLY if it is a purchase item (which it isn't here, it's the original). 
      // The original keeps its shipping cost? Or resets? 
      // Original serves as "Estimate". So it should keep shipping cost (Estimate). 
      // The previous logic reset it. But now we have a clone. 
      // The clone will have 0 shipping cost initially (handled in creation above).
      // The original should probably KEEP its shipping cost as it represents the "Plan".
      // So we REMOVE the reset logic for the original item.
    };

    let updatedItemsList = projectToUpdate.items.map(i => i.id === itemId ? updatedItem : i);

    // ADD CLONE IF APPROVED - but never a second one. A stale double approval
    // used to create duplicate purchase clones that showed up as duplicated
    // items in the UI.
    const cloneAlreadyExists = itemHasPurchaseClone(itemId, projectToUpdate.items);
    const shouldCreateClone = finalStatus === 'approved' && !itemToUpdate.isPurchaseItem && !cloneAlreadyExists;
    if (shouldCreateClone) {
      const purchaseCloneId = generateItemId();
      const purchaseClone: Item = {
        ...updatedItem, // Copy from the updated original (with Approved status)
        id: purchaseCloneId,
        isPurchaseItem: true,
        originalItemId: itemToUpdate.id,
        shippingCost: 0, // Reset shipping for purchase mode
        price: 0, // Reset actual price
      };
      updatedItemsList.push(purchaseClone);
    }

    // If rejecting, drop any purchase clones of this item (they can only exist
    // here if a stale approval raced this rejection - defence in depth)
    const clonesToDelete = finalStatus === 'rejected'
      ? projectToUpdate.items.filter(i => i.isPurchaseItem === true && String(i.originalItemId) === String(itemId))
      : [];
    if (clonesToDelete.length > 0) {
      const cloneIds = new Set(clonesToDelete.map(c => c.id));
      updatedItemsList = updatedItemsList.filter(i => !cloneIds.has(i.id));
    }

    // Recalculate purchaser funds if status changed to/from approved
    let finalPurchaserFunds = projectToUpdate.purchaserFunds.map(pf => ({ ...pf, fundEntries: [...pf.fundEntries] }));
    if ((oldItemStatus !== 'approved' && finalStatus === 'approved') || (oldItemStatus === 'approved' && finalStatus !== 'approved')) {
      const affectedPurchaserIds = new Set<string>();
      if (itemToUpdate.purchaser) affectedPurchaserIds.add(itemToUpdate.purchaser);

      affectedPurchaserIds.forEach(purchaserId => {
        if (!purchaserId) return;
        let fundIndex = finalPurchaserFunds.findIndex(f => f.purchaserId === purchaserId);
        if (fundIndex !== -1) {
          // Use the same formula as the bottom bar / save path so the stored
          // spentAmount can never double-count an original plus its clone
          const newTotalSpentForThisPurchaser = calculateSpentAmountForUser(purchaserId, updatedItemsList);
          finalPurchaserFunds[fundIndex].spentAmount = newTotalSpentForThisPurchaser;
          finalPurchaserFunds[fundIndex].availableAmount =
            (finalPurchaserFunds[fundIndex].allocatedAmount || 0) +
            (finalPurchaserFunds[fundIndex].additionalFunds || 0) -
            newTotalSpentForThisPurchaser;
        }
      });
    }

    const auditEntry = createAuditEntry(currentUser.id, currentUser.fullName, auditAction, auditDetails, itemId, { status: oldItemStatus, approvals: oldApprovals }, { status: finalStatus, approvals: updatedApprovals });

    const finalUpdatedProjectData: Project = {
      ...projectToUpdate,
      items: updatedItemsList,
      purchaserFunds: finalPurchaserFunds,
      // New audit entries go to the audit_trail subcollection (AuditService);
      // keep the legacy array as-is instead of growing it on every approval.
      auditTrail: projectToUpdate.auditTrail,
    };

    try {
      await AuditService.addAuditEntry(project.id.toString(), auditEntry);
    } catch (auditError) {
      // Audit logging must never block the approval itself
    }

    // Save to Firestore immediately
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      const { removeUndefinedValues } = await import('../utils');

      // Save all updated/new items to the items subcollection
      // Find the updated item and any new purchase clone
      const updatedOriginalItem = updatedItemsList.find(i => i.id === itemId);
      if (updatedOriginalItem) {
        await ItemsService.saveItem(project.id.toString(), updatedOriginalItem);
      }

      // If approved, also save the new purchase clone (it was added to updatedItemsList)
      if (shouldCreateClone) {
        const purchaseClone = updatedItemsList.find(i => i.isPurchaseItem && String(i.originalItemId) === String(itemId));
        if (purchaseClone) {
          await ItemsService.saveItem(project.id.toString(), purchaseClone);
        }
      }

      // If rejected, remove any orphaned purchase clones from the subcollection
      for (const clone of clonesToDelete) {
        await ItemsService.deleteItem(project.id.toString(), clone.id);
      }

      // Save project document WITHOUT items array (items are in subcollection)
      const projectDocRef = doc(db, 'projects', project.id.toString());
      const projectData: any = {
        name: finalUpdatedProjectData.name,
        description: finalUpdatedProjectData.description,
        initialFund: finalUpdatedProjectData.initialFund,
        totalReceived: finalUpdatedProjectData.totalReceived,
        items: [], // Items are in subcollection - keep empty
        fundEntries: finalUpdatedProjectData.fundEntries,
        purchaserFunds: finalUpdatedProjectData.purchaserFunds,
        initialFundAttachments: finalUpdatedProjectData.initialFundAttachments || [],
        mode: finalUpdatedProjectData.mode,
        status: finalUpdatedProjectData.status,
        teamsInvolved: finalUpdatedProjectData.teamsInvolved,
        createdBy: finalUpdatedProjectData.createdBy,
        createdAt: finalUpdatedProjectData.createdAt,
        auditTrail: finalUpdatedProjectData.auditTrail,
        categories: finalUpdatedProjectData.categories,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: currentUser?.id,
        deletionApprovals: finalUpdatedProjectData.deletionApprovals || [],
      };

      if (finalUpdatedProjectData.initialFundDate) projectData.initialFundDate = finalUpdatedProjectData.initialFundDate;
      if (finalUpdatedProjectData.deadline) projectData.deadline = finalUpdatedProjectData.deadline;
      if (finalUpdatedProjectData.closedAt) projectData.closedAt = finalUpdatedProjectData.closedAt;
      if (finalUpdatedProjectData.closedBy) projectData.closedBy = finalUpdatedProjectData.closedBy;
      if (finalUpdatedProjectData.deletionInitiatedBy) projectData.deletionInitiatedBy = finalUpdatedProjectData.deletionInitiatedBy;

      const cleanedData = removeUndefinedValues(projectData);
      await setDoc(projectDocRef, cleanedData);
    } catch (dbError) {
      alert('Failed to save status update to database. Please try again.');
      return; // Don't continue with notifications if database save failed
    }

    // Local state will be updated automatically by the realtime listener

    notifyUserIds.forEach(userId => {
      if (userId && userId !== currentUser.id) { // Don't notify self
        addNotification({
          userId,
          type: finalStatus === 'approved' ? 'item_approved' : 'item_rejected',
          title: notificationTitle,
          message: notificationMessage,
          projectId: project.id,
          itemId: itemToUpdate.id,
          projectName: project.name,
          itemName: itemToUpdate.name
        });
      }
    });
    if (finalStatus === 'approved' && itemToUpdate.purchaser && itemToUpdate.purchaser !== currentUser.id && !notifyUserIds.includes(itemToUpdate.purchaser)) {
      addNotification({
        userId: itemToUpdate.purchaser,
        type: 'item_approved_for_purchase',
        title: t('notification.item.approvedForPurchase.title', { defaultValue: 'Item Ready for Purchase' }),
        message: t('notification.item.approvedForPurchase.message', { itemName: itemToUpdate.name, projectName: project.name }),
        projectId: project.id,
        itemId: itemToUpdate.id,
        projectName: project.name,
        itemName: itemToUpdate.name
      });
    }
  };

  const handleAdminUnapproveItem = async (itemId: number) => {
    if (!currentUser || currentUser.role !== 'admin' || isProjectClosed) return;

    const projectToUpdate = projects.find(p => p.id === project.id);
    if (!projectToUpdate) return;

    const itemIndex = projectToUpdate.items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;

    const itemToUpdate = { ...projectToUpdate.items[itemIndex] };
    const oldItemStatus = itemToUpdate.status;
    const oldApprovals = [...itemToUpdate.approvals];

    // Only unapprove items that are currently approved
    if (oldItemStatus !== 'approved') return;

    const finalStatus: ItemStatus = 'pending';
    const updatedApprovals: ItemApproval[] = [];

    let updatedItemsList: Item[];

    if (itemToUpdate.isPurchaseItem && itemToUpdate.originalItemId) {
      const originalItemIndex = projectToUpdate.items.findIndex(i => i.id === itemToUpdate.originalItemId);

      if (originalItemIndex !== -1) {
        const originalItem = projectToUpdate.items[originalItemIndex];
        const updatedOriginal: Item = {
          ...originalItem,
          status: finalStatus,
          approvals: updatedApprovals,
          lastEditedAt: new Date().toISOString(),
          lastEditedBy: currentUser.id
        };
        // Update original to pending and remove the clone
        updatedItemsList = projectToUpdate.items
          .map(i => i.id === itemToUpdate.originalItemId ? updatedOriginal : i)
          .filter(i => i.id !== itemId);
      } else {
        // Fallback if original not found: just remove clone? Or keep it pending?
        // Ideally remove it as it's a purchase item.
        updatedItemsList = projectToUpdate.items.filter(i => i.id !== itemId);
      }
    } else {
      const updatedItem: Item = {
        ...itemToUpdate,
        status: finalStatus,
        approvals: updatedApprovals,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: currentUser.id,
      };

      // Remove the associated purchase item (clone) if it exists
      updatedItemsList = projectToUpdate.items.map(i => i.id === itemId ? updatedItem : i);
      updatedItemsList = updatedItemsList.filter(i => !(i.isPurchaseItem === true && String(i.originalItemId) === String(itemId)));
    }

    // Recalculate purchaser funds because status changed from approved -> pending
    let finalPurchaserFunds = projectToUpdate.purchaserFunds.map(pf => ({ ...pf, fundEntries: [...pf.fundEntries] }));
    const affectedPurchaserIds = new Set<string>();
    if (itemToUpdate.purchaser) affectedPurchaserIds.add(itemToUpdate.purchaser);
    affectedPurchaserIds.forEach(purchaserId => {
      if (!purchaserId) return;
      const fundIndex = finalPurchaserFunds.findIndex(f => f.purchaserId === purchaserId);
      if (fundIndex !== -1) {
        // Same formula as the bottom bar / save path (no clone double-counting)
        const newTotalSpentForThisPurchaser = calculateSpentAmountForUser(purchaserId, updatedItemsList);
        finalPurchaserFunds[fundIndex].spentAmount = newTotalSpentForThisPurchaser;
        finalPurchaserFunds[fundIndex].availableAmount =
          (finalPurchaserFunds[fundIndex].allocatedAmount || 0) +
          (finalPurchaserFunds[fundIndex].additionalFunds || 0) -
          newTotalSpentForThisPurchaser;
      }
    });

    const auditEntry = createAuditEntry(
      currentUser.id,
      currentUser.fullName,
      'ITEM_UNAPPROVED',
      `Item "${itemToUpdate.name}" unapproved by admin ${currentUser.fullName}.`,
      itemId,
      { status: oldItemStatus, approvals: oldApprovals },
      { status: finalStatus, approvals: updatedApprovals }
    );

    const finalUpdatedProjectData: Project = {
      ...projectToUpdate,
      items: updatedItemsList,
      purchaserFunds: finalPurchaserFunds,
      // New entries go to AuditService; keep (don't wipe) the legacy array
      auditTrail: projectToUpdate.auditTrail,
    };

    await AuditService.addAuditEntry(project.id.toString(), auditEntry);

    // Save to Firestore
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      const { removeUndefinedValues } = await import('../utils');

      // Handle item updates and deletions in the subcollection
      if (itemToUpdate.isPurchaseItem && itemToUpdate.originalItemId) {
        // We're unapproving a purchase clone -> update original to pending, delete clone
        const originalItemIndex = projectToUpdate.items.findIndex(i => i.id === itemToUpdate.originalItemId);
        if (originalItemIndex !== -1) {
          const updatedOriginal = updatedItemsList.find(i => i.id === itemToUpdate.originalItemId);
          if (updatedOriginal) {
            await ItemsService.saveItem(project.id.toString(), updatedOriginal);
          }
        }
        // Delete the purchase clone
        await ItemsService.deleteItem(project.id.toString(), itemId);
      } else {
        // We're unapproving an original item -> update it to pending, delete purchase clone
        const updatedOriginal = updatedItemsList.find(i => i.id === itemId);
        if (updatedOriginal) {
          await ItemsService.saveItem(project.id.toString(), updatedOriginal);
        }
        // Find and delete ALL purchase clones (a past double-approval bug could
        // have created more than one)
        const purchaseClonesToDelete = projectToUpdate.items.filter(i => i.isPurchaseItem === true && String(i.originalItemId) === String(itemId));
        for (const clone of purchaseClonesToDelete) {
          await ItemsService.deleteItem(project.id.toString(), clone.id);
        }
      }

      // Save project document WITHOUT items array (items are in subcollection)
      const projectDocRef = doc(db, 'projects', project.id.toString());
      const projectData: any = {
        name: finalUpdatedProjectData.name,
        description: finalUpdatedProjectData.description,
        initialFund: finalUpdatedProjectData.initialFund,
        totalReceived: finalUpdatedProjectData.totalReceived,
        items: [], // Items are in subcollection - keep empty
        fundEntries: finalUpdatedProjectData.fundEntries,
        purchaserFunds: finalUpdatedProjectData.purchaserFunds,
        initialFundAttachments: finalUpdatedProjectData.initialFundAttachments || [],
        mode: finalUpdatedProjectData.mode,
        status: finalUpdatedProjectData.status,
        teamsInvolved: finalUpdatedProjectData.teamsInvolved,
        createdBy: finalUpdatedProjectData.createdBy,
        createdAt: finalUpdatedProjectData.createdAt,
        auditTrail: finalUpdatedProjectData.auditTrail,
        categories: finalUpdatedProjectData.categories,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: currentUser?.id,
        deletionApprovals: finalUpdatedProjectData.deletionApprovals || [],
      };
      if (finalUpdatedProjectData.initialFundDate) projectData.initialFundDate = finalUpdatedProjectData.initialFundDate;
      if (finalUpdatedProjectData.deadline) projectData.deadline = finalUpdatedProjectData.deadline;
      if (finalUpdatedProjectData.closedAt) projectData.closedAt = finalUpdatedProjectData.closedAt;
      if (finalUpdatedProjectData.closedBy) projectData.closedBy = finalUpdatedProjectData.closedBy;
      if (finalUpdatedProjectData.deletionInitiatedBy) projectData.deletionInitiatedBy = finalUpdatedProjectData.deletionInitiatedBy;
      const cleanedData = removeUndefinedValues(projectData);
      await setDoc(projectDocRef, cleanedData);
    } catch (dbError) {
      alert('Failed to unapprove item. Please try again.');
      return;
    }

    // Local state will be updated automatically by the realtime listener
  };

  const toggleGroupExpansion = (groupId: number) => {
    setExpandedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const renderItemRow = (item: Item, isChild: boolean = false, parentGroup: Item | null = null) => {
    const isItemGloballyPending = item.status === 'pending';
    const canSupervisorActOnThisItem = activeTab === 'list' && isItemGloballyPending && canApprove && !isProjectClosed && !item.isGroup;
    const currentUserHasSpecificallyApprovedThisItem = item.approvals.some((app: ItemApproval) => app.teamSupervisorId === currentUser?.id && app.approved);

    let displayPrice = 0;
    let unitDisplayPrice = 0;
    let displayEstimatedPriceInPurchase = 0;
    let unitDisplayEstimatedPriceInPurchase = 0;

    if (item.isGroup) {
      const children = project.items.filter(child => child.parentId === item.id);
      const childrenTotalPrice = children.reduce((sum, child) => {
        const priceToUse = activeTab === 'list' ? (child.estimatePrice || 0) : (child.price || 0);
        return sum + (priceToUse * child.quantity) + (child.shippingCost || 0); // Include child.shippingCost
      }, 0);
      displayPrice = childrenTotalPrice + (item.shippingCost || 0); // Include item.shippingCost for the main display
      unitDisplayPrice = 0;

      if (activeTab === 'purchase') {
        const approvedOrBoughtChildren = children.filter(c => c.status === 'approved' || c.status === 'bought');
        const approvedChildrenTotalEstPrice = approvedOrBoughtChildren.reduce((sum, child) => sum + ((child.estimatePrice || 0) * child.quantity) + (child.shippingCost || 0), 0); // Include child.shippingCost
        displayEstimatedPriceInPurchase = approvedChildrenTotalEstPrice + (item.shippingCost || 0); // Include item.shippingCost

        // Use groupTotalPrice if set, otherwise calculate from children
        if (item.groupTotalPrice && item.groupTotalPrice > 0) {
          displayPrice = item.groupTotalPrice + (item.shippingCost || 0);
        } else {
          const approvedChildrenActualPrice = approvedOrBoughtChildren.reduce((sum, child) => sum + ((child.price || 0) * child.quantity) + (child.shippingCost || 0), 0); // Include child.shippingCost
          displayPrice = approvedChildrenActualPrice + (item.shippingCost || 0); // Override displayPrice, Include item.shippingCost
        }
      }
    } else {
      // Check if this is a child item whose parent group has groupTotalPrice set
      const parentHasGroupTotalPrice = isChild && parentGroup && parentGroup.groupTotalPrice && parentGroup.groupTotalPrice > 0;

      displayPrice = ((activeTab === 'list' ? (item.estimatePrice || 0) : (item.price || 0)) * item.quantity) + (item.shippingCost || 0); // Include item.shippingCost
      unitDisplayPrice = activeTab === 'list' ? (item.estimatePrice || 0) : (item.price || 0); // This is unit price, correct as is
      displayEstimatedPriceInPurchase = ((item.estimatePrice || 0) * item.quantity) + (item.shippingCost || 0); // Include item.shippingCost
      unitDisplayEstimatedPriceInPurchase = item.estimatePrice || 0;

      // If parent group has groupTotalPrice, hide child prices in purchase mode
      if (parentHasGroupTotalPrice && activeTab === 'purchase') {
        displayPrice = 0;
        unitDisplayPrice = 0;
      }
    }

    // Determine the class for the item description
    let descriptionClasses = ['item-list-description']; // Base class always applied
    if (item.isGroup) {
      descriptionClasses.push('italic', 'text-xs', 'mt-0.5', 'text-gray-500', 'dark:text-gray-400');
    } else {
      if (String(item.details).trim() === "0") {
        descriptionClasses.push('item-detail-zero-font', 'text-gray-600', 'dark:text-gray-400');
      } else {
        descriptionClasses.push('text-xs', 'text-gray-600', 'dark:text-gray-400');
      }
    }
    const descriptionClassName = descriptionClasses.join(' ');

    // Preload item images on hover for faster modal opening
    const preloadItemImages = () => {
      const imageAttachments = (item.attachments || []).filter(att => AttachmentManager.canPreviewImage(att));
      imageAttachments.slice(0, 3).forEach(att => { // Preload first 3 images
        const imageUrl = att.url || att.data;
        if (imageUrl) {
          const img = new Image();
          img.src = imageUrl;
        }
      });
    };

    return (
      <React.Fragment key={item.id}>
        <tr
          className={`hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors cursor-pointer ${isChild ? 'bg-blue-50/50 dark:bg-slate-700/50 border-l-4 border-blue-300 dark:border-blue-600' : ''} ${item.isGroup && !isChild ? 'bg-gradient-to-r from-gray-50 to-white dark:from-slate-800 dark:to-slate-900' : ''}`}
          onClick={() => item.isGroup ? toggleGroupExpansion(item.id) : setSelectedItemForDetailView(item)}
          onMouseEnter={preloadItemImages}
          data-item-id={item.id}
        >
          {/* === NAME CELL === */}
          <td className={`table-col-name py-3 md:py-4 whitespace-normal ${isChild ? 'pl-8 pr-2' : 'px-4 md:px-6'}`}>
            <div className="flex items-center">
              {/* Group expand/collapse button */}
              {item.isGroup && !isChild && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleGroupExpansion(item.id); }}
                  className="p-1.5 mr-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-md flex-shrink-0 transition-colors"
                  aria-expanded={expandedGroupIds.includes(item.id)}
                  aria-label={expandedGroupIds.includes(item.id) ? t('group.collapse', { defaultValue: 'Collapse group' }) : t('group.expand', { defaultValue: 'Expand group' })}
                >
                  {expandedGroupIds.includes(item.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              )}
              {/* Child item indent indicator */}
              {isChild && !item.isGroup && (
                <div className="flex items-center mr-2 flex-shrink-0">
                  <div className="w-4 h-px bg-blue-300 dark:bg-blue-600"></div>
                  <div className="w-2 h-2 rounded-full bg-blue-400 dark:bg-blue-500 ml-0.5"></div>
                </div>
              )}
              {/* Nested group button */}
              {isChild && item.isGroup && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleGroupExpansion(item.id); }}
                  className="p-1 mr-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex-shrink-0"
                  aria-expanded={expandedGroupIds.includes(item.id)}
                  aria-label={expandedGroupIds.includes(item.id) ? t('group.collapse', { defaultValue: 'Collapse group' }) : t('group.expand', { defaultValue: 'Expand group' })}
                >
                  {expandedGroupIds.includes(item.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
              )}

              <div className="flex-grow min-w-0 w-full">
                {/* Mobile: name on left, qty+price on right */}
                <div className="flex items-start justify-between md:block gap-2 w-full">
                  <div className="truncate min-w-0 flex-1" title={item.isGroup ? (item.groupName || item.name) : item.name}>
                    <span
                      className={`item-list-name ${item.isGroup ? 'cursor-pointer font-semibold text-blue-700 dark:text-blue-400' : ''} ${isChild && !item.isGroup ? 'font-normal text-gray-700 dark:text-gray-300' : ''}`}
                      onClick={item.isGroup ? (e) => { e.stopPropagation(); toggleGroupExpansion(item.id); } : undefined}
                    >
                      {item.isGroup ? (item.groupName || item.name) : item.name}
                    </span>
                    {item.isEmergency && !item.isGroup && (<span className={`table-status-pill bg-red-100 text-red-700 ml-2`}>{t('item.emergency')}</span>)}
                    {/* Mobile-only: status + evidence icons below name */}
                    {!item.isGroup && (
                      <div className="md:hidden mt-0.5 flex flex-wrap items-center gap-1.5">
                        {(() => {
                          const mobileApprovedTeamsCount = new Set(item.approvals.filter((app: ItemApproval) => app.approved).map((app: ItemApproval) => app.teamName)).size;
                          return (
                            <span className={`table-status-pill text-[0.65rem] ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : item.status === 'approved' ? 'bg-green-100 text-green-800' : item.status === 'bought' ? 'bg-teal-100 text-teal-800' : item.status === 'rejected' ? 'bg-red-100 text-red-800' : item.status === 'converted' ? 'bg-blue-100 text-blue-800' : item.status === 'editing' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                              {item.status === 'pending' && numRequiredApprovals > 0
                                ? `${t('item.status.pending')} (${mobileApprovedTeamsCount}/${numRequiredApprovals})`
                                : t(`item.status.${item.status}`)}
                            </span>
                          );
                        })()}
                        {/* Evidence icons - purchase tab only */}
                        {activeTab === 'purchase' && (
                          <>
                            {(() => {
                              const hasProof = (item.attachments || []).some(a => a.attachmentType === 'proof_of_purchase');
                              return (
                                <span title={t('attachment.type.proof_of_purchase', { defaultValue: 'หลักฐานการซื้อ' })} className="inline-flex">
                                  <Receipt className={`w-4 h-4 ${hasProof ? 'text-green-600' : 'text-red-400'}`} />
                                </span>
                              );
                            })()}
                            {(() => {
                              const hasPicture = (item.attachments || []).some(a => a.attachmentType === 'product_picture');
                              return (
                                <span title={t('attachment.type.product_picture', { defaultValue: 'รูปภาพสินค้า' })} className="inline-flex">
                                  <Camera className={`w-4 h-4 ${hasPicture ? 'text-green-600' : 'text-red-400'}`} />
                                </span>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Mobile-only: qty + price right-aligned (bigger), purchaser (smaller) */}
                  <div className="md:hidden flex-shrink-0 text-right">
                    <div className="text-sm font-bold text-gray-800 whitespace-nowrap">
                      {item.isGroup ? `1 ${t('general.set', { defaultValue: 'Set' })}` : `${item.quantity} ${item.unit}`}
                    </div>
                    <div className="text-sm text-gray-700 font-bold whitespace-nowrap">฿{displayPrice.toLocaleString()}</div>
                    {item.purchaser && usersToUse.find((p: User) => p.id === item.purchaser) && (
                      <div className="truncate mt-0.5 ml-auto text-[0.6rem] text-gray-400 font-normal leading-tight max-w-[120px]">
                        {usersToUse.find((p: User) => p.id === item.purchaser)?.fullName}
                      </div>
                    )}
                  </div>
                </div>
                {/* Description - desktop only */}
                <div className={`${descriptionClassName} truncate-2-lines hidden md:block`} title={item.isGroup
                  ? (item.shippingCost && item.shippingCost > 0
                    ? `${item.childItemIds?.length || 0} ${t('general.items', { defaultValue: 'items' })} + ${t('item.shippingCost')}: ฿${item.shippingCost.toLocaleString()}`
                    : `${item.childItemIds?.length || 0} ${t('general.items', { defaultValue: 'items' })}`
                  )
                  : item.details || ""}>
                  {item.isGroup
                    ? (item.shippingCost && item.shippingCost > 0
                      ? `${item.childItemIds?.length || 0} ${t('general.items', { defaultValue: 'items' })} + ${t('item.shippingCost')}: ฿${item.shippingCost.toLocaleString()}`
                      : `${item.childItemIds?.length || 0} ${t('general.items', { defaultValue: 'items' })}`
                    )
                    : item.details || ""}
                </div>
                {/* Shipping cost - desktop only */}
                {!item.isGroup && (item.shippingCost || 0) > 0 && !(isChild && parentGroup && parentGroup.groupTotalPrice && parentGroup.groupTotalPrice > 0 && activeTab === 'purchase') && (
                  <div className="text-xxs text-orange-600 dark:text-orange-400 mt-0.5 truncate hidden md:block">
                    {t('item.shippingCost')}: ฿{(item.shippingCost || 0).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </td>

          {/* === QUANTITY CELL - desktop only (mobile qty is inline in name cell) === */}
          <td className="table-col-quantity px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 hidden md:table-cell">
            <div className="truncate">
              {item.isGroup ? `1 ${t('general.set', { defaultValue: 'Set' })}` : `${item.quantity} ${item.unit}`}
            </div>
          </td>

          {/* === PRICE (estimate in purchase mode) - desktop only === */}
          {activeTab === 'purchase' && (
            <td className="table-col-price px-4 py-4 whitespace-nowrap text-sm text-gray-500 italic hidden md:table-cell">
              {isChild && parentGroup && parentGroup.groupTotalPrice && parentGroup.groupTotalPrice > 0 ? (
                <div className="font-semibold truncate text-gray-400">—</div>
              ) : (
                <>
                  <div className="font-semibold truncate">฿{displayEstimatedPriceInPurchase.toLocaleString()}</div>
                  {!item.isGroup && unitDisplayEstimatedPriceInPurchase > 0 && (<div className="unit-price-text truncate">฿{unitDisplayEstimatedPriceInPurchase.toLocaleString()}/{item.unit}</div>)}
                </>
              )}
            </td>
          )}

          {/* === PRICE (main) - desktop only === */}
          <td className="table-col-price px-4 py-4 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell">
            {isChild && parentGroup && parentGroup.groupTotalPrice && parentGroup.groupTotalPrice > 0 && activeTab === 'purchase' ? (
              <div className="font-semibold truncate text-gray-400" title={t('item.priceIncludedInGroup', { defaultValue: 'Price included in group total' })}>—</div>
            ) : (
              <>
                <div className="font-semibold truncate">฿{displayPrice.toLocaleString()}</div>
                {!item.isGroup && unitDisplayPrice > 0 && (<div className="unit-price-text truncate">฿{unitDisplayPrice.toLocaleString()}/{item.unit}</div>)}
              </>
            )}
          </td>

          {/* === CATEGORY - desktop only === */}
          <td className="table-col-category px-4 py-4 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell">
            {editingInlineItemId === item.id && editingInlineField === 'category' && canEdit && !isProjectClosed && !item.isGroup ? (
              <select value={item.category || ''} onChange={(e) => handleUpdateItemField(item.id, 'category', e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus className="inline-edit-select dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600 w-full">
                {project.categories.map(cat => (<option key={cat} value={cat}>{cat}</option>))}
                <option value="">{t('general.not.set')}</option>
              </select>
            ) : (
              <div className="truncate" title={item.category || (item.isGroup ? '' : t('general.not.set'))}>
                <span onClick={(e) => { e.stopPropagation(); if (canEdit && !isProjectClosed && !item.isGroup) { setEditingInlineItemId(item.id); setEditingInlineField('category'); } }} className={(canEdit && !isProjectClosed && !item.isGroup) ? "cursor-pointer hover:underline" : ""}>
                  {item.category || (item.isGroup ? '' : t('general.not.set'))}
                </span>
              </div>
            )}
          </td>

          {/* === STATUS - desktop only === */}
          <td className="table-col-status px-4 py-4 whitespace-nowrap hidden md:table-cell">
            <div className="truncate">
              {!item.isGroup && (() => {
                const approvedTeamsCount = new Set(item.approvals.filter((app: ItemApproval) => app.approved).map((app: ItemApproval) => app.teamName)).size;
                return (
                  <span className={`table-status-pill ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : item.status === 'approved' ? 'bg-green-100 text-green-800' : item.status === 'bought' ? 'bg-teal-100 text-teal-800' : item.status === 'rejected' ? 'bg-red-100 text-red-800' : item.status === 'converted' ? 'bg-blue-100 text-blue-800' : item.status === 'editing' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                    {item.status === 'pending' && numRequiredApprovals > 0
                      ? `${t('item.status.pending')} (${approvedTeamsCount}/${numRequiredApprovals})`
                      : t(`item.status.${item.status}`)}
                  </span>
                );
              })()}
              {item.rejectionReason && item.status === 'rejected' && !item.isGroup && (<p className="rejection-reason-text truncate" title={item.rejectionReason}>{t('item.rejection.reason')}: {item.rejectionReason.substring(0, 15)}...</p>)}
            </div>
          </td>

          {/* === PURCHASER - desktop only === */}
          {(activeTab === 'list' || activeTab === 'purchase') && (
            <td className="table-col-purchaser px-4 py-4 whitespace-nowrap text-xs text-gray-500 hidden md:table-cell">
              {editingInlineItemId === item.id && editingInlineField === 'purchaser' && canEditPurchaser && !isProjectClosed && !item.isGroup ? (
                <select value={item.purchaser || ''} onChange={(e) => handleUpdateItemField(item.id, 'purchaser', e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus className="inline-edit-select dark:bg-slate-700 dark:text-gray-100 dark:border-slate-600 w-full max-w-full text-xs">
                  <option value="">{t('general.not.assigned')}</option>
                  {availablePurchasers.map((user: User) => (<option key={user.id} value={user.id}>{user.fullName} ({user.team})</option>))}
                </select>
              ) : (
                <div className="truncate max-w-[150px]" title={item.isGroup ? '' : (item.purchaser && usersToUse.find((p: User) => p.id === item.purchaser) ? usersToUse.find((p: User) => p.id === item.purchaser)?.fullName + ` (${usersToUse.find((p: User) => p.id === item.purchaser)?.team})` : t('general.not.assigned'))}>
                  <span onClick={(e) => { e.stopPropagation(); if (canEditPurchaser && !isProjectClosed && !item.isGroup) { setEditingInlineItemId(item.id); setEditingInlineField('purchaser'); } }} className={(canEditPurchaser && !isProjectClosed && !item.isGroup) ? "cursor-pointer hover:underline" : ""}>
                    {item.isGroup ? '' : (item.purchaser && usersToUse.find((p: User) => p.id === item.purchaser) ? usersToUse.find((p: User) => p.id === item.purchaser)?.fullName : <span className="italic text-gray-400">{t('general.not.assigned')}</span>)}
                  </span>
                </div>
              )}
            </td>
          )}

          {/* === EVIDENCE - desktop only === */}
          {activeTab === 'purchase' && !item.parentId && (
            <td className="table-col-evidence px-4 py-4 whitespace-nowrap text-center hidden md:table-cell">
              <div className="flex items-center justify-center gap-1">
                {(() => {
                  const isGreen = item.isGroup
                    ? (() => {
                      if ((item.attachments || []).some(a => a.attachmentType === 'proof_of_purchase')) return true;
                      const children = project.items.filter(child => child.parentId === item.id && (child.status === 'approved' || child.status === 'bought'));
                      return children.length > 0 && children.every(child => (child.attachments || []).some(a => a.attachmentType === 'proof_of_purchase'));
                    })()
                    : (item.attachments || []).some(a => a.attachmentType === 'proof_of_purchase');
                  return (
                    <span title={t('attachment.type.proof_of_purchase', { defaultValue: 'หลักฐานการซื้อ' })}>
                      <Receipt className={`w-5 h-5 ${isGreen ? 'text-green-600' : 'text-red-400'}`} />
                    </span>
                  );
                })()}
                {(() => {
                  const isGreen = item.isGroup
                    ? (() => {
                      if ((item.attachments || []).some(a => a.attachmentType === 'product_picture')) return true;
                      const children = project.items.filter(child => child.parentId === item.id && (child.status === 'approved' || child.status === 'bought'));
                      return children.length > 0 && children.every(child => (child.attachments || []).some(a => a.attachmentType === 'product_picture'));
                    })()
                    : (item.attachments || []).some(a => a.attachmentType === 'product_picture');
                  return (
                    <span title={t('attachment.type.product_picture', { defaultValue: 'รูปภาพสินค้า' })}>
                      <Camera className={`w-5 h-5 ${isGreen ? 'text-green-600' : 'text-red-400'}`} />
                    </span>
                  );
                })()}
              </div>
            </td>
          )}
          {activeTab === 'purchase' && item.parentId && (
            <td className="table-col-evidence px-4 py-4 whitespace-nowrap text-center hidden md:table-cell">
            </td>
          )}

          {/* === MENU - hidden on mobile, tap row to view details === */}
          <td className="table-col-menu px-4 py-4 whitespace-nowrap text-sm text-center hidden md:table-cell">
            <div className="flex items-center justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (openDropdownId === item.id) {
                    setOpenDropdownId(null);
                  } else {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const menuHeight = 200;
                    const menuWidth = 192;
                    let top = rect.bottom + 4;
                    let left = rect.left;
                    if (top + menuHeight > window.innerHeight) {
                      top = rect.top - menuHeight - 4;
                      if (top < 4) top = 4;
                    }
                    if (left + menuWidth > window.innerWidth) {
                      left = window.innerWidth - menuWidth - 4;
                      if (left < 4) left = 4;
                    }
                    setDropdownPosition({ top: `${top}px`, left: `${left}px` });
                    setOpenDropdownId(item.id);
                  }
                }}
                className="p-1.5 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                title="Actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>
        {item.isGroup && expandedGroupIds.includes(item.id) && (
          <>
            {project.items
              .filter(childItem => childItem.parentId === item.id)
              // Each tab must show only ONE of the original/purchase-clone pair:
              // - List tab: originals only (never purchase clones)
              // - Purchase tab: purchase clones, bought items, and approved
              //   originals that have NOT been superseded by a clone
              .filter(childItem => activeTab === 'purchase'
                ? (childItem.isPurchaseItem === true
                  || childItem.status === 'bought'
                  || (childItem.status === 'approved' && !itemHasPurchaseClone(childItem.id, project.items)))
                : !childItem.isPurchaseItem)
              .map(childItem => renderItemRow(childItem, true, item))}
            {/* Add Item to Group Button - MOVED TO ACTION ROW OF GROUP HEADER */}
          </>
        )}
      </React.Fragment>
    );
  };

  const visibleItems = getVisibleItems();

  // Helper function to check for matching descendant statuses
  const checkDescendantStatusRecursive = (
    currentItem: Item,
    allProjectItems: Item[],
    statusesToMatch: ItemStatus[]
  ): boolean => {
    if (!currentItem.isGroup) {
      return statusesToMatch.includes(currentItem.status);
    }

    // It's a group, check its children
    const childrenOfCurrentGroup = allProjectItems.filter(
      (child) => child.parentId === currentItem.id
    );
    return childrenOfCurrentGroup.some((child) =>
      checkDescendantStatusRecursive(child, allProjectItems, statusesToMatch)
    );
  };

  const calculateRecursiveEstimatedPrice = (item: Item, allItems: Item[], statusFilter?: ItemStatus | ItemStatus[]): number => {
    if (item.isGroup) {
      const children = allItems.filter(child => child.parentId === item.id);
      const childrenTotalEstimatedPrice = children.reduce((sum, child) => sum + calculateRecursiveEstimatedPrice(child, allItems, statusFilter), 0);

      if (statusFilter) { // If a status filter IS active
        if (childrenTotalEstimatedPrice > 0) {
          // If children (matching the status) contributed to the price, then group's shipping cost is part of this status-specific sum.
          return childrenTotalEstimatedPrice + (item.shippingCost || 0);
        } else {
          // Children's price sum for this status is 0.
          // Check if any descendant *actually has* this status (even if its price contribution was 0).
          const statusesToConsider = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
          const hasMatchingDescendant = children.some(child =>
            checkDescendantStatusRecursive(child, allItems, statusesToConsider)
          );

          if (hasMatchingDescendant) {
            // A descendant matches the status (e.g., a pending item with 0 price).
            // So, the group's shipping cost IS part of this status-specific sum.
            return (item.shippingCost || 0);
          } else {
            // No descendant matches this status. Group's shipping cost is NOT part of this status-specific sum.
            return 0;
          }
        }
      } else { // No status filter, standard calculation (group shipping always adds to children's sum)
        return childrenTotalEstimatedPrice + (item.shippingCost || 0);
      }
    }
    // Apply status filter if provided for non-group items
    if (statusFilter) {
      const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
      if (!statuses.includes(item.status)) {
        return 0; // Does not contribute if status doesn't match
      }
    }
    // For individual items, include their shipping cost.
    return ((item.estimatePrice || 0) * item.quantity) + (item.shippingCost || 0);
  };

  // Helper to check for superseded items (items that have been converted to purchase items)
  const isSuperseded = (item: Item, allItems: Item[]) => {
    return item.status === 'approved' && !item.isPurchaseItem && allItems.some(i => i.isPurchaseItem === true && String(i.originalItemId) === String(item.id));
  };

  const calculateRecursiveActualPrice = (item: Item, allItems: Item[], statusFilter?: ItemStatus | ItemStatus[] | 'approved_or_unsupervised', purchaserFilter?: string): number => {
    if (item.isGroup) {
      let children = allItems.filter(child => child.parentId === item.id);

      // Apply purchaser filter to children if provided
      if (purchaserFilter) {
        children = children.filter(child => child.purchaser === purchaserFilter);
      }

      // Check if groupTotalPrice is set - use it instead of summing children
      if (item.groupTotalPrice && item.groupTotalPrice > 0) {
        // Check if group has qualifying children (to determine if group should be counted)
        const qualifyingChildren = children.filter(child => {
          if (!statusFilter) return true;
          if (statusFilter === 'approved_or_unsupervised') {
            return child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child, allItems));
          }
          const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
          return statuses.includes(child.status);
        });

        if (qualifyingChildren.length > 0) {
          // If purchaserFilter is set, calculate proportional share of groupTotalPrice
          if (purchaserFilter) {
            const allChildrenInGroup = allItems.filter(c => c.parentId === item.id);
            const qualifyingCount = qualifyingChildren.length;
            const totalQualifying = allChildrenInGroup.filter(c => {
              if (statusFilter === 'approved_or_unsupervised') {
                return c.status === 'bought' || c.isUnsupervisedPurchase === true || (c.status === 'approved' && !isSuperseded(c, allItems));
              }
              const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter || 'approved'];
              return statuses.includes(c.status);
            }).length;
            // Proportional shipping cost
            const proportionalShipping = totalQualifying > 0 ? ((item.shippingCost || 0) * qualifyingCount / totalQualifying) : 0;
            // For groups with groupTotalPrice: sum children's actual prices for this purchaser
            const childrenTotalForUser = qualifyingChildren.reduce((sum, child) =>
              sum + ((child.price || 0) * child.quantity) + (child.shippingCost || 0), 0);
            return childrenTotalForUser + proportionalShipping;
          }
          return item.groupTotalPrice + (item.shippingCost || 0);
        }
        return 0; // Group with no qualifying children contributes 0
      }

      // No groupTotalPrice set - sum children's prices as before
      const childrenTotalActualPrice = children.reduce((sum, child) => sum + calculateRecursiveActualPrice(child, allItems, statusFilter, purchaserFilter), 0);

      // For groups: only include the group's cost if it has children that meet the status filter
      // This ensures groups are only counted when they have relevant approved/unsupervised children
      if (childrenTotalActualPrice > 0) {
        // If purchaserFilter, calculate proportional group shipping
        if (purchaserFilter) {
          const allChildrenInGroup = allItems.filter(c => c.parentId === item.id);
          const userChildCount = children.filter(c => {
            if (!statusFilter) return true;
            if (statusFilter === 'approved_or_unsupervised') {
              return c.status === 'bought' || c.isUnsupervisedPurchase === true || (c.status === 'approved' && !isSuperseded(c, allItems));
            }
            const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
            return statuses.includes(c.status);
          }).length;
          const totalQualifying = allChildrenInGroup.filter(c => {
            if (statusFilter === 'approved_or_unsupervised') {
              return c.status === 'bought' || c.isUnsupervisedPurchase === true || (c.status === 'approved' && !isSuperseded(c, allItems));
            }
            const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter || 'approved'];
            return statuses.includes(c.status);
          }).length;
          const proportionalShipping = totalQualifying > 0 ? ((item.shippingCost || 0) * userChildCount / totalQualifying) : 0;
          return childrenTotalActualPrice + proportionalShipping;
        }
        return childrenTotalActualPrice + (item.shippingCost || 0);
      }
      return 0; // Group with no qualifying children contributes 0
    }

    // Apply status filter if provided for non-group items
    if (statusFilter) {
      if (statusFilter === 'approved_or_unsupervised') {
        if (!(item.status === 'bought' || item.isUnsupervisedPurchase === true || (item.status === 'approved' && !isSuperseded(item, allItems)))) {
          return 0;
        }
      } else {
        const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
        if (!statuses.includes(item.status)) {
          return 0;
        }
      }
    }

    // Apply purchaser filter for non-group items
    if (purchaserFilter && item.purchaser !== purchaserFilter) {
      return 0;
    }

    // For individual items, include their shipping cost.
    return ((item.price || 0) * item.quantity) + (item.shippingCost || 0);
  };

  const totalEstimatedPrice = useMemo(() => {
    if (activeTab !== 'list') return 0;
    // Sums estimated prices of all items shown in visibleItems, recursing into groups.
    // No additional status filter is applied here as visibleItems already respects top-level filters.
    return visibleItems.reduce((sum, item) => sum + calculateRecursiveEstimatedPrice(item, project.items), 0);
  }, [visibleItems, activeTab, project.items]);

  const totalActualPrice = useMemo(() => {
    if (activeTab !== 'purchase') return 0;
    // visibleItems already filters by purchaser for non-admin users
    // Just sum the actual prices without additional purchaser filter
    return visibleItems.reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0);
  }, [visibleItems, activeTab, project.items]);

  const totalApprovedEstimatedPrice = useMemo(() => {
    if (activeTab !== 'list') return 0;
    // Sums estimated prices of 'approved' items within visibleItems, recursing into groups.
    return visibleItems.reduce((sum, item) => sum + calculateRecursiveEstimatedPrice(item, project.items, 'approved'), 0);
  }, [visibleItems, activeTab, project.items]);

  const totalEstimatedPriceInPurchaseMode = useMemo(() => {
    if (activeTab !== 'purchase') return 0;
    // visibleItems in purchase mode are already 'approved' or 'unsupervisedPurchase'.
    // Sums estimated prices, recursing into groups and ensuring only approved children contribute.
    return visibleItems.reduce((sum, item) => sum + calculateRecursiveEstimatedPrice(item, project.items, ['approved', 'bought']), 0);
  }, [visibleItems, activeTab, project.items]);

  const totalPendingEstimatedPrice = useMemo(() => {
    if (activeTab !== 'list') return 0;
    // Sums estimated prices of 'pending' or 'editing' items within visibleItems, recursing into groups.
    return visibleItems.reduce((sum, item) => sum + calculateRecursiveEstimatedPrice(item, project.items, ['pending', 'editing']), 0);
  }, [visibleItems, activeTab, project.items]);

  // Add after other handlers, before renderItemRow
  const handleDeleteItem = async (item: Item) => {
    if (!window.confirm(t('item.delete.confirm', { itemName: item.name, defaultValue: `Are you sure you want to delete item "${item.name}"? This action cannot be undone.` }))) {
      return;
    }
    let projectToUpdate = projects.find(p => p.id === project.id);
    if (!projectToUpdate) return;
    let itemsForUpdate = [...projectToUpdate.items];
    let itemsToDelete: Item[] = [item];
    // If group, delete all children recursively
    if (item.isGroup) {
      const collectChildren = (parent: Item) => {
        const children = itemsForUpdate.filter(i => String(i.parentId) === String(parent.id));
        children.forEach(child => {
          itemsToDelete.push(child);
          if (child.isGroup) collectChildren(child);
        });
      };
      collectChildren(item);
    }
    // Also delete purchase clones of every deleted item - otherwise the clone
    // stays behind as an orphan row in the Purchase tab forever
    const cloneCandidates = [...itemsToDelete];
    cloneCandidates.forEach(deletedItem => {
      if (!deletedItem.isPurchaseItem) {
        itemsForUpdate.forEach(i => {
          if (i.isPurchaseItem === true && String(i.originalItemId) === String(deletedItem.id) && !itemsToDelete.some(d => d.id === i.id)) {
            itemsToDelete.push(i);
          }
        });
      }
    });
    // Remove from parent group if needed
    if (item.parentId) {
      const parentIdx = itemsForUpdate.findIndex(i => String(i.id) === String(item.parentId) && i.isGroup);
      if (parentIdx !== -1) {
        const parent = { ...itemsForUpdate[parentIdx] };
        parent.childItemIds = (parent.childItemIds || []).filter(id => String(id) !== String(item.id));
        itemsForUpdate[parentIdx] = parent;
      }
    }
    // Remove items
    const updatedItems = itemsForUpdate.filter(i => !itemsToDelete.some(d => d.id === i.id));

    // Recalculate purchaser funds - deleting a bought/approved item changes
    // what its purchaser has spent. Previously funds were saved unchanged,
    // leaving stale spentAmount/availableAmount until an unrelated item edit.
    const affectedPurchaserIds = new Set<string>();
    itemsToDelete.forEach(d => { if (d.purchaser) affectedPurchaserIds.add(d.purchaser); });
    const recalculatedPurchaserFunds = projectToUpdate.purchaserFunds.map(pf => {
      if (!affectedPurchaserIds.has(pf.purchaserId)) return pf;
      const newSpent = calculateSpentAmountForUser(pf.purchaserId, updatedItems);
      return {
        ...pf,
        spentAmount: newSpent,
        availableAmount: (pf.allocatedAmount || 0) + (pf.additionalFunds || 0) - newSpent,
      };
    });
    // Clean up attachments (continue even if some files don't exist)
    for (const delItem of itemsToDelete) {
      if (delItem.attachments && delItem.attachments.length > 0) {
        try {
          await AttachmentManager.cleanupAttachments(delItem.attachments);
        } catch (e) {
          // Log but continue - missing files are not critical for deletion
        }
      }
    }
    // Audit
    const auditEntry = createAuditEntry(currentUser?.id || '', currentUser?.fullName || '', 'ITEM_DELETED', `Item "${item.name}" deleted`, item.id, item, null);

    // Save to Firestore - DELETE items from subcollection and update project (without items)
    try {
      const { doc, setDoc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      const { removeUndefinedValues } = await import('../utils');

      // Delete items from subcollection using ItemsService
      for (const delItem of itemsToDelete) {
        await ItemsService.deleteItem(project.id, delItem.id);
      }

      // Update parent if needed (save just the parent item, not the whole project)
      if (item.parentId) {
        const parentIdx = itemsForUpdate.findIndex(i => String(i.id) === String(item.parentId) && i.isGroup);
        if (parentIdx !== -1) {
          const updatedParent = itemsForUpdate[parentIdx];
          await ItemsService.saveItem(project.id, updatedParent);
        }
      }

      const projectDocRef = doc(db, 'projects', project.id.toString());

      // Get fresh project data WITHOUT items (items are in subcollection)
      const projectData: any = {
        name: projectToUpdate.name,
        description: projectToUpdate.description,
        initialFund: projectToUpdate.initialFund,
        totalReceived: projectToUpdate.totalReceived,
        items: [], // ALWAYS empty - items are in subcollection
        fundEntries: projectToUpdate.fundEntries,
        purchaserFunds: recalculatedPurchaserFunds,
        initialFundAttachments: projectToUpdate.initialFundAttachments || [],
        mode: projectToUpdate.mode,
        status: projectToUpdate.status,
        teamsInvolved: projectToUpdate.teamsInvolved,
        createdBy: projectToUpdate.createdBy,
        createdAt: projectToUpdate.createdAt,
        // auditTrail: [...projectToUpdate.auditTrail, auditEntry],
        auditTrail: [], // Legacy field cleared
        categories: projectToUpdate.categories,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: currentUser?.id,
        deletionApprovals: projectToUpdate.deletionApprovals || [],
      };

      await AuditService.addAuditEntry(project.id.toString(), auditEntry);

      if (projectToUpdate.initialFundDate) projectData.initialFundDate = projectToUpdate.initialFundDate;
      if (projectToUpdate.deadline) projectData.deadline = projectToUpdate.deadline;
      if (projectToUpdate.closedAt) projectData.closedAt = projectToUpdate.closedAt;
      if (projectToUpdate.closedBy) projectData.closedBy = projectToUpdate.closedBy;
      if (projectToUpdate.deletionInitiatedBy) projectData.deletionInitiatedBy = projectToUpdate.deletionInitiatedBy;

      const cleanedData = removeUndefinedValues(projectData);
      await setDoc(projectDocRef, cleanedData);
    } catch (error) {
      alert(t('error.save.failed', { defaultValue: 'Failed to save changes. Please try again.' }));
      return;
    }

    // Local state will be updated automatically by the realtime listener
    addNotification({
      userId: item.createdBy,
      type: 'assignment', // changed to valid NotificationType
      title: t('notification.item.deleted.title', { defaultValue: 'Item Deleted' }),
      message: t('notification.item.deleted.message', { itemName: item.name, projectName: project.name, defaultValue: `Item "${item.name}" was deleted from project "${project.name}".` }),
      projectId: project.id,
      itemId: item.id,
    });
  };

  const TeamManagementModal: React.FC = () => {
    const allTeams: TeamName[] = ['Marketing A', 'Marketing B', 'Marketing C', 'Marketing D', 'Marketing E', 'Production SBK', 'Production SDC', 'MUNMUN 1', 'MUNMUN 2', 'Oasis'];
    const [selectedTeams, setSelectedTeams] = useState<TeamName[]>(project.teamsInvolved || []);

    const handleTeamToggle = (team: TeamName) => {
      setSelectedTeams(prev =>
        prev.includes(team) ? prev.filter(t => t !== team) : [...prev, team]
      );
    };

    const handleSaveChanges = async () => {
      if (!currentUser || currentUser.role !== 'admin') {
        alert("You don't have permission to change project teams.");
        return;
      }

      const db = getFirestore();
      const projectRef = doc(db, 'projects', project.id.toString());

      try {
        await updateDoc(projectRef, {
          teamsInvolved: selectedTeams
        });

        // Local state will be updated automatically by the realtime listener

        setShowTeamManagement(false);
        alert('Project teams updated successfully!');

        // Add audit trail entry using AuditService
        const auditEntry = createAuditEntry(
          currentUser.id,
          currentUser.fullName,
          'PROJECT_TEAMS_UPDATED',
          `Teams for project "${project.name}" were updated.`,
          project.id,
          { oldTeams: project.teamsInvolved },
          { newTeams: selectedTeams }
        );
        await AuditService.addAuditEntry(project.id.toString(), auditEntry);

      } catch (error) {
        alert("Failed to update project teams. Please try again.");
      }
    };

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2 className="text-xl font-bold mb-4">Manage Project Teams</h2>
          <div className="space-y-2">
            {allTeams.map(team => (
              <div key={team} className="flex items-center">
                <input
                  type="checkbox"
                  id={`team-${team}`}
                  checked={selectedTeams.includes(team)}
                  onChange={() => handleTeamToggle(team)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={`team-${team}`} className="ml-2 block text-sm text-gray-900">
                  {team}
                </label>
              </div>
            ))}
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <button onClick={() => setShowTeamManagement(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSaveChanges} className="btn-primary">Save Changes</button>
          </div>
        </div>
      </div>
    );
  };

  const MoveToGroupModal: React.FC = () => {
    const handleClose = () => {
      setShowMoveToGroupModal(false);
      setItemToMoveToGroup(null);
      setSelectedTargetGroupId('');
    };

    // Filter available groups - exclude the item's current parent and the item itself if it's a group
    const availableTargetGroups = availableGroups.filter(group => {
      if (!itemToMoveToGroup) return false;
      // Can't move to itself
      if (group.id === itemToMoveToGroup.id) return false;
      // Can't move to current parent
      if (group.id === itemToMoveToGroup.parentId) return false;
      return true;
    });

    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{t('item.moveToGroup', { defaultValue: 'Move to Group' })}</h2>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {itemToMoveToGroup && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">{t('item.moving', { defaultValue: 'Moving item:' })}</p>
              <p className="font-medium">{itemToMoveToGroup.name}</p>
              {itemToMoveToGroup.parentId && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('item.currentGroup', { defaultValue: 'Currently in:' })} {project.items.find(i => i.id === itemToMoveToGroup.parentId)?.groupName || project.items.find(i => i.id === itemToMoveToGroup.parentId)?.name || 'Unknown'}
                </p>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('item.selectTargetGroup', { defaultValue: 'Select target group:' })}
            </label>
            {availableTargetGroups.length > 0 ? (
              <select
                value={selectedTargetGroupId}
                onChange={(e) => setSelectedTargetGroupId(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('item.selectGroup', { defaultValue: '-- Select a group --' })}</option>
                {availableTargetGroups.map(group => (
                  <option key={group.id} value={group.id.toString()}>
                    {group.groupName || group.name} ({group.childItemIds?.length || 0} {t('general.items', { defaultValue: 'items' })})
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-500 text-sm italic">
                {t('item.noAvailableGroups', { defaultValue: 'No available groups to move to. Create a group first.' })}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <button onClick={handleClose} className="btn-secondary px-4 py-2">
              {t('action.cancel')}
            </button>
            <button
              onClick={handleMoveItemToGroup}
              disabled={!selectedTargetGroupId}
              className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('item.moveToGroup', { defaultValue: 'Move to Group' })}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add function to calculate actual total received from fund entries
  const calculateActualTotalReceived = (project: Project): number => {
    // Calculate from fund entries to ensure accuracy
    // Total = initial_project entries + additional_project entries + additional_to_user entries
    // Note: allocated_to_user entries are internal transfers and should NOT be counted

    const totalFromInitialEntries = (project.fundEntries || [])
      .filter(entry => entry.type === 'initial_project')
      .reduce((sum, entry) => sum + entry.amount, 0);

    const totalFromAdditionalProject = (project.fundEntries || [])
      .filter(entry => entry.type === 'additional_project')
      .reduce((sum, entry) => sum + entry.amount, 0);

    const totalFromAdditionalUsers = (project.purchaserFunds || []).reduce((sum, fund) => {
      // Use the summary field 'additionalFunds' which correctly tracks net user additions (including clears/migrations)
      // This is more reliable than summing 'additional_to_user' entries which might be missing in legacy data
      return sum + (fund.additionalFunds || 0);
    }, 0);

    // Standard Modern Calculation: If we have explicit initial entries, use strict summation
    if (totalFromInitialEntries > 0) {
      return totalFromInitialEntries + totalFromAdditionalProject + totalFromAdditionalUsers;
    }

    // Fallback for Legacy Projects (No initial_project entry):
    // If no initial entry exists, we infer the "Effective Project Fund" from simple balances.
    // Effective Project Fund = Remaining Initial Fund + Total Allocated to Users
    // This correctly reconstructs the "Project Side" funds even if entries are missing.
    // Then we add the Explicitly Added Personal Funds (additional_to_user).

    // We use this fallback WHENEVER appropriate initial entries are missing, 
    // regardless of whether initialFund is > 0 (it might be 0 if fully allocated).
    const currentRemainingProjectFund = project.initialFund || 0;
    const totalAllocatedToUsers = (project.purchaserFunds || []).reduce((sum, pf) => sum + (pf.allocatedAmount || 0), 0);

    const effectiveProjectLevelFund = currentRemainingProjectFund + totalAllocatedToUsers;

    return effectiveProjectLevelFund + totalFromAdditionalUsers;
  };

  // Calculate current user's personal fund data for status bar
  const currentUserFund = project.purchaserFunds.find(f => f.purchaserId === currentUser?.id);
  const userAllocated = currentUserFund
    ? (currentUserFund.allocatedAmount || 0) + (currentUserFund.additionalFunds || 0)
    : 0;
  // Use the same calculation as SummaryTab for consistency
  const userSpent = currentUser?.id ? calculateSpentAmountForUser(currentUser.id, project.items) : 0;
  const userRemaining = userAllocated - userSpent;
  return (
    <div className="p-4 sm:p-6"> {/* Root Element */}
      <div className="project-header-container mb-6"> {/* CSS handles flex-col, align-stretch */}
        <button onClick={() => setCurrentProject(null)} className="project-back-button mr-4"> {/* Positioned by CSS */}
          <ArrowLeft className="project-back-icon" />
        </button>

        {/* --- ROW 0: Badges Row --- */}
        <div className="project-badges-row"> {/* Badges row */}
          <span className={`project-status-badge ${project.status === 'active' ? 'project-status-active' : project.status === 'closed' ? 'project-status-closed' : 'project-status-pending-deletion'}`}>{t(`project.status.${project.status}`)}</span>
          {project.deadline && project.deadline !== '' && (<span className={`project-deadline-badge ${isOverdue(project.deadline) ? 'project-deadline-overdue' : isDeadlineApproaching(project.deadline) ? 'project-deadline-warning' : 'project-deadline-normal'}`}>{isOverdue(project.deadline) ? t('deadline.overdue') : isDeadlineApproaching(project.deadline, 1) ? t('deadline.due.today') : isDeadlineApproaching(project.deadline) ? t('deadline.due.soon') : new Date(project.deadline).toLocaleDateString()}</span>)}
        </div>

        {/* --- ROW 1: Title AND Action Buttons --- */}
        <div className="flex justify-between items-start w-full">
          <div className="flex-grow min-w-0">
            <h1 className="project-title text-xl md:text-2xl font-bold text-gray-800 truncate" title={project.name}>{project.name}</h1>
          </div>
          <div className="hidden md:flex space-x-2 flex-shrink-0 ml-4">
            {!isProjectClosed && currentUser && (
              <>
                <button onClick={() => setShowFundManagement(true)} className="project-header-btn project-header-btn-green"><CreditCard className="project-header-btn-icon" />{t('fund.management')}</button>
                {(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') && (
                  <button onClick={closeProjectHandler} className="project-header-btn project-header-btn-red"><Lock className="project-header-btn-icon" />{t('project.close')}</button>
                )}
              </>
            )}
            <button onClick={() => setShowExportModal(true)} className="project-header-btn project-header-btn-blue"><Download className="project-header-btn-icon" />{t('project.export')}</button>
          </div>
        </div>

        {/* --- ROW 2: Description/Teams --- */}
        <div className="hidden md:block mt-2">
          <p className="project-description text-sm text-gray-600 mt-1 line-clamp-2">{project.description}</p>
          <div className="project-teams-row mt-2">
            <span className="project-teams-label text-xs text-gray-500">{t('general.teams')}: </span>
            {project.teamsInvolved.map((team: TeamName) => (<span key={team} className="project-team-badge">{team}</span>))}
          </div>
        </div>
        {/* Mobile: compact teams row */}
        <div className="md:hidden flex flex-wrap gap-1 mt-1">
          {project.teamsInvolved.map((team: TeamName) => (<span key={team} className="project-team-badge text-[0.65rem]">{team}</span>))}
        </div>

        {/* --- Budget Summary --- */}
        {/* Mobile: compact 3-column summary */}
        <div className="md:hidden mt-3 bg-white rounded-lg border border-gray-200 p-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[0.6rem] text-gray-500">{t('summary.budget', { defaultValue: 'งบประมาณ' })}</div>
              <div className="text-sm font-bold text-green-600">฿{calculateActualTotalReceived(project).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[0.6rem] text-gray-500">{t('summary.totalSpent', { defaultValue: 'ใช้จ่าย' })}</div>
              <div className="text-sm font-bold text-red-600">฿{(project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0)).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[0.6rem] text-gray-500">{t('summary.remaining', { defaultValue: 'คงเหลือ' })}</div>
              <div className={`text-sm font-bold ${(calculateActualTotalReceived(project) - project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ฿{(calculateActualTotalReceived(project) - project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0)).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop: full budget summary */}
        <div className="hidden md:flex justify-between items-start w-full mt-2">
          <div className="flex-grow">
            <p className="project-description text-sm text-gray-600 mt-1 line-clamp-2">{project.description}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '16px', alignItems: 'flex-end' }}>
            <div className="project-budget-summary" style={{ marginLeft: '0' }}>
              <div className="budget-section">
                <span className="budget-section-label font-bold text-teal-700">{t('summary.project', { defaultValue: 'โครงการ' })}:</span>
              </div>
              <div className="budget-divider"></div>
              <div className="budget-section">
                <span className="budget-section-label">{t('summary.budget', { defaultValue: 'งบประมาณรวม' })}:</span>
                <span className="budget-section-value budget-value-green">฿{calculateActualTotalReceived(project).toLocaleString()}</span>
              </div>
              <div className="budget-divider"></div>
              <div className="budget-section">
                <span className="budget-section-label">{t('summary.totalSpent', { defaultValue: 'ใช้จ่ายแล้ว' })}:</span>
                <span className="budget-section-value budget-value-red">฿{(project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0)).toLocaleString()}</span>
              </div>
              <div className="budget-divider"></div>
              <div className="budget-section">
                <span className="budget-section-label">{t('summary.remaining', { defaultValue: 'คงเหลือ' })}:</span>
                <span className={`budget-section-value ${(calculateActualTotalReceived(project) - project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0)) >= 0 ? 'budget-value-green' : 'budget-value-red'}`}>
                  ฿{(calculateActualTotalReceived(project) - project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0)).toLocaleString()}
                </span>
              </div>
              <div className="budget-divider"></div>
              <div className="budget-section">
                <span className="budget-section-label">{t('summary.usage', { defaultValue: 'การใช้งาน' })}:</span>
                <span className={`budget-usage-badge ${(() => {
                  const totalSpentForUsage = project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0);
                  const actualTotalReceived = calculateActualTotalReceived(project);
                  const usagePercent = actualTotalReceived > 0 ? (totalSpentForUsage / actualTotalReceived) * 100 : 0;
                  return usagePercent > 90 ? 'budget-usage-red' :
                    usagePercent > 70 ? 'budget-usage-yellow' : 'budget-usage-green';
                })()
                  }`}>
                  {(() => {
                    const totalSpentForUsage = project.items.filter(item => !item.parentId).reduce((sum, item) => sum + calculateRecursiveActualPrice(item, project.items, 'approved_or_unsupervised'), 0);
                    const actualTotalReceived = calculateActualTotalReceived(project);
                    const usagePercent = actualTotalReceived > 0 ? (totalSpentForUsage / actualTotalReceived) * 100 : 0;
                    return usagePercent.toFixed(1);
                  })()}%
                </span>
              </div>
            </div>

            {currentUser && (
              <div className="project-budget-summary" style={{ backgroundColor: 'rgba(15, 118, 110, 0.08)', borderColor: 'rgba(15, 118, 110, 0.3)', marginLeft: '0' }}>
                <div className="budget-section">
                  <span className="budget-section-label font-bold text-teal-700">{t('summary.personal', { defaultValue: 'ส่วนตัว' })}:</span>
                </div>
                <div className="budget-divider"></div>
                <div className="budget-section">
                  <span className="budget-section-label">{t('user.fund.allocated')}:</span>
                  <span className="budget-section-value budget-value-green">฿{userAllocated.toLocaleString()}</span>
                </div>
                <div className="budget-divider"></div>
                <div className="budget-section">
                  <span className="budget-section-label">{t('user.fund.spent')}:</span>
                  <span className="budget-section-value budget-value-red">฿{userSpent.toLocaleString()}</span>
                </div>
                <div className="budget-divider"></div>
                <div className="budget-section">
                  <span className="budget-section-label">{t('user.fund.remaining')}:</span>
                  <span className={`budget-section-value ${userRemaining >= 0 ? 'budget-value-green' : 'budget-value-red'}`}>
                    ฿{userRemaining.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="tab-group-container mb-8 mt-4">
        <button onClick={() => setActiveTab('list')} className={`tab-button ${activeTab === 'list' ? 'tab-button-active' : ''}`}><List className="tab-button-icon" />{t('project.tab.list')}<span className="tab-button-badge tab-badge-blue">{project.items.filter(item => !item.isGroup).length}</span></button>
        <button onClick={() => setActiveTab('purchase')} className={`tab-button ${activeTab === 'purchase' ? 'tab-button-active' : ''}`}><ShoppingCart className="tab-button-icon" />{t('project.tab.purchase')}<span className="tab-button-badge tab-badge-green">{project.items.filter((i: Item) => !i.isGroup && (i.status === 'approved' || i.status === 'bought' || i.isUnsupervisedPurchase)).length}</span></button>
        <button onClick={() => setActiveTab('summary')} className={`tab-button ${activeTab === 'summary' ? 'tab-button-active' : ''}`}><TrendingUp className="tab-button-icon" />{t('project.summary')}<span className="tab-button-badge tab-badge-purple">{project.purchaserFunds.length}</span></button> {/* Removed tab-button-summary */}
      </div>

      {activeTab === 'summary' ? (
        <SummaryTab project={project} currentUser={currentUser} allUsers={allUsers} setProjects={setProjects} setCurrentProject={setCurrentProject} t={t} addNotification={addNotification} />
      ) : (
        <div className="flex flex-col md:flex-row md:gap-x-6">
          <div className="space-y-6 flex-shrink-0 w-full"> {/* Panel no longer affects width */}
            <div className="filter-bar-container bg-white p-3 md:p-4 rounded-lg shadow">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 items-end">
                <div><label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-1">{t('search.placeholder')}</label><input id="searchTerm" type="text" placeholder={t('search.placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="p-2 border rounded w-full shadow-sm focus:ring-blue-500 focus:border-blue-500" /></div>
                <div><label htmlFor="filterStatus" className="block text-sm font-medium text-gray-700 mb-1">{t('filter.status')}</label><select id="filterStatus" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as ItemStatus | '')} className="p-2 border rounded w-full shadow-sm focus:ring-blue-500 focus:border-blue-500"><option value="">{t('filter.all')}</option><option value="pending">{t('item.status.pending')}</option><option value="approved">{t('item.status.approved')}</option><option value="rejected">{t('item.status.rejected')}</option><option value="editing">{t('item.status.editing', { defaultValue: 'Editing' })}</option><option value="converted">{t('item.status.converted')}</option></select></div>
                <div><label htmlFor="filterCategory" className="block text-sm font-medium text-gray-700 mb-1">{t('filter.category')}</label><select id="filterCategory" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="p-2 border rounded w-full shadow-sm focus:ring-blue-500 focus:border-blue-500"><option value="">{t('filter.all')}</option>{project.categories.map((cat: string) => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                {(activeTab === 'list' || activeTab === 'purchase' || isAdminOrSupervisor) && (<div><label htmlFor="filterPurchaser" className="block text-sm font-medium text-gray-700 mb-1">{t('filter.purchaser')}</label><select id="filterPurchaser" value={filterPurchaser} onChange={(e) => setFilterPurchaser(e.target.value)} className="p-2 border rounded w-full shadow-sm focus:ring-blue-500 focus:border-blue-500"><option value="">{t('filter.all')}</option>{availablePurchasers.map((p: User) => <option key={p.id} value={p.id}>{p.fullName}</option>)}<option value="unassigned">{t('general.not.assigned', { defaultValue: 'Not Assigned' })}</option></select></div>)}
              </div>
            </div>
            <div className="flex justify-between items-center py-3">
              {!isProjectClosed && canEdit && (<button onClick={() => { setItemAccessedFrom(activeTab as 'list' | 'purchase'); setEditingItem(null); setPreselectedParentIdForNewItem(null); setShowAddItem(true); }} className="btn-primary px-4 py-2 flex items-center shadow hover:shadow-md transition-shadow"><Plus className="w-5 h-5 mr-2" />{activeTab === 'list' ? t('item.add') : t('item.add.purchase')}</button>)}
              {(activeTab === 'list' || activeTab === 'purchase') && (visibleItems.length > 0 || searchTerm || filterCategory || filterStatus || filterPurchaser) && (
                <div className="total-sum-display-box text-right">
                  {activeTab === 'list' && (
                    <div className="flex items-baseline justify-end flex-wrap gap-x-3 gap-y-1">
                      <div className="whitespace-nowrap">
                        <span className="sum-label text-[0.65rem] md:text-xs text-gray-500 mr-1">{t('total.estimated.cost.short', { defaultValue: 'รวม' })}:</span>
                        <span className="sum-amount text-xs md:text-sm font-semibold text-gray-700">฿{totalEstimatedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <span className="text-gray-400 hidden md:inline"> | </span>
                      <div className="whitespace-nowrap">
                        <span className="sum-label text-[0.65rem] md:text-xs text-gray-500 mr-1">{t('total.pending.estimated.cost.short', { defaultValue: 'รออนุมัติ' })}:</span>
                        <span className="sum-amount text-xs md:text-sm font-semibold text-yellow-700">฿{totalPendingEstimatedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <span className="text-gray-400 hidden md:inline"> | </span>
                      <div className="whitespace-nowrap">
                        <span className="sum-label text-[0.65rem] md:text-xs text-gray-500 mr-1">{t('total.approved.estimated.cost.short', { defaultValue: 'อนุมัติ' })}:</span>
                        <span className="sum-amount text-xs md:text-sm font-semibold text-green-700">฿{totalApprovedEstimatedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                  {activeTab === 'purchase' && (
                    <div className="flex items-baseline justify-end flex-wrap gap-x-3 gap-y-1">
                      <div className="whitespace-nowrap">
                        <span className="sum-label text-[0.65rem] md:text-xs text-gray-500 mr-1">{t('total.estimated.cost.short', { defaultValue: 'ประมาณ' })}:</span>
                        <span className="sum-amount text-xs md:text-sm font-semibold text-gray-700">฿{totalEstimatedPriceInPurchaseMode.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <span className="text-gray-400 hidden md:inline"> | </span>
                      <div className="whitespace-nowrap">
                        <span className="sum-label text-[0.65rem] md:text-xs text-gray-500 mr-1">{t('total.actual.cost.short', { defaultValue: 'จริง' })}:</span>
                        <span className="sum-amount text-xs md:text-sm font-semibold text-gray-700">฿{totalActualPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {visibleItems.length === 0 ? (<div className="text-center py-16 text-gray-500 bg-white rounded-lg shadow"><List className="w-20 h-20 mx-auto text-gray-300" /><p className="mt-4 text-xl">{t('item.no.items')}</p><p className="text-sm mt-1">{t('try.adjusting.filters.or.add.new', { defaultValue: 'Try adjusting your filters or add a new item.' })}</p></div>) : (
              <div className="w-full">
                <div className="bg-white shadow-md rounded-lg overflow-hidden md:overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 hidden md:table-header-group">
                      <tr>
                        <th scope="col" className="table-col-name px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('item.name')} / {t('item.details')}</th>
                        <th scope="col" className="table-col-quantity px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('item.quantity')} & {t('item.unit')}</th>
                        {activeTab === 'purchase' && (
                          <th scope="col" className="table-col-price px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('item.price.estimate')}</th>
                        )}
                        <th scope="col" className="table-col-price px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{activeTab === 'list' ? t('item.price.estimate') : t('item.price.actual')}</th>
                        <th scope="col" className="table-col-category px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('item.category')}</th>
                        <th scope="col" className="table-col-status px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('table.header.itemStatus')}</th>
                        {(activeTab === 'list' || activeTab === 'purchase') && (
                          <th scope="col" className="table-col-purchaser px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">{t('item.purchaser')}</th>
                        )}
                        {activeTab === 'purchase' && (
                          <th scope="col" className="table-col-evidence px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" title={t('item.evidence', { defaultValue: 'หลักฐาน' })} aria-label={t('item.evidence', { defaultValue: 'หลักฐาน' })}></th>
                        )}
                        <th scope="col" className="table-col-menu px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Menu</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visibleItems.map((item: Item) => renderItemRow(item))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          {/* Attachment panel is now a modal, rendered below */}
        </div>
      )}

      {/* Dropdown Menu Portal */}
      {openDropdownId !== null && (() => {
        const item = project.items.find(i => i.id === openDropdownId);
        if (!item) return null;

        // Allow admin/supervisor always, or requester on pending items, or ANY user can edit groups
        const canEdit = (currentUser?.role === 'admin' || currentUser?.role === 'supervisor') ||
          (item.requestedBy === currentUser?.id && item.status === 'pending') ||
          item.isGroup; // Any user can edit groups
        const canSupervisorActOnThisItem = activeTab === 'list' && item.status === 'pending' && canApprove && !isProjectClosed && !item.isGroup;
        const currentUserHasSpecificallyApprovedThisItem = item.approvals.some((app: ItemApproval) => app.teamSupervisorId === currentUser?.id && app.approved);
        const availableGroups = project.items.filter((i: Item) => i.isGroup && !i.parentId);

        return createPortal(
          <div
            className="dropdown-menu"
            style={dropdownPosition}
          >
            {/* View Attachments */}
            {Array.isArray(item.attachments) && item.attachments.length > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedItemForAttachmentView(item);
                  setOpenDropdownId(null);
                }}
                className="dropdown-menu-item"
              >
                <Eye className="w-4 h-4" />
                {t('attachment.view.action', { defaultValue: 'View Attachments' })} ({item.attachments.length})
              </button>
            )}

            {/* Edit */}
            {canEdit && !isProjectClosed && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingItem(item);
                  setItemAccessedFrom(activeTab as 'list' | 'purchase');
                  setPreselectedParentIdForNewItem(null);
                  setShowAddItem(true);
                  setOpenDropdownId(null);
                }}
                className="dropdown-menu-item"
              >
                <Edit className="w-4 h-4" />
                {t('action.edit')}
              </button>
            )}

            {/* Add Item to Group */}
            {item.isGroup && canEdit && !isProjectClosed && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPreselectedParentIdForNewItem(item.id);
                  setEditingItem(null);
                  setShowAddItem(true);
                  setOpenDropdownId(null);
                }}
                className="dropdown-menu-item"
              >
                <FolderPlus className="w-4 h-4" />
                {t('action.addItemToGroup')}
              </button>
            )}

            {/* Move to Group */}
            {!item.isGroup && canEdit && !isProjectClosed && availableGroups.length > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setItemToMoveToGroup(item);
                  setShowMoveToGroupModal(true);
                  setOpenDropdownId(null);
                }}
                className="dropdown-menu-item"
              >
                <ArrowRightCircle className="w-4 h-4" />
                {t('action.moveToGroup')}
              </button>
            )}

            {/* Approve/Reject */}
            {canSupervisorActOnThisItem && !currentUserHasSpecificallyApprovedThisItem && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleItemStatusUpdate(item.id, 'approved');
                    setOpenDropdownId(null);
                  }}
                  className="dropdown-menu-item text-green-700"
                  disabled={isProjectClosed}
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('action.approve')}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const reason = prompt(t('item.rejection.prompt', { defaultValue: 'Please provide a reason for rejection:' }));
                    if (reason !== null) {
                      handleItemStatusUpdate(item.id, 'rejected', reason || t('item.rejection.reason.default', { defaultValue: 'No reason provided' }));
                    }
                    setOpenDropdownId(null);
                  }}
                  className="dropdown-menu-item text-red-700"
                  disabled={isProjectClosed}
                >
                  <XCircle className="w-4 h-4" />
                  {t('action.reject')}
                </button>
              </>
            )}

            {/* Unapprove (Admin only) */}
            {!item.isGroup && currentUser?.role === 'admin' && item.status === 'approved' && !isProjectClosed && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAdminUnapproveItem(item.id);
                  setOpenDropdownId(null);
                }}
                className="dropdown-menu-item text-orange-700"
              >
                <RotateCcw className="w-4 h-4" />
                {t('action.unapprove', { defaultValue: 'Unapprove' })}
              </button>
            )}

            {/* Delete — in list mode any logged-in user can delete */}
            {(canEdit || (activeTab === 'list' && !!currentUser)) && !isProjectClosed && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteItem(item);
                  setOpenDropdownId(null);
                }}
                className="dropdown-menu-item text-red-700"
              >
                <Trash2 className="w-4 h-4" />
                {t('action.delete')}
              </button>
            )}
          </div>,
          document.body
        );
      })()}

      {showAddItem && <AddItemModal initialPreviewTrigger={triggerInitialAttachmentPreview} onPreviewConsumed={() => setTriggerInitialAttachmentPreview(null)} defaultParentId={preselectedParentIdForNewItem} />}
      {showFundManagement && <FundManagementModal />}
      {selectedItemForAttachmentView && selectedItemForAttachmentView.attachments && selectedItemForAttachmentView.attachments.length > 0 && (
        <AttachmentPreviewPanel
          item={selectedItemForAttachmentView}
          onClose={() => setSelectedItemForAttachmentView(null)}
          t={t}
        />
      )}
      {itemToMoveToGroup && <MoveToGroupModal />}
      {showExportModal && (
        <ExportProgressModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          project={project}
          currentUser={currentUser}
          availablePurchasers={availablePurchasers}
          t={t}
        />
      )}
      {showTeamManagement && <TeamManagementModal />}

      {/* Item Detail Modal */}
      {selectedItemForDetailView && (
        <ItemDetailModal
          item={selectedItemForDetailView}
          project={project}
          onClose={() => setSelectedItemForDetailView(null)}
          usersToUse={usersToUse}
          t={t}
          onEdit={() => {
            setEditingItem(selectedItemForDetailView);
            setItemAccessedFrom(activeTab as 'list' | 'purchase');
            setPreselectedParentIdForNewItem(null);
            setShowAddItem(true); // Open edit modal
            setSelectedItemForDetailView(null); // Close detail modal when opening edit
          }}
          onDelete={() => handleDeleteItem(selectedItemForDetailView)}
          onApprove={() => handleItemStatusUpdate(selectedItemForDetailView.id, 'approved')}
          onReject={() => {
            setItemToReject(selectedItemForDetailView);
            setShowRejectModal(true);
          }}
          canEdit={canEdit || (selectedItemForDetailView.requestedBy === currentUser?.id && selectedItemForDetailView.status === 'pending')}
          canApprove={canApprove && selectedItemForDetailView.status === 'pending' && !selectedItemForDetailView.isGroup}
          canDelete={(currentUser?.role === 'admin' || currentUser?.role === 'supervisor') ||
            (selectedItemForDetailView.requestedBy === currentUser?.id && selectedItemForDetailView.status === 'pending') ||
            (activeTab === 'list' && !!currentUser)}
          isProjectClosed={isProjectClosed}
        />
      )}
    </div>
  );
};

export default ProjectView;
